import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { createClient } from "@supabase/supabase-js";

// ─── Supabase client (web only — null in Electron desktop) ───────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = (SUPABASE_URL && SUPABASE_ANON) ? createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: {
    detectSessionInUrl: true,
    flowType: 'implicit',
  }
}) : null;


const VENDOR_STAGES = ["Identified", "Discussed", "Enquiry Sent", "Awaiting Reply", "Quote Received", "Call / Meeting Held", "Shortlisted", "Contract Sent", "Booked", "Declined by Us", "Unavailable"];
// Stages that end a candidate's run — always applied, never "regressed" past
const TERMINAL_STAGES = ["Booked", "Declined by Us", "Unavailable"];
const STAGE_COLOR = {
  "Identified": "#64748B", "Discussed": "#94A3B8", "Enquiry Sent": "#60A5FA", "Awaiting Reply": "#60A5FA", "Quote Received": "#A78BFA",
  "Call / Meeting Held": "#FBBF24", "Shortlisted": "#F472B6", "Contract Sent": "#34D399", "Booked": "#10B981",
  "Declined by Us": "#475569", "Unavailable": "#475569",
};
const PROJECT_STATUSES = [["not_started","Not started"],["researching","Researching"],["in_progress","In progress"],["booked","Booked"],["complete","Complete"]];
const PROJECT_STATUS_COLOR = { not_started: "#64748B", researching: "#60A5FA", in_progress: "#FBBF24", booked: "#10B981", complete: "#34D399" };

const PRIORITY_META = {
  high:   { color: "#F87171", bg: "rgba(248,113,113,0.1)",  border: "rgba(248,113,113,0.3)",  glow: "rgba(248,113,113,0.2)" },
  medium: { color: "#FBBF24", bg: "rgba(251,191,36,0.1)",   border: "rgba(251,191,36,0.3)",   glow: "rgba(251,191,36,0.15)" },
  low:    { color: "#34D399", bg: "rgba(52,211,153,0.1)",   border: "rgba(52,211,153,0.3)",   glow: "rgba(52,211,153,0.1)"  },
};

const SOURCE_COLORS = { Gmail: "#EA4335", Slack: "#7B5EA7", QuickBooks: "#2CA01C", Manual: "#6366F1" };

// ─── HELPERS ─────────────────────────────────────────────────────────────────
// Urgency score used by the "Top 3" section. Blends due-date proximity (the
// dominant signal — overdue > today > tomorrow > this week) with the task's
// priority level, then falls back to manual sort order so ties are stable.
function urgencyScore(t) {
  const priorityWeight = { high: 30, medium: 15, low: 0 }[t.priority] ?? 15;
  let dueWeight = 0;
  if (t.due_date) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(t.due_date + (t.due_date.length === 10 ? "T00:00:00" : ""));
    const days = Math.round((due - today) / 86_400_000);
    if (days < 0)        dueWeight = 50 + Math.min(-days, 30); // overdue: older = more urgent
    else if (days === 0) dueWeight = 45;
    else if (days === 1) dueWeight = 35;
    else if (days <= 3)  dueWeight = 25;
    else if (days <= 7)  dueWeight = 15;
    else if (days <= 14) dueWeight = 8;
    else                 dueWeight = 3;
  }
  // A sender waiting on you, or a reply you haven't reviewed, nudges the task up
  const attention = (!t.is_sent || t.has_reply) ? 5 : 0;
  return dueWeight + priorityWeight + attention;
}

// Picks the N most urgent tasks from a list. Tasks parked in "Later" are
// excluded — the user has explicitly deferred them.
function pickTopTasks(list, n = 3) {
  return [...list]
    .filter(t => (t.priority_bucket || "today") !== "later")
    .sort((a, b) =>
      urgencyScore(b) - urgencyScore(a) ||
      (a.sort_order || 0) - (b.sort_order || 0) ||
      new Date(a.created_at || 0) - new Date(b.created_at || 0)
    )
    .slice(0, n);
}

// ── Activity helpers (Checklist view + task cards) ───────────────────────────
// The most recent thing that happened on a task: newest history entry, using the
// message's real date (`at`) when known, else the time we logged it (`ts`).
function lastActivity(t) {
  const h = Array.isArray(t.history) ? t.history : [];
  if (!h.length) {
    if (t.reply_snippet && t.reply_from) return { at: t.updated_at || t.created_at, from: t.reply_from, label: null, message: t.reply_snippet, msg_id: t.reply_msg_id || null, type: "update" };
    return null;
  }
  const when = e => new Date(e.at || e.ts || 0).getTime();
  const newest = [...h].sort((a, b) => when(b) - when(a))[0];
  return { at: newest.at || newest.ts, from: newest.from || "—", label: newest.label || null, message: newest.message || "", msg_id: newest.reply_msg_id || newest.msg_id || null, type: newest.type || "update" };
}

// Who has the ball. Returns a display name ("You", "Erika", "The Millhouse").
function nextActionOwner(t) {
  if (t.status === "complete") return null;
  if (t.action_owner === "assignee" || (t.is_sent && !t.has_reply && t.action_owner !== "you"))
    return (t.is_sent ? t.assignee_name : t.sender_name) || "Them";
  return "You";
}

function fmtShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

function defaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().split("T")[0];
}

// Deep-links directly to the specific message in Gmail
// For sent messages: #sent/ prefix; for received: #all/ works universally
// Direct message URL using Gmail's internal message ID (most reliable)
function gmailMsgUrl(messageId, isSent = false, userEmail = null) {
  const folder = isSent ? "sent" : "all";
  const authuser = userEmail ? `?authuser=${encodeURIComponent(userEmail)}` : "";
  return `https://mail.google.com/mail/u/0/${authuser}#${folder}/${messageId}`;
}

// Opens the exact message using direct Gmail message ID path with correct account
function gmailExactUrl(messageId, userEmail = null) {
  // Use direct message path — more reliable than rfc822msgid search
  // authuser forces the correct Google account
  const authParam = userEmail ? `?authuser=${encodeURIComponent(userEmail)}` : "";
  return `https://mail.google.com/mail/u/0/${authParam}#all/${messageId}`;
}

function extractSenderName(fromStr) {
  if (!fromStr) return "Unknown";
  // "John Smith <john@example.com>" → "John Smith"
  // "john@example.com" → "john@example.com"
  const match = fromStr.match(/^"?([^"<]+)"?\s*</);
  if (match) return match[1].trim();
  return fromStr.replace(/<.*>/, "").trim() || fromStr;
}


// ─── MANUAL TASK MODAL ───────────────────────────────────────────────────────
function AddTaskModal({ onAdd, onClose, projects = [], defaultProjectId = null }) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState(defaultDueDate());
  const [projectId, setProjectId] = useState(defaultProjectId || "");
  const [assignee, setAssignee] = useState("");
  const [notes, setNotes] = useState("");

  const submit = () => {
    if (!title.trim()) return;
    const proj = projects.find(p => p.id === projectId);
    const hasAssignee = assignee.trim() && assignee.trim().toLowerCase() !== "you";
    // With an assignee, this is work you delegated → sent-tracking with follow-up.
    const followUp = hasAssignee
      ? (dueDate
          ? new Date(new Date(dueDate).getTime() + 24 * 3600 * 1000).toISOString()
          : new Date(Date.now() + 48 * 3600 * 1000).toISOString())
      : null;
    onAdd({
      task_id: `manual-${Date.now()}`,
      title: title.trim(),
      owner_id: "u1",
      sender_id: "u1",
      sender_name: "You",
      assignee_name: hasAssignee ? assignee.trim() : "You",
      source: "Manual",
      source_label: "Manually added",
      priority,
      status: "open",
      due_date: dueDate || null,
      project_ids: projectId ? [projectId] : [],
      project: proj ? proj.name : null,
      context: notes.trim() || "Manually created task.",
      next_action: title.trim(),
      notes: notes.trim(),
      is_sent: hasAssignee,
      awaiting_response: hasAssignee,
      action_owner: hasAssignee ? "assignee" : "you",
      follow_up_after: followUp,
      priority_bucket: "today",
      sort_order: Date.now(),
      created_at: new Date().toISOString(),
      thread_id: null,
      source_url: null,
      gmail_id: null,
      history: [{ ts: new Date().toISOString(), type: "created", from: "You", label: hasAssignee ? "Task assigned" : "Task created", message: title.trim() }],
    });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#0F1829", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, padding: 28, width: 460, maxWidth: "95vw" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#DDE4F0", fontFamily: "'Lora', serif" }}>Add Task</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Task title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done?"
            style={inputStyle} autoFocus onKeyDown={e => e.key === "Enter" && submit()} />
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value)} style={inputStyle}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Due date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Project</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} style={inputStyle}>
              <option value="">None</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Assign to (optional)</label>
            <input value={assignee} onChange={e => setAssignee(e.target.value)} placeholder="Leave blank for yourself"
              style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional context..."
            rows={3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 13, color: "#475569", background: "transparent", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 7, padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} style={{ fontSize: 13, color: "#fff", background: "linear-gradient(135deg, #6366F1, #8B5CF6)", border: "none", borderRadius: 7, padding: "8px 18px", cursor: "pointer", fontWeight: 600 }}>Add Task</button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 11, color: "#64748B", fontWeight: 600, marginBottom: 5, letterSpacing: "0.05em", textTransform: "uppercase" };
const inputStyle = { width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: "8px 11px", color: "#DDE4F0", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

// ─── CLAUDE SYNC ENGINE ───────────────────────────────────────────────────────

// One place every sync-engine call goes through. Surfaces API errors instead of
// silently yielding empty text, and retries the transient ones (our own 429
// rate limit, Anthropic 429/529 overloaded) with backoff. A full re-sync fires
// dozens of calls in a minute, which is exactly when those show up.
class ClaudeCallError extends Error { constructor(msg, status) { super(msg); this.status = status; } }
async function callClaude(body, token = null, label = "claude") {
  let lastErr = null;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const res = await apiFetch("/api/claude", { method: "POST", body: JSON.stringify(body) }, token);
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      const apiErr = data?.error ? (typeof data.error === "string" ? data.error : (data.error.message || data.error.type || "API error")) : null;
      const status = res.status;
      const transient = status === 429 || status === 529 || status === 503 || /overloaded|rate.?limit/i.test(apiErr || "");
      if (!res.ok || apiErr) {
        lastErr = new ClaudeCallError(`${label}: ${apiErr || `HTTP ${status}`}`, status);
        if (transient && attempt < 3) {
          const wait = Math.min(15000, (data?.retryAfter ? data.retryAfter * 1000 : 0) || 2500 * Math.pow(2, attempt));
          console.warn(`${label}: transient error (${apiErr || status}), retrying in ${wait}ms`);
          await new Promise(r => setTimeout(r, wait));
          continue;
        }
        throw lastErr;
      }
      const text = (data.content || []).map(c => c.text || "").join("");
      if (!text.trim()) throw new ClaudeCallError(`${label}: empty response${data.stop_reason ? ` (stop: ${data.stop_reason})` : ""}`, status);
      return text;
    } catch(e) {
      lastErr = e instanceof ClaudeCallError ? e : new ClaudeCallError(`${label}: ${e.message}`, 0);
      if (e instanceof ClaudeCallError && e.status !== 0) throw e;   // non-transient API error — don't loop
      if (attempt < 3) { await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); continue; } // network blip
    }
  }
  throw lastErr;
}

// Pull the JSON array out of a model response, tolerating code fences, prose
// before/after the array, and a response truncated mid-array by max_tokens
// (keeps the complete objects, drops the cut-off one).
function parseJsonArray(text, label = "claude") {
  const cleaned = String(text || "").replace(/```json|```/g, "");
  const start = cleaned.indexOf("[");
  if (start === -1) { console.log(`${label}: no JSON array in response:`, cleaned.slice(0, 160)); return null; }
  const fromStart = cleaned.slice(start);
  const end = fromStart.lastIndexOf("]");
  if (end !== -1) {
    try { const p = JSON.parse(fromStart.slice(0, end + 1)); if (Array.isArray(p)) return p; } catch { /* try salvage */ }
  }
  // Salvage a truncated array: cut back to the last complete top-level object
  let depth = 0, inStr = false, esc = false, lastObjEnd = -1;
  for (let i = 0; i < fromStart.length; i++) {
    const c = fromStart[i];
    if (esc) { esc = false; continue; }
    if (c === "\\") { esc = true; continue; }
    if (c === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (c === "{" || c === "[") depth++;
    else if (c === "}" || c === "]") { depth--; if (depth === 1 && c === "}") lastObjEnd = i; }
  }
  if (lastObjEnd !== -1) {
    try { const p = JSON.parse(fromStart.slice(0, lastObjEnd + 1) + "]"); if (Array.isArray(p)) { console.warn(`${label}: response was truncated — salvaged ${p.length} entries`); return p; } } catch { /* give up */ }
  }
  console.log(`${label}: parse failed:`, cleaned.slice(0, 200));
  return null;
}

async function claudeExtractTasks(messages, token = null, projects = [], partner = null) {
  const projectCatalog = (projects || []).map(p => `- id: "${p.id}" | name: "${p.name}"${p.description ? ` | about: ${p.description.slice(0, 220)}` : ""}`).join("\n");
  const partnerLine = partner && (partner.name || partner.email)
    ? `\nOUR SIDE: The user plans together with ${partner.name || "their partner"}${partner.email ? ` (${partner.email})` : ""}. Emails FROM this person are written on behalf of the couple — treat them as "our side", the same as the user. If ${partner.name || "the partner"} emails a vendor and the user is cc'd, that is OUR enquiry to the vendor, not a request to the user.\n`
    : "";
  const systemPrompt = `You are an AI task extraction engine with live read access to Gmail, Slack, and QuickBooks.

The current user is "You" (u1). Each message includes a "current_user_email" field — this is YOUR email address. Use it to identify references to yourself in the email body (e.g. if current_user_email is "sean@haven.org", then "Sean" in the body = You).
${partnerLine}
Each message has a pre-computed "is_sent" boolean field set by the server — trust it completely. Do NOT try to infer sent/received from the "from" field yourself.

A single email may contain MULTIPLE tasks assigned to DIFFERENT people — extract ALL of them as separate task objects.

For each message, extract all actionable tasks. Rules:

RECEIVED messages (is_sent = false):
- Someone else sent this TO you — you need to act on it
- "sender_name" = extract from the "from" field
- "assignee_name" = "You"
- "is_sent" = false

SENT messages (is_sent = true):
- YOU sent this to someone — extract a separate task for EACH person you are asking, assigning, or proposing something to
- "sender_name" = "You"
- "assignee_name" = the specific person you're addressing (e.g. "Erika", "Jen", "Sean")
- "is_sent" = true
- Task title = what THAT SPECIFIC PERSON needs to do or respond to

WHAT COUNTS AS A SENT TASK (be generous — these all create tracking tasks):
- Direct assignments ("can you do X", "please handle Y")
- OPEN QUESTIONS you're asking them ("do we know where X is routed?", "any thoughts on Y?", "what's the status of Z?") — title these like "Confirm where X is routed" or "Get [recipient]'s thoughts on Y"
- Proposals or suggestions that need their response/agreement ("maybe we should X", "I think we should Y", "what if we Z") — title these like "Decide on X with [recipient]"
- Status checks or follow-ups
- Requests for confirmation ("does this work for you?", "let me know if...")
- Even if the email also contains compliments ("This is great!") or acknowledgments, EXTRACT the embedded question/proposal as a task

DO NOT EXTRACT (sent):
- Pure thank-you or compliment with zero questions/proposals
- Pure acknowledgments of completed work

NAME RESOLUTION:
- The "from" field contains the sender's email. The "to" field contains recipient emails.
- Use these to resolve names: if the email is from or sent by the current user, that person IS "You".
- Common aliases for "You": any name in the "from" field of a sent message, or names like "Sean", "I", "me" in the body.
- If a task is assigned to the same person who sent the email (i.e. "You"/Sean/the sender), treat it as a received task (is_sent = false, assignee_name = "You")
- Project = named after the assignee or their work area

GENERAL RULES:
- Extract ALL tasks from the message body — do not limit to one task per email
- If a message contains a numbered list of action items (e.g. "1. Erika will... 2. Jen will... 3. Sean will..."), create a SEPARATE task object for EACH item
- Never summarize multiple action items into one task — each individual assignment gets its own task entry with the same msg_id
- CREATE a task ONLY for truly actionable requests ("please complete", "can you", direct assignments, numbered action items)
- SKIP greetings, acknowledgments, casual check-ins, receipts, non-actionable statements
- SKIP any message that contains an unsubscribe link or button — these are marketing/promotional emails and are never actionable
- SKIP newsletters, promotional offers, automated notifications, and any bulk/mass email
- Infer priority from: explicit urgency, due dates, financial impact
- For due_date: use specific dates if mentioned, infer from urgency words (ASAP/urgent/today/tomorrow). If NO date and NO urgency, return null.
- If the entire message has no actionable content, return a single skip entry for that msg_id

CC-ONLY EMAILS (user was cc'd but not in To):
- Check the "cc_only" boolean field. If cc_only = true, the user was cc'd on this email but is not the primary recipient.
- Default behavior: set visibility = "fyi" for ALL tasks extracted from cc_only messages — they are informational, not directly actionable by the user
- EXCEPTION — set visibility = "normal" ONLY if:
  (a) The email body explicitly names the user (matching current_user_email or their first name) AND asks them to do something, OR
  (b) The task's assignee_name would be "You" based on direct instruction in the body (not just by being cc'd)
- When visibility = "fyi", still extract the task normally (capture the title, assignee, context) but mark it for pending-review in the FYI folder
- For non-cc_only messages (cc_only = false), always use visibility = "normal"

PROJECT ROUTING (file each task into the right project):
The user's projects are listed below with a description of what belongs in each. For EVERY task you create, pick the single best project by reading the email's actual content — vendor names, subject matter, what is being discussed — not just keywords.
- Set "project_id" to the matching project's id and "project_confidence" to 0.0–1.0
- Be honest with confidence: a vendor named in the project description → 0.9+; a clear topic match → 0.75; a plausible guess → 0.5; no idea → 0.2 and project_id null
- If nothing fits, leave project_id null. The user will be asked to file it manually.
PROJECTS:
${projectCatalog || "(none)"}

FORWARDED EMAILS (is_forwarded = true):
The user forwarded this email to themselves or received a forward. Forwards are almost always meant to be kept and reviewed, even when nothing in them says "please do X".
- ALWAYS create at least one task for a forwarded email. Never return skip for is_forwarded = true.
- If there is an explicit action, extract it normally.
- If there is NO explicit action, create one task with: title = a short summary of what the forward is about (NOT "Fwd: ..."), visibility = "review", next_action = "Review and decide whether anything is needed", and context = a 1-2 sentence summary of the forwarded content. Route it to a project like any other task.

VENDOR MENTIONS (return on EVERY message, including skips, as a top-level "vendor_mentions" array on the first entry for that msg_id):
A vendor is a business or person offering a service for one of the projects — a venue, photographer, band, florist, planner, caterer, etc. Include every vendor the message is TO, FROM, or ABOUT, even when the message creates no task. For each: { "vendor_name": "<name as written, e.g. 'Aoife' or 'The Millhouse'>", "project_id": "<from PROJECTS, or null>", "stage": <see below>, "note": "1 sentence: what this message tells us about this vendor" }.
Choose "stage" from what the message implies:
- "Discussed" — merely mentioned or compared as an option, no contact yet
- "Enquiry Sent" — OUR side wrote to the vendor (asked availability, pricing, questions)
- "Quote Received" — the vendor replied with pricing, packages or availability
- "Call / Meeting Held" — a call, tour or meeting is confirmed or has happened
- "Shortlisted" — our side expresses a clear preference for this vendor
- "Contract Sent" / "Booked" — a contract or deposit is sent / confirmed
- "Declined by Us" / "Unavailable" — we ruled them out / they can't do our date
Omit vendor_mentions (or return []) when the message involves no vendors.

EXAMPLE — if a sent email says:
"Action items: 1. Erika will create the guide. 2. Jen will send wireframes. 3. Sean will build the website."
You should return THREE separate task objects all with the same msg_id:
[
  { "msg_id": "abc", "action": "create", "title": "Create the guide", "assignee_name": "Erika", "is_sent": true, ... },
  { "msg_id": "abc", "action": "create", "title": "Send wireframes", "assignee_name": "Jen", "is_sent": true, ... },
  { "msg_id": "abc", "action": "create", "title": "Build the website", "assignee_name": "You", "is_sent": false, ... }
]

Return ONLY a valid JSON array with no markdown, no extra text.
Each element is either a task or a skip. One msg_id can appear multiple times (once per task):
[
  {
    "msg_id": "the original message id",
    "action": "create" or "skip",
    "skip_reason": "brief reason if skipped",
    "title": "concise actionable task title",
    "sender_name": "name of who sent the message (You or external sender)",
    "assignee_name": "name of who must act",
    "priority": "high" or "medium" or "low",
    "due_date": "YYYY-MM-DD or null",
    "project": "inferred project name",
    "context": "1-2 sentences: what needs doing and why",
    "is_sent": true if YOU assigned this to someone else, false if it is assigned to You,
    "visibility": "normal" | "fyi" | "review" (see CC-ONLY and FORWARDED rules above),
    "next_action": "imperative next step, under 15 words",
    "project_id": "id from PROJECTS, or null",
    "project_confidence": 0.0 to 1.0,
    "vendor_mentions": [ { "vendor_name": "...", "project_id": "...", "stage": "...", "note": "..." } ] or omit
  }
]`;

  const cleanedMessages = JSON.stringify(messages, null, 2).replace(/[^\x20-\x7E\n]/g, ' ');
  // Throws on API failure so the caller can retry those messages next sync
  // instead of silently marking them processed with nothing extracted.
  const text = await callClaude({
    model: "claude-sonnet-4-6",
    max_tokens: 6000,
    system: systemPrompt,
    messages: [{ role: "user", content: "Process these messages:\n" + cleanedMessages }],
  }, token, "extract");
  const parsed = parseJsonArray(text, "extract");
  if (!parsed) throw new ClaudeCallError("extract: unparseable response", 0);
  return parsed;
}

// ─── CLAUDE TRIAGE (Pass 1) ──────────────────────────────────────────────────
// Quick pass to classify messages as actionable or not, using only snippet.
// Returns array of { msg_id, actionable: true/false }
async function claudeTriageMessages(messages, token = null) {
  if (!messages.length) return [];
  const systemPrompt = `You are a quick email triage engine. For each message, decide if it MIGHT contain an actionable task or open question that requires someone to do something or respond.

Be VERY INCLUSIVE — when in doubt, mark actionable. The downstream extraction step will filter further.

If is_forwarded = true, ALWAYS mark actionable — forwarded emails are kept for review regardless of content.

Mark as ACTIONABLE if the email contains ANY of these (even mixed in with compliments or chit-chat):
- Direct requests ("can you", "please", "let's", "we should")
- Open QUESTIONS that need an answer ("do we know", "what about", "any thoughts on", "should we")
- Suggestions or proposals that imply a decision or change ("maybe we should", "I think we should", "what if we", "consider")
- Confirmations needed ("does this work", "let me know", "confirm")
- Follow-ups, status checks, or check-ins
- Logistics, scheduling, or coordination
- Sent emails containing any of the above (the user is asking or proposing — that creates a tracking task)

Mark as NOT ACTIONABLE only when highly confident it is one of:
- Marketing/promotional email, newsletter, automated notification, unsubscribe email, receipt
- Pure thank-you or pure acknowledgment with NO embedded question or proposal anywhere

ALSO classify every message into exactly one "category":
- "actionable" — matches the ACTIONABLE rules above
- "update"     — real human correspondence (or a vendor/system message about a real booking) with NOTHING to do, but that carries STATUS worth recording: a booking or payment confirmation, "received, thanks", "we'll get back to you Monday", meeting/tour confirmation, pricing or availability information, a contract or invoice attached, an itinerary, a cancellation. Vendor emails about the user's own bookings are "update", not noise.
- "noise"      — marketing, newsletters, promotions, cold outreach, automated notifications unrelated to a specific booking, social/app notifications

Return ONLY a valid JSON array:
[{ "msg_id": "...", "actionable": true or false, "category": "actionable" | "update" | "noise" }]`;

  try {
    const text = await callClaude({
      model: "claude-haiku-4-5-20251001",  // Haiku for fast/cheap triage
      max_tokens: 2048,                    // 20 msgs × 3 fields comfortably; 1024 could truncate
      system: systemPrompt,
      messages: [{ role: "user", content: "Triage these messages:\n" + JSON.stringify(messages, null, 2).replace(/[^\x20-\x7E\n]/g, ' ') }],
    }, token, "triage");
    const parsed = parseJsonArray(text, "triage");
    if (parsed) {
      // Any message the model left out is treated as actionable so nothing is missed
      const seen = new Set(parsed.map(r => r.msg_id));
      return [...parsed, ...messages.filter(m => !seen.has(m.msg_id)).map(m => ({ msg_id: m.msg_id, actionable: true, category: "actionable" }))];
    }
  } catch(e) {
    console.warn("Triage failed:", e.message);
  }
  // If triage fails, assume all are actionable so nothing is missed
  return messages.map(m => ({ msg_id: m.msg_id, actionable: true, category: "actionable" }));
}

// ─── CLAUDE STATUS UPDATES (Pass 1.5) ────────────────────────────────────────
// Messages with nothing to DO can still change the STATE of a task — a venue
// confirming a booking, a vendor acknowledging a deposit, a planner sending
// general information. This pass matches each such message to an existing task
// (or at least a project) and returns a dated, one-line status entry for it.
async function claudeExtractStatusUpdates(messages, taskCatalog, projects = [], token = null, partner = null) {
  if (!messages.length) return [];
  const projectCatalog = (projects || []).map(p => `- id: "${p.id}" | name: "${p.name}"${p.description ? ` | about: ${p.description.slice(0, 160)}` : ""}`).join("\n");
  const partnerLine = partner && (partner.name || partner.email)
    ? `OUR SIDE: the user plans together with ${partner.name || "their partner"}${partner.email ? ` (${partner.email})` : ""}. Emails from this person are on behalf of the couple — an email from them to a vendor is OUR enquiry.\n`
    : "";
  const systemPrompt = `You maintain a task checklist by reading emails that contain NO explicit request, and recording what they tell us about existing tasks and vendors.

Each message includes: msg_id, from, to, subject, body, timestamp, is_sent (true = the user sent it), and likely_task_ids (tasks that share the same email thread — strongly prefer one of these when present).
${partnerLine}

EXISTING TASKS (task_id | title | project | status | counterparty):
${taskCatalog || "(none)"}

PROJECTS:
${projectCatalog || "(none)"}

For each message decide:
1. task_id — the ONE existing task this message is about, or null. Match on the thread first (likely_task_ids), then on vendor/person name and subject matter. A completed task can still be matched (e.g. a confirmation arriving after the user marked it done).
2. project_id — the project this belongs to even if no task matches, or null.
3. summary — ONE sentence, under 20 words, third person, past tense, naming who did what. This is what the user will read in a "Last activity" column.
   Good: "The Millhouse confirmed the booking and received the deposit."
   Good: "Claire acknowledged the enquiry and will send pricing by Friday."
   Good: "You sent the signed contract to Fairyhill."
   Bad:  "Email received." / "Update on booking."
4. label — 2-5 words for a log line: "Booking confirmed", "Deposit received", "Pricing sent", "Acknowledged enquiry", "Tour confirmed", "Information sent".
5. kind — "confirmation" | "acknowledgement" | "information" | "scheduling" | "payment" | "cancellation" | "other"
6. marks_complete — true ONLY if this message clearly closes the matched task's loop (booking confirmed for a "Book X" task, item delivered for a "Send X" task, question fully answered).
7. next_action / next_action_owner — if the message implies a next step, write it as an imperative under 12 words and say who owns it: "you" or "them". Otherwise null. An acknowledgement like "we'll revert Monday" → next_action "Wait for Claire to send pricing", owner "them".
8. is_noise — true if on reflection this is marketing/automated/irrelevant and should be ignored entirely.
9. confidence — 0.0 to 1.0 that the task_id / project_id match is right.
10. vendor_mentions — every vendor (business or person offering a service: venue, photographer, band, florist…) the message is TO, FROM or ABOUT, as [{ "vendor_name": "<as written>", "project_id": "<from PROJECTS or null>", "stage": "...", "note": "1 sentence" }]. Stage from what the message implies: "Discussed" (mentioned/compared only) · "Enquiry Sent" (our side wrote to them) · "Quote Received" (they sent pricing/packages/availability) · "Call / Meeting Held" · "Shortlisted" (we express preference) · "Contract Sent" · "Booked" · "Declined by Us" · "Unavailable". Empty array if none.

Return ONLY a valid JSON array, one object per msg_id:
[{ "msg_id": "...", "task_id": "... or null", "project_id": "... or null", "confidence": 0.0-1.0, "summary": "...", "label": "...", "kind": "...", "marks_complete": false, "next_action": "... or null", "next_action_owner": "you" | "them" | null, "is_noise": false, "vendor_mentions": [] }]`;

  const text = await callClaude({
    model: "claude-sonnet-4-6",
    max_tokens: 4000,
    system: systemPrompt,
    messages: [{ role: "user", content: "Record status from these messages:\n" + JSON.stringify(messages, null, 2).replace(/[^\x20-\x7E\n]/g, ' ') }],
  }, token, "status");
  return parseJsonArray(text, "status") || [];
}

// ─── CLAUDE: is new extracted task a continuation of existing thread task? ──
// Returns a map of new_task_id -> existing_task_id (or null if genuinely new)
async function claudeMatchThreadTasks(candidates, token = null) {
  // candidates: [{ new_task_id, new_title, new_context, existing_tasks: [{task_id, title, context}] }]
  if (!candidates.length) return {};

  const systemPrompt = `You decide whether a newly extracted task is a continuation of an existing open task in the same email thread, or whether the thread has drifted to a genuinely new topic that deserves its own task.

RULES:
- If the new task describes the same deliverable/goal as an existing task (even with different wording), return the existing task_id — it is a continuation
- If the new task is about a clearly different topic (scheduling a meeting when the original was about design work, asking about something unrelated, etc.), return null — it should become a new task
- Do NOT match tasks just because they share a thread. Threads drift — match on semantic similarity of the work to be done

Return ONLY a valid JSON array:
[{ "new_task_id": "...", "matches_existing": "task_id or null", "reason": "brief reason" }]`;

  try {
    const text = await callClaude({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 2048,
      system: systemPrompt,
      messages: [{ role: "user", content: "Match these:\n" + JSON.stringify(candidates, null, 2).replace(/[^\x20-\x7E\n]/g, ' ') }],
    }, token, "thread-match");
    const arr = parseJsonArray(text, "thread-match") || [];
    const map = {};
    arr.forEach(r => { map[r.new_task_id] = r.matches_existing || null; });
    return map;
  } catch(e) { console.warn("Thread-match failed:", e.message); return {}; }
}

// ─── CLAUDE REPLY ANALYSIS ────────────────────────────────────────────────────
// Given open sent tasks and their thread messages, classify what happened in each thread.
async function claudeAnalyzeReplies(threadData, token = null) {
  // threadData: [{ task_id, task_title, assignee_name, messages: [{from, snippet, is_sent_by_user, timestamp}] }]
  if (!threadData.length) return [];

  const systemPrompt = `You analyze email threads to determine who currently owns the action on each task.

For each thread you receive:
- task_id, task_title, task_type ("sent" or "received"), assignee_name
- messages: full thread in chronological order (oldest first, newest last), each with: from, snippet, is_sent_by_user, timestamp

## CRITICAL DECISION PROCESS

Step 1: Find the LAST SUBSTANTIVE message in the thread.
  - IGNORE emoji-only reactions ("reacted via Gmail", "👍", "❤️")
  - IGNORE pure acknowledgments with no content ("thanks!", "ok", "got it", "sounds good")
  - A substantive message has actual information, a question, a commitment, a deliverable, or a decision
  - CONFIRMATIONS that complete a loop ARE substantive (e.g. confirming a meeting time the other party proposed)

Step 2: Determine who sent the last substantive message using is_sent_by_user:
  - is_sent_by_user = true → the current user (You) sent it
  - is_sent_by_user = false → someone else sent it

Step 3: Determine outcome based on WHO sent last AND what it said:

  If the LAST substantive message was sent by the USER (is_sent_by_user = true):
    - If it clearly completes the task ("here it is", "done", "just sent", confirming a meeting time they asked about) → "completed"
    - Otherwise → "waiting_on_assignee" (ball is back with them)
    - reply_from_is_user MUST be true
    - reply_from MUST be "You"

  If the LAST substantive message was sent by SOMEONE ELSE (is_sent_by_user = false):
    - If it clearly confirms completion ("done!", "sent it") → "completed"
    - Otherwise → "waiting_on_user" (they asked something, you need to respond)
    - reply_from_is_user MUST be false
    - reply_from MUST be the sender's name

Step 4: Detect topic drift (use sparingly — only for clear, unresolved new action items).
  - Set topic_drift: true ONLY when ALL of these are true:
    (a) Recent messages are about a substantively different topic than task_title (not just "the conversation continued")
    (b) The new topic contains a CLEAR UNRESOLVED action item that someone still needs to do
    (c) The new topic is NOT already resolved or just confirming something (a scheduled meeting that's been confirmed = resolved = NOT drift)
  - Default to topic_drift: false when unsure or when the new topic is just conversational/logistical/already resolved
  - Provide drift_title as a SHORT ACTIONABLE title (max 60 chars) in the form of a task, e.g. "Meet with Jen at 10am Friday" — NOT "Recent messages are about scheduling"
  - Provide drift_summary as 1 sentence of context

## EXAMPLE

Thread messages (oldest to newest):
  1. from:"You", snippet:"Can you send wireframes?", is_sent_by_user:true
  2. from:"Jen", snippet:"Here they are [attached]", is_sent_by_user:false
  3. from:"You", snippet:"Looks good, let's meet Friday 10am to discuss", is_sent_by_user:true
  4. from:"Jen", snippet:"10am Friday works for me!", is_sent_by_user:false
  5. from:"You", snippet:"Chat then!", is_sent_by_user:true

Last substantive message = #5 from You, confirming the meeting.
→ outcome: "completed" (meeting is set, nothing outstanding)
→ reply_from_is_user: true
→ reply_from: "You"
→ status_update: "User confirmed Friday 10am meeting with Jen"

## WRITING next_action, thread_summary AND event_label

next_action — the single outstanding step, written as a direct instruction to
the reader. Start with a verb. One sentence, under 15 words. Name the person.
  Good: "Reply to Jen about the whimsical logo direction she proposed."
  Good: "Wait for Erika to send the signed contract."
  Bad:  "Jen sent logo options and is awaiting a response." (describes, doesn't instruct)
If the task is complete, say what closed it: "Friday 10am call is confirmed."

thread_summary — 2-3 sentences of plain prose covering the whole conversation
from the beginning: what was originally asked, what has happened since, and
where it stands now. Written in second person for the reader's own actions
("You asked Jen to…"). Do NOT bullet it. Do NOT prefix with labels like
"Status:" or "Update:". This replaces reading the thread, so it must stand alone.

event_label — a 3-6 word summary of the single most recent substantive message,
for a one-line log entry. No punctuation at the end.
  Good: "Sent logo options, awaiting direction"
  Bad:  "Jen sent over the logo options and is now waiting to hear back"

## REQUIRED OUTPUT

Return ONLY a valid JSON array:
[
  {
    "task_id": "...",
    "outcome": "completed" | "waiting_on_assignee" | "waiting_on_user" | "pending",
    "action_owner": "you" | "assignee" | "done",
    "next_action": "imperative sentence — see rules above",
    "thread_summary": "2-3 sentence prose summary of the whole thread",
    "event_label": "3-6 words describing the latest message",
    "reply_snippet": "first 100 chars of the LAST SUBSTANTIVE message",
    "reply_from": "name of sender (use 'You' if is_sent_by_user:true)",
    "reply_from_is_user": true | false,
    "reply_msg_id": "Gmail message ID of last substantive message",
    "status_update": "1 short sentence in third person",
    "topic_drift": true | false,
    "drift_title": "if topic_drift: short actionable task title (max 60 chars), or null",
    "drift_summary": "if topic_drift: 1 sentence of context, or null"
  }
]`;

  const text = await callClaude({
    model: "claude-sonnet-4-6",
    max_tokens: 4000,
    system: systemPrompt,
    messages: [{ role: "user", content: "Analyze these threads:\n" + JSON.stringify(threadData, null, 2).replace(/[^\x20-\x7E\n]/g, ' ') }],
  }, token, "replies");
  return parseJsonArray(text, "replies") || [];
}

// ─── TASK CARD ────────────────────────────────────────────────────────────────
function TaskCard({ task, onComplete, isNew, onUpdateDueDate, onUpdateVisibility, projects = [], onSetProject, onSetStage, onReplyDrafted }) {
  const [open, setOpen] = useState(false);
  const [glowing, setGlowing] = useState(isNew);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState(task.notes || "");
  const [savedNote, setSavedNote] = useState(task.notes || "");
  const [followUpDraft, setFollowUpDraft] = useState(null);   // composer text (null = composer closed)
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [draftStatus, setDraftStatus] = useState(null);        // null | "creating" | "opened" | { error }
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [editingDate, setEditingDate] = useState(task.due_date || "");
  const pm = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const overdue = task.due_date && !task.is_sent && new Date(task.due_date) < new Date();
  const needsFollowUp = task.is_sent && task.awaiting_response && task.follow_up_after && new Date(task.follow_up_after) <= new Date();
  const days = task.due_date ? Math.ceil((new Date(task.due_date) - new Date()) / 86400000) : null;

  useEffect(() => {
    if (isNew) { const t = setTimeout(() => setGlowing(false), 3500); return () => clearTimeout(t); }
  }, [isNew]);

  const dueTxt = !task.due_date ? null :
    overdue ? `Overdue ${new Date(task.due_date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}` :
    days === 0 ? "Due today" : days === 1 ? "Due tomorrow" :
    `Due ${new Date(task.due_date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}`;

  const senderDisplay = task.is_sent
    ? (task.assignee_name || "Unknown")
    : (task.sender_name || "Unknown");

  // Single source of truth for ownership. `full` reads on the collapsed meta
  // line, `short` is the eyebrow above the next action in the expanded view.
  const ownership = (() => {
    if (task.status === "complete") return { full: "Completed", short: "Completed", color: "#34D399" };
    if (task.is_sent) {
      if (task.action_owner === "you" || task.has_reply)
        return { full: `Your turn · reply from ${senderDisplay}`, short: "Your turn", color: "#34D399" };
      if (needsFollowUp)
        return { full: `Follow-up needed · waiting on ${senderDisplay}`, short: "Follow-up needed", color: "#FBBF24" };
      return { full: `Waiting on ${senderDisplay}`, short: `Waiting on ${senderDisplay}`, color: "#60A5FA" };
    }
    if (task.action_owner === "assignee")
      return { full: `Waiting on ${senderDisplay} · you replied`, short: `Waiting on ${senderDisplay}`, color: "#60A5FA" };
    return { full: `Your turn · from ${senderDisplay}`, short: "Your turn", color: "#C084FC" };
  })();

  // The imperative next step. Falls back to the original context for tasks that
  // have never been through reply analysis (single-message threads).
  const nextAction = task.next_action || task.context || null;
  const threadSummary = task.thread_summary || null;
  const last = lastActivity(task);
  const showLast = last && last.type !== "created";
  const nextOwner = nextActionOwner(task);
  const history = Array.isArray(task.history) ? task.history : [];

  const gmailLink = task.gmail_id ? gmailExactUrl(task.gmail_id, window.__toodue_gmail_email__) : null;

  // Ask Claude for a reply / follow-up body, using everything we know about the thread.
  const generateFollowUp = async (e) => {
    e && e.stopPropagation();
    setFollowUpLoading(true);
    setDraftStatus(null);
    const counterpart = task.is_sent ? task.assignee_name : task.sender_name;
    const mode = task.is_sent && task.awaiting_response && !task.has_reply ? "follow-up" : "reply";
    try {
      const res = await apiFetch('/api/claude', {
        method: 'POST',
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 400,
          system: `You write short, warm, professional emails on the user's behalf. Write ONLY the email body — no subject line, no greeting line like "Hi Name," and no sign-off; the user adds those in Gmail. 2-4 sentences. Be specific to the thread; never invent facts, dates or prices that are not in the context. If a decision or detail is needed from the user, leave a clearly marked placeholder like [confirm date].`,
          messages: [{ role: 'user', content: `Write a ${mode} email for this task.
Task: ${task.title}
Talking to: ${counterpart || 'the other party'}
${mode === "follow-up" ? "Purpose: politely check on progress and reference what was originally requested." : "Purpose: respond to their latest message and move the task forward."}
What needs to happen next: ${task.next_action || task.context || ''}
Thread so far: ${task.thread_summary || task.context || ''}
Their latest message: ${task.reply_snippet || ''}
Original subject: ${task.source_label || ''}` }]
        })
      }, window.__toodue_token__);
      const data = await res.json();
      const draft = (data.content || []).map(c => c.text || '').join('').trim();
      setFollowUpDraft(draft || '');
    } catch(e) {
      setFollowUpDraft('');
      setDraftStatus({ error: 'Could not generate a draft — write one below or try again.' });
    }
    setFollowUpLoading(false);
  };

  // Create a real Gmail DRAFT inside the task's thread and open it. Replying in
  // the thread is what lets the sync engine attach the sent message (and every
  // later reply) back to this task.
  const createGmailDraft = async (draft) => {
    setDraftStatus("creating");
    try {
      const res = await apiFetch('/api/gmail/draft', {
        method: 'POST',
        body: JSON.stringify({
          thread_id: task.thread_id || null,
          to: task.thread_id ? undefined : "",
          subject: task.thread_id ? undefined : (task.source_label && task.source_label !== "Manually added" ? task.source_label : task.title),
          body: draft || "",
        }),
      }, window.__toodue_token__);
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'reconnect_gmail') {
          setDraftStatus({ error: 'Toodue needs permission to create drafts. Reconnect Gmail (Settings → Reconnect) and try again.', reconnect: true });
        } else if (data.error === 'gmail_not_connected') {
          setDraftStatus({ error: 'Connect Gmail first (Settings).', reconnect: true });
        } else {
          setDraftStatus({ error: data.message || data.error || `Draft failed (${res.status})` });
        }
        return;
      }
      window.open(data.url, '_blank');
      onReplyDrafted && onReplyDrafted(task.task_id, { thread_id: data.thread_id, message_id: data.message_id, to: data.to, subject: data.subject, snippet: (draft || "").slice(0, 140) });
      setDraftStatus("opened");
      setFollowUpDraft(null);
    } catch(e) {
      setDraftStatus({ error: e.message });
    }
  };

  const openComposer = (e) => {
    e && e.stopPropagation();
    setDraftStatus(null);
    setFollowUpDraft(prev => prev === null ? "" : prev);
  };

  const linkStyle = { fontSize: 12, color: "#7A8CA3", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", textDecoration: "none" };
  const dot = <span style={{ color: "#2A3648", fontSize: 12 }}>·</span>;

  return (
    <div
      onClick={() => setOpen(x => !x)}
      style={{
        background: glowing ? "rgba(99,102,241,0.06)" : "transparent",
        borderBottom: "0.5px solid rgba(255,255,255,0.06)",
        borderRadius: 0,
        padding: "13px 4px",
        cursor: "pointer",
        transition: "background 0.5s ease",
        animation: glowing ? "slidein 0.3s ease" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <button
          onClick={e => { e.stopPropagation(); onComplete(task.task_id); }}
          aria-label={task.status === "complete" ? "Mark incomplete" : "Mark complete"}
          style={{
            width: 15, height: 15, borderRadius: "50%",
            border: `1.5px solid ${task.status === "complete" ? "#34D399" : "#3A4A61"}`,
            background: task.status === "complete" ? "#34D399" : "transparent",
            cursor: "pointer", flexShrink: 0, position: "relative", top: 2, padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s",
          }}
        >
          {task.status === "complete" && <span style={{ color: "#080D1A", fontSize: 9, fontWeight: 900, lineHeight: 1 }}>✓</span>}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{
              fontFamily: "'Lora', Georgia, serif", fontSize: 15,
              color: task.status === "complete" ? "#3A4A61" : "#E8EDF5",
              textDecoration: task.status === "complete" ? "line-through" : "none",
            }}>{task.title}</span>
            {glowing && <span style={{ fontSize: 11, color: "#818CF8" }}>New</span>}
            {task.visibility === "fyi" && <span style={{ fontSize: 11, color: "#7DD3FC" }}>FYI</span>}
            {task.visibility === "review" && <span style={{ fontSize: 11, color: "#FBBF24" }}>Forwarded · review</span>}
            {task.needs_filing && <span style={{ fontSize: 11, color: "#F0ABFC" }}>Needs filing</span>}
            {task.kind === "candidate" && task.vendor_stage && (
              <span style={{ fontSize: 11, color: STAGE_COLOR[task.vendor_stage] || "#94A3B8", border: `1px solid ${(STAGE_COLOR[task.vendor_stage] || "#94A3B8")}44`, borderRadius: 4, padding: "0 6px" }}>{task.vendor_stage}</span>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#5B6B80" }}>
            <span style={{ color: ownership.color }}>{ownership.full}</span>
            {dueTxt && <> · <span style={{ color: overdue ? "#F87171" : days !== null && days <= 1 ? "#FBBF24" : "#5B6B80" }}>{dueTxt}</span></>}
            {task.project && <> · {task.project}</>}
            {savedNote && <> · note</>}
          </div>
          {showLast && (
            <div style={{ fontSize: 12, color: "#4A5A70", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontFamily: "monospace", fontSize: 11 }}>{fmtShortDate(last.at)}</span> · <span style={{ color: last.from === "You" ? "#8B93C9" : "#5FA8C9" }}>{last.from}</span> — {last.label || last.message}
            </div>
          )}
        </div>

        <span
          title={`${task.priority} priority`}
          style={{ width: 6, height: 6, borderRadius: "50%", background: pm.color, flexShrink: 0, position: "relative", top: -2 }}
        />
        <span style={{ fontSize: 12, color: "#3A4A61", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
      </div>

      {open && (
        <div style={{ marginTop: 16, paddingLeft: 27, animation: "fadein 0.2s ease" }} onClick={e => e.stopPropagation()}>

          {task.needs_filing && projects.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "#F0ABFC", marginBottom: 5 }}>Which project does this belong to?</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.6, marginBottom: 8 }}>
                I couldn't tell from the email. Pick one, or leave it out of projects.
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select defaultValue="" onChange={e => { e.stopPropagation(); if (e.target.value) onSetProject && onSetProject(task.task_id, e.target.value); }} onClick={e => e.stopPropagation()}
                  style={{ fontSize: 12, background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 6, color: "#CBD5E1", padding: "5px 8px", fontFamily: "inherit" }}>
                  <option value="">Choose a project…</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button onClick={e => { e.stopPropagation(); onSetProject && onSetProject(task.task_id, null); }} style={linkStyle}>No project</button>
              </div>
            </div>
          )}

          {task.kind === "candidate" && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 5 }}>Pipeline stage</div>
              <select value={task.vendor_stage || "Identified"} onChange={e => { e.stopPropagation(); onSetStage && onSetStage(task.task_id, e.target.value); }} onClick={e => e.stopPropagation()}
                style={{ fontSize: 12, background: "#0F1B2D", border: `1px solid ${(STAGE_COLOR[task.vendor_stage] || "#1E3A5F")}66`, borderRadius: 6, color: STAGE_COLOR[task.vendor_stage] || "#CBD5E1", padding: "5px 8px", fontFamily: "inherit" }}>
                {VENDOR_STAGES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
              {task.source_url && <a href={task.source_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ ...linkStyle, marginLeft: 12 }}>Website ↗</a>}
            </div>
          )}

          {task.visibility === "review" && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "#FBBF24", marginBottom: 5 }}>Forwarded email — nothing explicit to do</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.6, marginBottom: 8 }}>
                Kept so it isn't lost. Keep it as a task, or dismiss it.
              </div>
              <div style={{ display: "flex", gap: 14, fontSize: 12 }}>
                <button onClick={e => { e.stopPropagation(); onUpdateVisibility && onUpdateVisibility(task.task_id, "normal"); }} style={{ ...linkStyle, color: "#34D399" }}>Keep as task</button>
                <button onClick={e => { e.stopPropagation(); onUpdateVisibility && onUpdateVisibility(task.task_id, "dismissed"); }} style={linkStyle}>Dismiss</button>
              </div>
            </div>
          )}

          {task.visibility === "fyi" && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "#7DD3FC", marginBottom: 5 }}>You were cc'd</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.6, marginBottom: 8 }}>
                This may not need anything from you. Keep it or dismiss it.
              </div>
              <div style={{ display: "flex", gap: 14, fontSize: 12 }}>
                <button onClick={e => { e.stopPropagation(); onUpdateVisibility && onUpdateVisibility(task.task_id, "normal"); }}
                  style={{ ...linkStyle, color: "#34D399" }}>Keep</button>
                <button onClick={e => { e.stopPropagation(); onUpdateVisibility && onUpdateVisibility(task.task_id, "dismissed"); }}
                  style={linkStyle}>Dismiss</button>
              </div>
            </div>
          )}

          {nextAction && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: ownership.color, marginBottom: 5 }}>
                {task.status === "complete" ? "Closed with" : "Next action"}
                {nextOwner && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, borderRadius: 5, padding: "1px 7px", color: nextOwner === "You" ? "#C084FC" : "#60A5FA", background: nextOwner === "You" ? "rgba(192,132,252,0.12)" : "rgba(96,165,250,0.12)" }}>→ {nextOwner}</span>}
              </div>
              <div style={{ fontSize: 15, color: "#E8EDF5", lineHeight: 1.5 }}>{nextAction}</div>
            </div>
          )}

          {showLast && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#475569", letterSpacing: "0.08em", marginBottom: 6 }}>Last activity</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.65 }}>
                <span style={{ color: "#64748B", fontFamily: "monospace", fontSize: 12 }}>{fmtShortDate(last.at)}</span>
                {" · "}<span style={{ color: last.from === "You" ? "#A5B4FC" : "#7DD3FC" }}>{last.from}</span>
                {last.label && <> · {last.label}</>}
                {last.msg_id && <a href={gmailExactUrl(last.msg_id)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ marginLeft: 8, color: "#475569", fontSize: 11, textDecoration: "none" }}>Open in Gmail ↗</a>}
                {last.message && last.message !== last.label && <div style={{ marginTop: 3 }}>{last.message}</div>}
              </div>
            </div>
          )}

          {threadSummary && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#475569", letterSpacing: "0.08em", marginBottom: 6 }}>Thread</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.65 }}>{threadSummary}</div>
            </div>
          )}

          {history.length > 0 && (
            <div style={{ marginBottom: 20, fontSize: 12, color: "#5B6B80", lineHeight: 1.9 }}>
              {history.map((h, i) => {
                const isYou = h.from === "You" || h.from === "you";
                const text = h.label || h.message || "";
                return (
                  <div key={i} style={{ display: "flex", gap: 14 }}>
                    <span style={{ width: 48, color: "#3A4A61", flexShrink: 0 }}>
                      {new Date(h.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                    <span style={{ width: 52, color: isYou ? "#C084FC" : "#94A3B8", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {isYou ? "You" : h.from}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
                  </div>
                );
              })}
            </div>
          )}

          {savedNote && !showNoteInput && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#475569", letterSpacing: "0.08em", marginBottom: 6 }}>Note</div>
              <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.65 }}>{savedNote}</div>
            </div>
          )}

          {showNoteInput && (
            <div style={{ marginBottom: 20 }}>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note"
                rows={3}
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5, marginBottom: 8 }}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
              <div style={{ display: "flex", gap: 14 }}>
                <button onClick={e => { e.stopPropagation(); setSavedNote(noteText); setShowNoteInput(false); }}
                  style={{ ...linkStyle, color: "#34D399" }}>Save note</button>
                <button onClick={e => { e.stopPropagation(); setNoteText(savedNote); setShowNoteInput(false); }}
                  style={linkStyle}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", paddingTop: 14, borderTop: "0.5px solid rgba(255,255,255,0.05)", fontSize: 12 }}>
            {showDatePicker ? (
              <>
                <input
                  type="date"
                  value={editingDate}
                  onChange={e => setEditingDate(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize: 12, background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 5, color: "#CBD5E1", padding: "3px 7px", cursor: "pointer", fontFamily: "inherit" }}
                />
                <button onClick={e => { e.stopPropagation(); onUpdateDueDate(task.task_id, editingDate); setShowDatePicker(false); }}
                  style={{ ...linkStyle, color: "#34D399" }}>Save</button>
                <button onClick={e => { e.stopPropagation(); setEditingDate(task.due_date || ""); setShowDatePicker(false); }}
                  style={linkStyle}>Cancel</button>
              </>
            ) : (
              <button onClick={e => { e.stopPropagation(); setShowDatePicker(true); }}
                style={{ ...linkStyle, color: "#5B6B80" }}>
                Due <span style={{ color: overdue ? "#F87171" : "#94A3B8", borderBottom: "1px dotted #3A4A61" }}>
                  {task.due_date ? new Date(task.due_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "not set"}
                </span>
              </button>
            )}

            {gmailLink && <>{dot}<a href={gmailLink} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={linkStyle}>Open in Gmail</a></>}

            {dot}
            <button onClick={e => { e.stopPropagation(); setShowNoteInput(x => !x); }} style={linkStyle}>
              {savedNote ? "Edit note" : "Add note"}
            </button>

            {task.status !== "complete" && (task.kind || "action") !== "info" && (
              <>{dot}<button onClick={openComposer}
                style={{ ...linkStyle, color: "#A5B4FC", fontWeight: 600 }}>
                {task.thread_id ? "↩ Reply" : "✉ Email"}
              </button></>
            )}

            {task.is_sent && task.awaiting_response && task.status !== "complete" && (
              <>{dot}<button onClick={e => { openComposer(e); generateFollowUp(e); }} disabled={followUpLoading}
                style={{ ...linkStyle, color: "#FBBF24", cursor: followUpLoading ? "wait" : "pointer", opacity: followUpLoading ? 0.6 : 1 }}>
                {followUpLoading ? "Drafting…" : "Draft follow-up"}
              </button></>
            )}

            {task.status !== "complete" && (
              <>{dot}<button onClick={e => { e.stopPropagation(); onComplete(task.task_id); }}
                style={{ ...linkStyle, color: "#34D399" }}>Mark complete</button></>
            )}
          </div>

          {followUpDraft !== null && (
            <div onClick={e => e.stopPropagation()} style={{ marginTop: 16, border: "1px solid rgba(99,102,241,0.25)", background: "rgba(99,102,241,0.05)", borderRadius: 9, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "#A5B4FC", letterSpacing: "0.08em", fontWeight: 700 }}>
                  {task.thread_id ? `REPLY TO ${(task.is_sent ? task.assignee_name : task.sender_name) || "THREAD"}`.toUpperCase() : "NEW EMAIL"}
                </span>
                <span style={{ fontSize: 11, color: "#475569" }}>{task.thread_id ? "stays in the same Gmail thread" : "starts a new thread linked to this task"}</span>
                <div style={{ flex: 1 }} />
                <button onClick={generateFollowUp} disabled={followUpLoading} style={{ ...linkStyle, color: "#FBBF24", opacity: followUpLoading ? 0.6 : 1 }}>
                  {followUpLoading ? "Drafting…" : followUpDraft ? "✨ Redraft with Claude" : "✨ Draft with Claude"}
                </button>
              </div>
              <textarea
                value={followUpDraft}
                onChange={e => setFollowUpDraft(e.target.value)}
                placeholder={followUpLoading ? "Claude is drafting…" : "Write your reply here, or let Claude draft it. You'll finish and send it in Gmail."}
                style={{ width: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 7, padding: "10px 12px", color: "#CBD5E1", fontSize: 13, lineHeight: 1.6, resize: "vertical", minHeight: 90, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
              <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 12, alignItems: "center", flexWrap: "wrap" }}>
                <button onClick={() => createGmailDraft(followUpDraft)} disabled={draftStatus === "creating"}
                  style={{ fontSize: 12, fontWeight: 600, color: "#0B1220", background: "#34D399", border: "none", borderRadius: 6, padding: "6px 14px", cursor: draftStatus === "creating" ? "wait" : "pointer", opacity: draftStatus === "creating" ? 0.7 : 1 }}>
                  {draftStatus === "creating" ? "Creating draft…" : "Open in Gmail →"}
                </button>
                <button onClick={() => { setFollowUpDraft(null); setDraftStatus(null); }} style={linkStyle}>Cancel</button>
                {draftStatus && draftStatus.error && (
                  <span style={{ color: "#F87171", fontSize: 12 }}>{draftStatus.error}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#3A4A61", marginTop: 8 }}>
                Toodue only creates a draft — you review and send from Gmail. Once sent, the next sync updates this task from the email, and later replies in the thread keep it current.
              </div>
            </div>
          )}
          {draftStatus === "opened" && followUpDraft === null && (
            <div style={{ marginTop: 12, fontSize: 12, color: "#34D399" }}>Draft opened in Gmail. Send it there — this task will update on the next sync.</div>
          )}

          {task.status === "complete" && task.completed_at && (
            <div style={{ marginTop: 12, fontSize: 12, color: "#34D399" }}>
              Completed {new Date(task.completed_at).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SYNC STATUS BAR ──────────────────────────────────────────────────────────
function SyncBar({ status, lastSync, newCount, onManualSync, gmailConnected, gmailExpired = false, onConnectGmail, onResync, onTestGmail, gmailCheck = null }) {
  const needsGmail = gmailConnected === false;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      background: "rgba(255,255,255,0.018)", border: "1px solid rgba(255,255,255,0.05)",
      borderRadius: 10, padding: "10px 16px", marginBottom: 20,
    }}>
      {["Gmail","Slack","QuickBooks"].map(s => {
        const off = s === "Gmail" && needsGmail;
        return (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: off ? "#F87171" : "#34D399", boxShadow: off ? "0 0 5px rgba(248,113,113,0.6)" : "0 0 5px rgba(52,211,153,0.6)", animation: status === "syncing" && !off ? "syncpulse 1.5s ease-in-out infinite" : "none" }} />
            <span style={{ fontSize: 11, color: SOURCE_COLORS[s], fontWeight: 600 }}>{s}</span>
          </div>
        );
      })}
      <div style={{ width: 1, height: 14, background: "rgba(255,255,255,0.06)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {status === "syncing" ? (
          <>
            <span style={{ display: "inline-block", animation: "spin 1s linear infinite", color: "#FBBF24", fontSize: 13 }}>↻</span>
            <span style={{ fontSize: 12, color: "#FBBF24", fontWeight: 500 }}>Claude is reading your messages…</span>
          </>
        ) : needsGmail ? (
          <>
            <span style={{ fontSize: 12, color: "#FBBF24", fontWeight: 500 }}>{gmailExpired ? "Gmail access expired — reconnect to resume syncing" : "Gmail isn't connected for this account"}</span>
            <button onClick={onConnectGmail}
              style={{ fontSize: 11, fontWeight: 600, color: "#fff", background: "#EA4335", border: "none", borderRadius: 6, padding: "4px 11px", cursor: "pointer", marginLeft: 4 }}>
              {gmailExpired ? "Reconnect Gmail" : "Connect Gmail"}
            </button>
          </>
        ) : status === "error" ? (
          <span style={{ fontSize: 12, color: "#F87171" }}>⚠ Sync error — check connection</span>
        ) : (
          <>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34D399", boxShadow: "0 0 5px rgba(52,211,153,0.5)" }} />
            <span style={{ fontSize: 12, color: "#34D399", fontWeight: 500 }}>All synced</span>
          </>
        )}
      </div>
      {lastSync && <span style={{ fontSize: 11, color: "#1E2D40" }}>Updated {lastSync}</span>}
      {newCount > 0 && <span style={{ fontSize: 11, background: "rgba(99,102,241,0.2)", color: "#A5B4FC", borderRadius: 8, padding: "2px 8px", fontWeight: 600 }}>+{newCount} tasks added</span>}
      <div style={{ flex: 1 }} />
      <button
        onClick={onManualSync}
        disabled={status === "syncing"}
        style={{
          fontSize: 11, color: "#475569", background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)", borderRadius: 6, padding: "4px 11px",
          cursor: status === "syncing" ? "default" : "pointer", display: "flex", alignItems: "center", gap: 5,
        }}
      >
        <span style={{ display: "inline-block", animation: status === "syncing" ? "spin 1s linear infinite" : "none" }}>↻</span>
        Sync Now
      </button>
      {onTestGmail && (
        <button onClick={onTestGmail} disabled={status === "syncing"}
          style={{ fontSize: 11, color: "#64748B", background: "transparent", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
          Test Gmail
        </button>
      )}
      {gmailCheck && (
        <div style={{ flexBasis: "100%", fontSize: 12, color: gmailCheck.pending ? "#94A3B8" : gmailCheck.ok ? "#34D399" : "#F87171", marginTop: 4, lineHeight: 1.5 }}>
          {gmailCheck.pending ? "Testing Gmail…" : gmailCheck.ok
            ? `✓ Gmail OK — ${gmailCheck.email}: ${gmailCheck.inbox7d} inbox + ${gmailCheck.sent7d} sent messages in the last 7 days${gmailCheck.scope && !gmailCheck.scope.includes("compose") ? " · (reply drafts need a reconnect)" : ""}`
            : `✗ Gmail ${gmailCheck.connected === false ? "not connected" : "FAILED"}: ${gmailCheck.error}${gmailCheck.reconnect ? " — reconnect Gmail in Settings" : ""}`}
        </div>
      )}
      {onResync && (
        <span title="Re-read this many days of mail and re-triage anything that isn't already a task" style={{ fontSize: 11, color: "#334155", display: "flex", gap: 6, alignItems: "center" }}>
          re-sync
          {[1, 7, 30].map(d => (
            <button key={d} onClick={() => onResync(d)} disabled={status === "syncing"}
              style={{ fontSize: 11, color: "#64748B", background: "transparent", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 5, padding: "2px 7px", cursor: status === "syncing" ? "default" : "pointer" }}>{d}d</button>
          ))}
        </span>
      )}
    </div>
  );
}

// ─── NOTIFICATION BANNER ──────────────────────────────────────────────────────
function AlertIcons({ tasks, setActiveFolder, setBannerFilter }) {
  const [open, setOpen] = useState(null); // "overdue" | "followup" | null
  const now = new Date();
  const overdue = tasks.filter(t => !t.is_sent && t.status === "open" && t.due_date && new Date(t.due_date) < now);
  const needsFollowUp = tasks.filter(t =>
    t.is_sent && t.awaiting_response && t.status === "open" &&
    t.follow_up_after && new Date(t.follow_up_after) <= now
  );

  const goToFilter = (filter) => {
    setActiveFolder("all");
    setBannerFilter(filter);
    setOpen(null);
  };

  const IconButton = ({ id, count, icon, color, bg, border, title }) => {
    if (count === 0) return null;
    return (
      <div style={{ position: "relative" }}>
        <button
          title={title}
          onClick={e => { e.stopPropagation(); setOpen(o => o === id ? null : id); }}
          style={{ position: "relative", width: 32, height: 32, borderRadius: 8, background: bg, border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}
        >
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{ position: "absolute", top: -6, right: -6, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: color, color: "#0A0A0A", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #080D1A" }}>{count}</span>
        </button>
      </div>
    );
  };

  const Popover = ({ id, list, color, border, label, filter, dateField }) => {
    if (open !== id) return null;
    return (
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: "absolute", top: 40, right: 0, width: 290, background: "#0F1B2D", border: `1px solid ${border}`, borderRadius: 10, padding: "12px 14px", boxShadow: "0 12px 40px rgba(0,0,0,0.5)", zIndex: 200 }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 11, color, letterSpacing: "0.08em", fontWeight: 700 }}>{label}</span>
          <span onClick={() => goToFilter(filter)} style={{ fontSize: 11, color: "#5B6B80", cursor: "pointer" }}>View all →</span>
        </div>
        {list.slice(0, 5).map((t, i) => {
          const d = t[dateField] ? new Date(t[dateField]) : null;
          return (
            <div key={t.task_id}
              onClick={() => goToFilter(filter)}
              style={{ fontSize: 13, color: "#CBD5E1", lineHeight: 1.5, padding: i === 0 ? "5px 0" : "8px 0 5px", borderTop: i === 0 ? "none" : "0.5px solid rgba(255,255,255,0.05)", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t.title}
              {d && <div style={{ fontSize: 11, color, marginTop: 2 }}>{dateField === "due_date" ? "Overdue " : "Waiting since "}{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>}
            </div>
          );
        })}
        {list.length > 5 && <div style={{ fontSize: 11, color: "#5B6B80", marginTop: 8 }}>+{list.length - 5} more</div>}
      </div>
    );
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  if (overdue.length === 0 && needsFollowUp.length === 0) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ position: "relative" }}>
        <IconButton id="overdue" count={overdue.length} icon="⚠" color="#F87171" bg="rgba(248,113,113,0.08)" border="rgba(248,113,113,0.2)" title={`${overdue.length} overdue`} />
        <Popover id="overdue" list={overdue} color="#FCA5A5" border="rgba(248,113,113,0.2)" label={`⚠ ${overdue.length} OVERDUE`} filter="overdue" dateField="due_date" />
      </div>
      <div style={{ position: "relative" }}>
        <IconButton id="followup" count={needsFollowUp.length} icon="⏰" color="#FBBF24" bg="rgba(251,191,36,0.08)" border="rgba(251,191,36,0.2)" title={`${needsFollowUp.length} needs follow-up`} />
        <Popover id="followup" list={needsFollowUp} color="#FDE68A" border="rgba(251,191,36,0.2)" label={`⏰ ${needsFollowUp.length} FOLLOW-UP`} filter="followup" dateField="follow_up_after" />
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ tasks, active, setActive, projects, onCreateProject, onRenameProject, onDeleteProject, draggedTask, onDropTaskInProject }) {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [dropTarget, setDropTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const open = tasks.filter(t => t.status === "open");
  const now = new Date();
  const allTasksCount = tasks.filter(t =>
    t.status === "open" && (t.visibility || "normal") === "normal" && !t.needs_filing && !["candidate", "info"].includes(t.kind || "action") && (
      !t.is_sent ||
      (t.is_sent && t.has_reply) ||
      (t.is_sent && t.awaiting_response && t.follow_up_after && new Date(t.follow_up_after) <= now)
    )
  ).length;
  const fyiCount = tasks.filter(t => t.status === "open" && (t.visibility === "fyi" || t.visibility === "review" || t.needs_filing)).length;

  // Count open tasks in a project (by id, with legacy name fallback)
  const projectCount = (proj) => tasks.filter(t => {
    if (t.status !== "open") return false;
    if (Array.isArray(t.project_ids) && t.project_ids.includes(proj.id)) return true;
    if (t.project && t.project === proj.name) return true;
    return false;
  }).length;

  const senderMap = {};
  tasks.filter(t => t.status === "open").forEach(t => {
    const person = t.is_sent
      ? (t.assignee_name && t.assignee_name !== "You" ? t.assignee_name : null)
      : (t.sender_name && t.sender_name !== "You" ? t.sender_name : null);
    if (!person) return;
    const key = `sender:${person}`;
    senderMap[key] = { name: person, count: (senderMap[key]?.count || 0) + 1 };
  });
  const senders = Object.entries(senderMap);

  const Item = ({ id, label, count, icon, sub }) => (
    <div onClick={() => setActive(id)} style={{
      display: "flex", alignItems: "center", gap: 7,
      padding: sub ? "6px 8px 6px 22px" : "7px 8px",
      borderRadius: 7, cursor: "pointer",
      background: active === id ? "rgba(99,102,241,0.15)" : "transparent",
      color: active === id ? "#A5B4FC" : "#475569",
      fontSize: 13, fontWeight: active === id ? 600 : 400,
      transition: "all 0.15s", marginBottom: 1,
    }}>
      <span style={{ fontSize: sub ? 11 : 14, opacity: 0.7 }}>{icon}</span>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {count > 0 && <span style={{ fontSize: 10, background: active === id ? "rgba(99,102,241,0.25)" : "rgba(255,255,255,0.05)", color: active === id ? "#818CF8" : "#334155", borderRadius: 8, padding: "1px 6px", fontWeight: 600 }}>{count}</span>}
    </div>
  );

  const Divider = ({ label, action }) => (
    <div style={{ display: "flex", alignItems: "center", padding: "14px 8px 4px" }}>
      <span style={{ fontSize: 9, color: "#1E2D40", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", flex: 1 }}>{label}</span>
      {action}
    </div>
  );

  const commitRename = (id) => {
    if (editValue.trim()) onRenameProject(id, editValue.trim());
    setEditingId(null);
    setEditValue("");
  };

  const commitCreate = () => {
    if (newName.trim()) onCreateProject(newName.trim());
    setCreating(false);
    setNewName("");
  };

  return (
    <div style={{ width: 215, flexShrink: 0, borderRight: "1px solid rgba(255,255,255,0.04)", padding: "18px 10px", overflowY: "auto", position: "sticky", top: 57, height: "calc(100vh - 57px)" }}>
      <Item id="all"      label="All Tasks"       count={allTasksCount} icon="◈" />
      <Item id="checklist" label="Checklist"      count={0} icon="☑" />
      <Item id="activity"  label="Activity"       count={0} icon="⚡" />
      <Item id="tracker"   label="Tracker"        count={0} icon="📊" />
      <Item id="sent"     label="Sent & Tracking" count={tasks.filter(t=>t.is_sent&&t.status==="open"&&(t.kind||"action")!=="candidate"&&!t.needs_filing).length} icon="↑" />
      <Item id="fyi"      label="Review"          count={fyiCount} icon="ⓘ" />
      <Item id="complete" label="Completed"       count={tasks.filter(t=>t.status==="complete").length} icon="✓" />

      <Divider label="Projects" action={
        <button onClick={() => { setCreating(true); setNewName(""); }}
          title="New project"
          style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 0 }}>+</button>
      } />

      {(() => {
        // Archived = auto-created projects with no open tasks. They collapse into a
        // separate section so migration leftovers don't crowd the pane. A hand-made
        // project, or any project with an open task, always stays active.
        const activeProjects = projects.filter(p => p.origin !== "auto" || projectCount(p) > 0);
        const archivedProjects = projects.filter(p => p.origin === "auto" && projectCount(p) === 0);

        const ProjectRow = (p) => {
          if (editingId === p.id) {
            return (
              <div key={p.id} style={{ padding: "4px 8px 4px 22px" }}>
                <input
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => commitRename(p.id)}
                  onKeyDown={e => { if (e.key === "Enter") commitRename(p.id); if (e.key === "Escape") { setEditingId(null); setEditValue(""); } }}
                  style={{ width: "100%", background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 5, color: "#CBD5E1", fontSize: 13, padding: "4px 7px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                />
              </div>
            );
          }
          const isDrop = dropTarget === p.id && draggedTask;
          return (
            <div
              key={p.id}
              onDragOver={e => { if (draggedTask) { e.preventDefault(); setDropTarget(p.id); } }}
              onDragLeave={() => setDropTarget(t => t === p.id ? null : t)}
              onDrop={e => { e.preventDefault(); setDropTarget(null); if (draggedTask) onDropTaskInProject(draggedTask, p.id); }}
              onClick={() => setActive(`proj:${p.id}`)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "6px 8px 6px 22px", borderRadius: 7, cursor: "pointer",
                background: isDrop ? "rgba(99,102,241,0.28)" : active === `proj:${p.id}` ? "rgba(99,102,241,0.15)" : "transparent",
                border: isDrop ? "1px dashed rgba(129,140,248,0.6)" : "1px solid transparent",
                color: active === `proj:${p.id}` ? "#A5B4FC" : "#475569",
                fontSize: 13, fontWeight: active === `proj:${p.id}` ? 600 : 400, marginBottom: 1,
                transition: "background 0.15s",
              }}
            >
              <span style={{ fontSize: 11, opacity: 0.7 }}>▸</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
              <span
                onClick={e => { e.stopPropagation(); setEditingId(p.id); setEditValue(p.name); }}
                title="Rename"
                style={{ fontSize: 11, color: "#334155", opacity: 0.6, padding: "0 2px" }}>✎</span>
              <span
                onClick={e => { e.stopPropagation(); if (window.confirm(`Delete project "${p.name}"? Tasks stay, they just leave this project.`)) onDeleteProject(p.id); }}
                title="Delete"
                style={{ fontSize: 12, color: "#334155", opacity: 0.6, padding: "0 2px" }}>×</span>
              {projectCount(p) > 0 && <span style={{ fontSize: 10, background: "rgba(255,255,255,0.05)", color: "#334155", borderRadius: 8, padding: "1px 6px", fontWeight: 600 }}>{projectCount(p)}</span>}
            </div>
          );
        };

        return (
          <>
            {activeProjects.map(ProjectRow)}

            {creating && (
              <div style={{ padding: "4px 8px 4px 22px" }}>
                <input
                  autoFocus
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onBlur={commitCreate}
                  onKeyDown={e => { if (e.key === "Enter") commitCreate(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
                  placeholder="Project name"
                  style={{ width: "100%", background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 5, color: "#CBD5E1", fontSize: 13, padding: "4px 7px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                />
              </div>
            )}
            {activeProjects.length === 0 && !creating && archivedProjects.length === 0 && (
              <div style={{ fontSize: 11, color: "#1E2D40", padding: "4px 8px 4px 22px" }}>No projects yet</div>
            )}

            {archivedProjects.length > 0 && (
              <>
                <div
                  onClick={() => setShowArchived(s => !s)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 8px 4px 22px", cursor: "pointer", fontSize: 11, color: "#334155" }}
                >
                  <span style={{ fontSize: 9, transform: showArchived ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▸</span>
                  <span>Archived</span>
                  <span style={{ fontSize: 10, background: "rgba(255,255,255,0.04)", color: "#334155", borderRadius: 8, padding: "1px 6px" }}>{archivedProjects.length}</span>
                </div>
                {showArchived && archivedProjects.map(ProjectRow)}
              </>
            )}
          </>
        );
      })()}

      <Divider label="Senders" />
      {senders.length === 0 && <div style={{ fontSize: 11, color: "#1E2D40", padding: "4px 8px" }}>Syncing…</div>}
      {senders.map(([key, { name, count }]) => (
        <Item key={key} id={key} label={name} sub count={count} icon={
          <span style={{ width: 14, height: 14, borderRadius: "50%", background: "rgba(100,116,139,0.2)", border: "1px solid rgba(100,116,139,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, color: "#94A3B8" }}>
            {name[0]?.toUpperCase()}
          </span>
        } />
      ))}

      <div style={{ margin: "20px 0 0", padding: 12, background: "rgba(255,255,255,0.015)", borderRadius: 9, border: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ fontSize: 9, color: "#1E2D40", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>Priority Split</div>
        {["high","medium","low"].map(p => {
          const cnt = open.filter(t=>t.priority===p).length;
          const pct = open.length ? (cnt/open.length)*100 : 0;
          return (
            <div key={p} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: "#334155", textTransform: "capitalize" }}>{p}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: PRIORITY_META[p].color }}>{cnt}</span>
              </div>
              <div style={{ height: 3, background: "rgba(255,255,255,0.04)", borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${pct}%`, background: PRIORITY_META[p].color, borderRadius: 2, transition: "width 0.6s ease", opacity: 0.8 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ACTIVITY LOG ─────────────────────────────────────────────────────────────
function ActivityLog({ log, diagnostics }) {
  const [copied, setCopied] = useState(false);
  if (!log.length) return null;
  const copyDiag = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const text = typeof diagnostics === "function" ? diagnostics() : "";
      await navigator.clipboard.writeText(text);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    } catch(err) { console.error("copy failed", err); }
  };
  return (
    <details style={{ marginTop: 28 }}>
      <summary style={{ fontSize: 11, color: "#1E2D40", cursor: "pointer", userSelect: "none", padding: "6px 0", fontWeight: 600, letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 12 }}>
        <span>CLAUDE ACTIVITY LOG · {log.length} events</span>
        {diagnostics && <button onClick={copyDiag} style={{ fontSize: 10, color: "#475569", background: "transparent", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, padding: "1px 8px", cursor: "pointer", letterSpacing: 0 }}>{copied ? "Copied ✓" : "Copy diagnostics"}</button>}
      </summary>
      <div style={{ marginTop: 8, background: "rgba(0,0,0,0.35)", borderRadius: 8, padding: "12px 14px", fontFamily: "'DM Mono', 'Courier New', monospace", maxHeight: 200, overflowY: "auto" }}>
        {log.slice().reverse().map((l, i) => (
          <div key={i} style={{ fontSize: 11, color: l.error ? "#F87171" : l.dim ? "#1E2D40" : "#475569", marginBottom: 5, display: "flex", gap: 10 }}>
            <span style={{ color: "#0F1F35", flexShrink: 0 }}>{l.time}</span>
            <span>{l.text}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

// ─── PERSISTENCE ──────────────────────────────────────────────────────────────
// Desktop (Electron): uses localStorage. Web: uses Supabase API.
//
// Multi-account: every cache key is namespaced by the active account's user id
// so switching accounts never leaks one account's tasks, projects, or — most
// importantly — its incremental-sync cursor into the other.

const ACCOUNTS_KEY = "toodue_accounts";        // { [userId]: { id, email, name, avatar, access_token, refresh_token } }
const ACTIVE_KEY   = "toodue_active_account";  // userId of the account currently in use

function loadAccounts() {
  try { return JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || "{}") || {}; } catch { return {}; }
}
function saveAccounts(map) {
  try { localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(map)); } catch(e) { console.warn("accounts save failed", e); }
}
function upsertAccount(session) {
  if (!session?.user?.id) return;
  const map = loadAccounts();
  const u = session.user;
  map[u.id] = {
    id: u.id,
    email: u.email,
    name: u.user_metadata?.full_name || u.user_metadata?.name || null,
    avatar: u.user_metadata?.avatar_url || null,
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    last_used: Date.now(),
  };
  saveAccounts(map);
  localStorage.setItem(ACTIVE_KEY, u.id);
}
function removeAccount(userId) {
  const map = loadAccounts();
  delete map[userId];
  saveAccounts(map);
  if (localStorage.getItem(ACTIVE_KEY) === userId) localStorage.removeItem(ACTIVE_KEY);
}
function getActiveUserId() {
  const explicit = localStorage.getItem(ACTIVE_KEY);
  if (explicit) return explicit;
  // Fall back to whatever session Supabase itself has persisted, so the very
  // first load after this feature ships still namespaces correctly.
  try {
    const ref = (SUPABASE_URL || "").replace(/^https?:\/\//, "").split(".")[0];
    const raw = ref && localStorage.getItem(`sb-${ref}-auth-token`);
    const id = raw ? JSON.parse(raw)?.user?.id : null;
    if (id) { localStorage.setItem(ACTIVE_KEY, id); return id; }
  } catch { /* ignore */ }
  return "anon";
}
// Namespace a cache key by the active account.
function nsKey(key) {
  return `${getActiveUserId()}:${key}`;
}

function loadStored(key, fallback) {
  try {
    const raw = localStorage.getItem(nsKey(key));
    if (raw === null) return fallback;
    const val = JSON.parse(raw);
    if (fallback instanceof Set) return new Set(val);
    return val;
  } catch { return fallback; }
}
function saveStored(key, value) {
  try { localStorage.setItem(nsKey(key), JSON.stringify(value instanceof Set ? [...value] : value)); }
  catch(e) { console.warn("localStorage save failed", e); }
}

// ─── BETA DISCLAIMER (shown on first login) ──────────────────────────────────
function BetaDisclaimer({ onAccept }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2000, padding:20 }}>
      <div style={{ background:"#0F1B2D", border:"1px solid #1E3A5F", borderRadius:14, padding:"32px 36px", maxWidth:560, boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>
        <h2 style={{ margin:"0 0 4px", color:"#E2E8F0", fontSize:20, fontFamily:"'Lora', serif", fontWeight:700 }}>Welcome to Toodue Beta</h2>
        <p style={{ margin:"0 0 18px", color:"#64748B", fontSize:13 }}>Please read before continuing.</p>

        <div style={{ background:"rgba(251,191,36,0.05)", border:"1px solid rgba(251,191,36,0.2)", borderRadius:8, padding:"14px 16px", marginBottom:18 }}>
          <div style={{ fontSize:11, color:"#FBBF24", fontWeight:700, letterSpacing:"0.08em", marginBottom:6 }}>⚠ BETA SOFTWARE</div>
          <p style={{ margin:0, fontSize:13, color:"#CBD5E1", lineHeight:1.6 }}>
            Toodue is in active development. Features may change, tasks may be incorrectly extracted, and data loss is possible (though rare). Do not rely on Toodue as your only source of truth for important tasks.
          </p>
        </div>

        <div style={{ fontSize:13, color:"#CBD5E1", lineHeight:1.7, marginBottom:18 }}>
          <p style={{ margin:"0 0 12px" }}><strong style={{ color:"#E2E8F0" }}>What Toodue does:</strong> Reads your Gmail (read-only) and uses Anthropic's Claude AI to extract tasks. Your email content is sent to Claude for processing.</p>
          <p style={{ margin:"0 0 12px" }}><strong style={{ color:"#E2E8F0" }}>What we store:</strong> Extracted tasks, Gmail message IDs, short context snippets, and encrypted OAuth tokens. We do not store full email bodies.</p>
          <p style={{ margin:"0 0 12px" }}><strong style={{ color:"#E2E8F0" }}>Your rights:</strong> You can disconnect Gmail or delete your account at any time from Settings. Deletion is immediate and irreversible.</p>
          <p style={{ margin:0 }}>
            By continuing you agree to our{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" style={{ color:"#818CF8" }}>Privacy Policy</a> and{" "}
            <a href="/terms" target="_blank" rel="noreferrer" style={{ color:"#818CF8" }}>Terms of Service</a>.
          </p>
        </div>

        <button onClick={onAccept}
          style={{ width:"100%", background:"#6366F1", border:"none", borderRadius:8, color:"#fff", padding:"12px", fontSize:14, fontWeight:600, cursor:"pointer" }}>
          I understand — let's go
        </button>
      </div>
    </div>
  );
}

// ─── ASK CLAUDE — natural-language Q&A over your tasks ──────────────────────
function AskClaude({ tasks, projects = [], authToken, userEmail, gmailEmail, onClose, onTaskClick, onApplyActions }) {
  const projectsRef = { current: projects };
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState([]); // [{ q, a, sources, error }]
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  // Build a compact task summary for Claude (open tasks + recent completed)
  const buildTaskContext = () => {
    const now = Date.now();
    const open = tasks.filter(t => t.status === "open").map(t => ({
      id: t.task_id,
      title: t.title,
      assignee: t.assignee_name,
      sender: t.sender_name,
      is_sent_by_user: t.is_sent,
      action_owner: t.action_owner,
      next_action: t.next_action,
      thread_summary: t.thread_summary,
      kind: t.kind || "action",
      vendor_stage: t.vendor_stage || null,
      projects: (t.project_ids || []).map(id => (projectsRef.current.find(p => p.id === id) || {}).name).filter(Boolean),
      needs_filing: !!t.needs_filing,
      has_reply: t.has_reply,
      awaiting_response: t.awaiting_response,
      priority: t.priority,
      bucket: t.priority_bucket,
      due_date: t.due_date,
      project: t.project,
      source: t.source_label,
      visibility: t.visibility,
      context: (t.context || "").slice(0, 280),
      last_activity: Array.isArray(t.history) && t.history.length
        ? t.history[t.history.length - 1]
        : null,
      reply_snippet: t.reply_snippet ? t.reply_snippet.slice(0, 140) : null,
    }));
    const recentlyCompleted = tasks
      .filter(t => t.status === "complete" && t.completed_at)
      .filter(t => {
        const d = new Date(t.completed_at);
        return now - d.getTime() < 30 * 24 * 3600 * 1000;
      })
      .slice(0, 30)
      .map(t => ({
        id: t.task_id,
        title: t.title,
        assignee: t.assignee_name,
        completed_at: t.completed_at,
        is_sent_by_user: t.is_sent,
      }));
    return { open, recentlyCompleted };
  };

  const ask = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    const { open, recentlyCompleted } = buildTaskContext();
    const projectList = projectsRef.current.map(p => ({ id: p.id, name: p.name, status: p.status || null }));
    const systemPrompt = `You are an assistant inside Toodue, a task tracker that extracts action items from a user's email. The user may ASK about their tasks or TELL you to change them. You can do both.

You have access to:
- The user's name/email: ${userEmail || "unknown"}${gmailEmail ? ` (Gmail: ${gmailEmail})` : ""}
- All OPEN tasks (with current status, who owns the action, priority, history). Tasks with kind="candidate" are vendor options inside a project, each with a pipeline vendor_stage (e.g. "Call / Meeting Held" means a tour or call is booked/held). Every task lists the projects it belongs to.
- Recently COMPLETED tasks (last 30 days)
- The list of PROJECTS (id + name)

Refer to the user as "you" or by name when natural. Be concise — a few sentences usually. If the answer references specific tasks, include their task_ids in "sources" so the UI can show clickable cards.

## ACTIONS
When the user asks you to change something, return the changes as an "actions" array. The UI shows them for one-click confirmation, so be exact and never invent task_ids — use ids from the data. Available actions:
- { "type": "set_stage", "task_id", "stage" }  — vendor candidates only. Stages: ${VENDOR_STAGES.map(s => `"${s}"`).join(", ")}. "Cross off / rule out / remove" a vendor → "Declined by Us". "They can't do our date" → "Unavailable". "We've booked X" → "Booked".
- { "type": "complete", "task_id" } / { "type": "reopen", "task_id" }  — for regular tasks (not candidates)
- { "type": "set_due", "task_id", "due_date": "YYYY-MM-DD" }
- { "type": "set_priority", "task_id", "priority": "high" | "medium" | "low" }
- { "type": "set_bucket", "task_id", "bucket": "today" | "tomorrow" | "later" }
- { "type": "set_next_action", "task_id", "next_action": "...", "owner": "you" | "them" }
- { "type": "rename", "task_id", "title": "..." }
- { "type": "move_to_project", "task_id", "project_id" }
- { "type": "dismiss", "task_id" }  — hide an FYI/review item that needs nothing
- { "type": "add_note", "task_id", "note": "..." }  — logs a dated note on the task
- { "type": "create_task", "title", "project_id" (or null), "due_date" (or null), "priority", "next_action" (or null), "kind": "action" | "candidate", "stage" (candidates only) }
Match names loosely ("Parknasilla" matches "Parknasilla Resort & Spa"). If a request is ambiguous between two tasks, ask rather than guess (return no actions). In "answer", say what you're about to change in one sentence — the UI applies it only after the user confirms. Today is ${new Date().toISOString().slice(0, 10)}.

If the question can't be answered from the data, say so honestly. Don't make up tasks that don't exist.

Return ONLY valid JSON:
{
  "answer": "1-3 sentence natural language answer",
  "sources": ["task_id_1", "task_id_2"],
  "actions": [ ... ] or []
}`;
    // Keep the last few exchanges so follow-ups like "and cross off Adare too" work
    const prior = history.slice(-4).flatMap(h => [
      { role: "user", content: h.q },
      { role: "assistant", content: JSON.stringify({ answer: h.a || h.error || "", sources: h.sources || [], actions: h.actions || [] }) },
    ]);
    try {
      const text = await callClaude({
        model: "claude-sonnet-4-6",
        max_tokens: 1500,
        system: systemPrompt,
        messages: [
          ...prior,
          { role: "user", content: `${q}\n\nProjects:\n${JSON.stringify(projectList)}\n\nOpen tasks (${open.length}):\n${JSON.stringify(open, null, 1)}\n\nRecently completed (${recentlyCompleted.length}):\n${JSON.stringify(recentlyCompleted, null, 1)}` },
        ],
      }, authToken, "ask");
      const m = text.match(/\{[\s\S]*\}/);
      let parsed = null;
      if (m) { try { parsed = JSON.parse(m[0]); } catch { parsed = parseLooseJson(m[0]); } }
      const answer = parsed?.answer || text || "I couldn't generate a response.";
      const sources = (parsed?.sources || []).filter(id => tasks.some(t => t.task_id === id));
      // Validate actions against real data before offering them
      const actions = (Array.isArray(parsed?.actions) ? parsed.actions : []).filter(a => {
        if (!a || !a.type) return false;
        if (a.type === "create_task") return !!a.title;
        const t = tasks.find(x => x.task_id === a.task_id);
        if (!t) return false;
        if (a.type === "set_stage") return (t.kind || "action") === "candidate" && VENDOR_STAGES.includes(a.stage);
        if (a.type === "move_to_project") return projectsRef.current.some(p => p.id === a.project_id);
        return true;
      });
      setHistory(h => [...h, { q, a: answer, sources, actions, applied: false, error: null }]);
    } catch(e) {
      setHistory(h => [...h, { q, a: null, sources: [], actions: [], error: e.message }]);
    }
    setLoading(false);
    setQuery("");
  };

  // Human-readable line for each proposed change
  const describeAction = (a) => {
    const t = tasks.find(x => x.task_id === a.task_id);
    const name = t ? t.title : a.title || "task";
    const proj = (id) => projectsRef.current.find(p => p.id === id)?.name || "project";
    switch (a.type) {
      case "set_stage":       return `${name} → ${a.stage}`;
      case "complete":        return `Mark complete: ${name}`;
      case "reopen":          return `Reopen: ${name}`;
      case "set_due":         return `${name} · due ${a.due_date}`;
      case "set_priority":    return `${name} · ${a.priority} priority`;
      case "set_bucket":      return `${name} → ${a.bucket}`;
      case "set_next_action": return `${name} · next: ${a.next_action}${a.owner ? ` (→ ${a.owner === "you" ? "You" : "them"})` : ""}`;
      case "rename":          return `Rename "${name}" → "${a.title}"`;
      case "move_to_project": return `Move ${name} → ${proj(a.project_id)}`;
      case "dismiss":         return `Dismiss: ${name}`;
      case "add_note":        return `Note on ${name}: ${a.note}`;
      case "create_task":     return `Create ${a.kind === "candidate" ? "vendor option" : "task"}: ${a.title}${a.project_id ? ` in ${proj(a.project_id)}` : ""}${a.due_date ? ` · due ${a.due_date}` : ""}`;
      default:                return `${a.type}: ${name}`;
    }
  };

  const applyActions = (i) => {
    const h = history[i];
    if (!h || h.applied || !h.actions?.length) return;
    onApplyActions && onApplyActions(h.actions);
    setHistory(prev => prev.map((x, idx) => idx === i ? { ...x, applied: true } : x));
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask(query);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const suggestions = [
    "What's due this week?",
    "Show me all overdue tasks",
    "Cross Parknasilla off the venue list",
    "Move the florist deposit to Friday and make it high priority",
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(8,13,26,0.85)", zIndex: 1500, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 80, paddingLeft: 20, paddingRight: 20 }} onClick={onClose}>
      <div style={{ background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.6)", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        {/* Input row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <span style={{ fontSize: 18 }}>✨</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask about your tasks, or tell me what to change…"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#E2E8F0", fontSize: 15, fontFamily: "inherit" }}
          />
          {loading && <span style={{ fontSize: 11, color: "#818CF8" }}>thinking…</span>}
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.04)", border: "none", borderRadius: 5, color: "#64748B", padding: "3px 8px", fontSize: 11, cursor: "pointer" }}>esc</button>
        </div>

        {/* Conversation / suggestions */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {history.length === 0 && !loading && (
            <div>
              <div style={{ fontSize: 11, color: "#475569", fontWeight: 600, letterSpacing: "0.08em", marginBottom: 10, textTransform: "uppercase" }}>Try asking</div>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => ask(s)}
                  style={{ display: "block", width: "100%", textAlign: "left", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: "10px 14px", color: "#CBD5E1", fontSize: 13, marginBottom: 6, cursor: "pointer", transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.06)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {history.map((h, i) => (
            <div key={i} style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>You asked</div>
              <div style={{ fontSize: 14, color: "#E2E8F0", marginBottom: 12, fontWeight: 500 }}>{h.q}</div>
              <div style={{ fontSize: 12, color: "#818CF8", fontWeight: 600, marginBottom: 6, letterSpacing: "0.04em" }}>✨ Toodue</div>
              {h.error ? (
                <div style={{ fontSize: 13, color: "#F87171" }}>Error: {h.error}</div>
              ) : (
                <div style={{ fontSize: 14, color: "#CBD5E1", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{h.a}</div>
              )}
              {h.actions && h.actions.length > 0 && (
                <div style={{ marginTop: 12, border: `1px solid ${h.applied ? "rgba(52,211,153,0.35)" : "rgba(251,191,36,0.35)"}`, background: h.applied ? "rgba(52,211,153,0.05)" : "rgba(251,191,36,0.05)", borderRadius: 9, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: h.applied ? "#34D399" : "#FBBF24", marginBottom: 8 }}>
                    {h.applied ? `✓ Applied ${h.actions.length} change${h.actions.length !== 1 ? "s" : ""}` : `Proposed change${h.actions.length !== 1 ? "s" : ""}`}
                  </div>
                  {h.actions.map((a, j) => (
                    <div key={j} style={{ fontSize: 13, color: "#CBD5E1", padding: "3px 0", display: "flex", gap: 8 }}>
                      <span style={{ color: h.applied ? "#34D399" : "#FBBF24" }}>{h.applied ? "✓" : "•"}</span>
                      <span>{describeAction(a)}</span>
                    </div>
                  ))}
                  {!h.applied && (
                    <div style={{ display: "flex", gap: 12, marginTop: 10, alignItems: "center" }}>
                      <button onClick={() => applyActions(i)}
                        style={{ fontSize: 12, fontWeight: 700, color: "#0B1220", background: "#FBBF24", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer" }}>
                        Apply {h.actions.length} change{h.actions.length !== 1 ? "s" : ""}
                      </button>
                      <button onClick={() => setHistory(prev => prev.map((x, idx) => idx === i ? { ...x, actions: [] } : x))}
                        style={{ fontSize: 12, color: "#64748B", background: "transparent", border: "none", cursor: "pointer" }}>Don't apply</button>
                    </div>
                  )}
                </div>
              )}
              {h.sources && h.sources.length > 0 && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {h.sources.map(taskId => {
                    const t = tasks.find(x => x.task_id === taskId);
                    if (!t) return null;
                    return (
                      <button
                        key={taskId}
                        onClick={() => { onTaskClick && onTaskClick(t); onClose(); }}
                        style={{ textAlign: "left", background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: 7, padding: "8px 12px", color: "#CBD5E1", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
                      >
                        <span style={{ fontSize: 14 }}>{t.is_sent ? "↑" : "◈"}</span>
                        <span style={{ flex: 1, fontWeight: 500 }}>{t.title}</span>
                        <span style={{ fontSize: 10, color: "#64748B" }}>{t.is_sent ? `to ${t.assignee_name}` : `from ${t.sender_name}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748B", fontSize: 13 }}>
              <span>✨</span>
              <span>Toodue is thinking…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ACCOUNT SWITCHER (Instagram-style, off the header avatar) ──────────────
function AccountAvatar({ account, size = 30 }) {
  const initials = (account?.name || account?.email || "?")
    .split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
  if (account?.avatar) {
    return <img src={account.avatar} alt="" referrerPolicy="no-referrer"
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", display: "block" }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "#3C3489", color: "#CECBF6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, fontWeight: 700 }}>
      {initials}
    </div>
  );
}

function AccountSwitcher({ accounts, activeUserId, onSwitch, onAdd, onSignOut, onOpenSettings }) {
  const [open, setOpen] = useState(false);
  const active = accounts.find(a => a.id === activeUserId) || null;

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  const row = { display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", cursor: "pointer", borderRadius: 8, fontSize: 13, color: "#CBD5E1" };

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        title={active?.email || "Account"}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "block" }}
      >
        <AccountAvatar account={active} size={30} />
      </button>

      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{ position: "absolute", top: 40, right: 0, width: 300, background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 12, padding: 6, boxShadow: "0 16px 48px rgba(0,0,0,0.55)", zIndex: 300 }}
        >
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 700, padding: "8px 12px 4px", textTransform: "uppercase" }}>Accounts</div>

          {accounts.map(a => {
            const isActive = a.id === activeUserId;
            return (
              <div key={a.id}
                onClick={() => { if (!isActive) onSwitch(a.id); setOpen(false); }}
                style={{ ...row, background: isActive ? "rgba(99,102,241,0.1)" : "transparent" }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                <AccountAvatar account={a} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {a.name && <div style={{ fontSize: 13, color: "#E2E8F0", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>}
                  <div style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.email}</div>
                </div>
                {isActive && <span style={{ color: "#818CF8", fontSize: 14 }}>✓</span>}
              </div>
            );
          })}

          <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "6px 4px" }} />

          <div onClick={() => { setOpen(false); onAdd(); }} style={row}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", border: "1.5px dashed #3A4A61", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", fontSize: 16 }}>+</div>
            <span>Add account</span>
          </div>

          <div onClick={() => { setOpen(false); onOpenSettings(); }} style={row}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ width: 30, textAlign: "center", color: "#64748B", fontSize: 15 }}>⚙</div>
            <span>Settings</span>
          </div>

          <div onClick={() => { setOpen(false); onSignOut(); }} style={{ ...row, color: "#94A3B8" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ width: 30, textAlign: "center", color: "#64748B", fontSize: 15 }}>↪</div>
            <span>Sign out of {active?.email ? active.email.split("@")[0] : "this account"}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── COMPLETED-IN-PROJECT (collapsed history that stays with the project) ────
function CompletedInProject({ items, onComplete, projects, onSetStage }) {
  const [open, setOpen] = useState(false);
  const sorted = [...items].sort((a, b) => (b.completed_at || "") < (a.completed_at || "") ? -1 : 1);
  return (
    <div style={{ marginTop: 26 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 4px", marginBottom: 4 }}>
        <span style={{ fontSize: 9, color: "#3A4A61", transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▸</span>
        <span style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase" }}>Completed</span>
        <span style={{ fontSize: 10, color: "#34D399", background: "rgba(52,211,153,0.1)", borderRadius: 8, padding: "1px 7px", fontWeight: 600 }}>{items.length}</span>
      </div>
      {open
        ? sorted.map(t => <TaskCard key={t.task_id} task={t} onComplete={onComplete} isNew={false} projects={projects} onSetStage={onSetStage} />)
        : (
          <div style={{ paddingLeft: 21 }}>
            {sorted.slice(0, 4).map(t => (
              <div key={t.task_id} style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: 12, color: "#475569", padding: "3px 0" }}>
                <span style={{ color: "#34D399" }}>✓</span>
                <span style={{ flex: 1, textDecoration: "line-through", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                {t.completed_at && <span style={{ color: "#3A4A61", flexShrink: 0 }}>{new Date(t.completed_at + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
              </div>
            ))}
            {sorted.length > 4 && <div style={{ fontSize: 11, color: "#3A4A61", paddingTop: 4 }}>+{sorted.length - 4} more — click to expand</div>}
          </div>
        )}
    </div>
  );
}

// ─── PROJECT HEADER (status, brief, candidate pipeline) ───────────────────────
function ProjectHeader({ project, tasks, candidates, actionable, done = [], onStatus, onTargetDate, onOpenPlanner, renderCandidate }) {
  const [editDate, setEditDate] = useState(false);
  const [dateVal, setDateVal] = useState(project.target_date || "");
  const brief = project.agent_brief || null;
  const color = PROJECT_STATUS_COLOR[project.status] || "#64748B";

  // Last communication: newest history entry across every task in this project
  const lastComm = (() => {
    let best = null;
    [...candidates, ...actionable, ...done].forEach(t => {
      (Array.isArray(t.history) ? t.history : []).forEach(h => {
        if (h.type === "created" && h.label === "Imported from tracker") return;
        if (!best || new Date(h.ts) > new Date(best.ts)) best = { ...h, task: t };
      });
    });
    return best;
  })();

  // Next action: the nearest-due open action task (or the brief's suggestion if present)
  const nextTask = [...actionable].filter(t => t.status === "open").sort((a, b) => (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1)[0] || null;
  const nextAction = brief?.next_action || nextTask?.next_action || nextTask?.title || null;

  const daysTo = project.target_date ? Math.ceil((new Date(project.target_date) - new Date()) / 86400000) : null;
  const openCount = actionable.length;
  const doneCount = done.length;
  // Progress measures the CHECKLIST only. Ruled-out vendors are completed
  // candidates, and counting them would inflate progress just for saying no.
  const doneActions = done.filter(t => (t.kind || "action") !== "candidate").length;
  const totalActions = openCount + doneActions;
  const pct = totalActions ? Math.round((doneActions / totalActions) * 100) : 0;

  // The checklist: what's done most recently, and what's up next by due date.
  const upNext = [...actionable].sort((a, b) => (a.due_date || "9999-99-99") < (b.due_date || "9999-99-99") ? -1 : 1).slice(0, 5);
  const justDone = [...done].sort((a, b) => (b.completed_at || "") < (a.completed_at || "") ? -1 : 1).slice(0, 3);
  const fmt = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
  const overdueOn = (d) => d && new Date(d) < new Date();

  // Stage board: group open candidates by stage, in pipeline order
  const byStage = {};
  candidates.forEach(t => { const st = t.vendor_stage || "Identified"; (byStage[st] = byStage[st] || []).push(t); });
  const stagesPresent = VENDOR_STAGES.filter(st => byStage[st]?.length);

  const lbl = { fontSize: 10, color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 };

  return (
    <div style={{ marginBottom: 22 }}>
      {/* status strip */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 14, fontSize: 12 }}>
        <select value={project.status || "not_started"} onChange={e => onStatus(e.target.value)}
          style={{ fontSize: 12, fontWeight: 600, background: `${color}18`, border: `1px solid ${color}55`, borderRadius: 6, color, padding: "4px 8px", fontFamily: "inherit" }}>
          {PROJECT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {editDate ? (
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)} style={{ fontSize: 12, background: "#0F1B2D", border: "1px solid #1E3A5F", borderRadius: 5, color: "#CBD5E1", padding: "3px 7px", fontFamily: "inherit" }} />
            <button onClick={() => { onTargetDate(dateVal || null); setEditDate(false); }} style={{ fontSize: 12, color: "#34D399", background: "none", border: "none", cursor: "pointer" }}>Save</button>
            <button onClick={() => setEditDate(false)} style={{ fontSize: 12, color: "#64748B", background: "none", border: "none", cursor: "pointer" }}>Cancel</button>
          </span>
        ) : (
          <span onClick={() => setEditDate(true)} style={{ color: daysTo !== null && daysTo < 0 ? "#F87171" : daysTo !== null && daysTo < 30 ? "#FBBF24" : "#94A3B8", cursor: "pointer", borderBottom: "1px dotted #3A4A61" }}>
            {project.target_date ? `Target ${new Date(project.target_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${daysTo !== null ? ` · ${daysTo < 0 ? `${-daysTo}d overdue` : `${daysTo}d`}` : ""}` : "Set target date"}
          </span>
        )}
        <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#5B6B80" }}>
          <span style={{ width: 70, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
            <span style={{ display: "block", width: `${pct}%`, height: "100%", background: pct === 100 ? "#34D399" : "#6366F1", transition: "width 0.3s" }} />
          </span>
          {doneActions}/{totalActions} done{candidates.length ? ` · ${candidates.length} candidates` : ""}
        </span>
        <span style={{ flex: 1 }} />
        <button onClick={onOpenPlanner} style={{ fontSize: 12, color: "#A5B4FC", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}>✨ Planner brief</button>
      </div>

      {/* checklist: what's done, what's next */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
        <div style={{ background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.15)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={lbl}>Next action</div>
          <div style={{ fontSize: 14, color: "#E8EDF5", lineHeight: 1.5 }}>{nextAction || "Nothing outstanding"}</div>
          {nextTask?.due_date && <div style={{ fontSize: 11, color: overdueOn(nextTask.due_date) ? "#F87171" : "#5B6B80", marginTop: 4 }}>Due {fmt(nextTask.due_date)}</div>}

          {(justDone.length > 0 || upNext.length > 1) && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
              {justDone.map(t => (
                <div key={t.task_id} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12, color: "#475569", padding: "2px 0" }}>
                  <span style={{ color: "#34D399" }}>✓</span>
                  <span style={{ flex: 1, textDecoration: "line-through", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                  {t.completed_at && <span style={{ color: "#3A4A61", flexShrink: 0 }}>{fmt(t.completed_at)}</span>}
                </div>
              ))}
              {upNext.slice(1).map(t => (
                <div key={t.task_id} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12, color: "#94A3B8", padding: "2px 0" }}>
                  <span style={{ color: "#3A4A61" }}>○</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                  {t.due_date && <span style={{ color: overdueOn(t.due_date) ? "#F87171" : "#3A4A61", flexShrink: 0 }}>{fmt(t.due_date)}</span>}
                </div>
              ))}
              {openCount > upNext.length && <div style={{ fontSize: 11, color: "#3A4A61", paddingTop: 4 }}>+{openCount - upNext.length} more open</div>}
            </div>
          )}
        </div>
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={lbl}>Last communication</div>
          {lastComm ? (
            <>
              <div style={{ fontSize: 13, color: "#CBD5E1", lineHeight: 1.5 }}><span style={{ color: lastComm.from === "You" ? "#C084FC" : "#94A3B8", fontWeight: 600 }}>{lastComm.from}</span> · {lastComm.label || lastComm.message}</div>
              {lastComm.label && lastComm.message && lastComm.message !== lastComm.label && (
                <div style={{ fontSize: 12, color: "#7A8CA3", marginTop: 3, lineHeight: 1.45 }}>{String(lastComm.message).slice(0, 130)}</div>
              )}
              <div style={{ fontSize: 11, color: "#5B6B80", marginTop: 4 }}>{new Date(lastComm.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {lastComm.task.title}</div>
            </>
          ) : <div style={{ fontSize: 13, color: "#5B6B80" }}>No email activity yet</div>}
        </div>
      </div>

      {(brief?.summary || brief?.status_read) && (
        <div style={{ fontSize: 13, color: "#94A3B8", lineHeight: 1.65, marginBottom: 18, paddingLeft: 12, borderLeft: "2px solid rgba(99,102,241,0.3)" }}>
          {brief.summary || brief.status_read}
          {brief.risk && <div style={{ color: "#FBBF24", marginTop: 6 }}>⚠ {brief.risk}</div>}
          {brief.recommendations?.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {brief.recommendations.map((r, i) => <li key={i} style={{ marginBottom: 3 }}>{r}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* candidate pipeline */}
      {candidates.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ ...lbl, marginBottom: 10 }}>Candidates</div>
          {stagesPresent.map(st => (
            <div key={st} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: STAGE_COLOR[st] }} />
                <span style={{ fontSize: 12, color: STAGE_COLOR[st], fontWeight: 600 }}>{st}</span>
                <span style={{ fontSize: 11, color: "#3A4A61" }}>{byStage[st].length}</span>
              </div>
              {byStage[st].map(renderCandidate)}
            </div>
          ))}
        </div>
      )}
      {actionable.length > 0 && <div style={{ ...lbl, marginTop: 18 }}>Tasks</div>}
    </div>
  );
}

// Parse JSON that may have been cut off mid-object by a token limit. Tries a
// clean parse first, then repairs by closing unterminated strings/braces and
// dropping the final incomplete entry.
function parseLooseJson(text) {
  const start = text.indexOf("{");
  if (start === -1) return null;
  const body = text.slice(start);
  try { return JSON.parse(body.slice(0, body.lastIndexOf("}") + 1)); } catch { /* fall through */ }
  // Repair a response cut off mid-object: walk forward remembering where a
  // value last closed at each depth, then rebuild from the deepest complete one.
  let depth = 0, inStr = false, esc = false;
  const lastClose = {};
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (esc) { esc = false; continue; }
    if (c === "\\") { esc = true; continue; }
    if (c === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (c === "{" || c === "[") depth++;
    else if (c === "}" || c === "]") { lastClose[depth] = i; depth--; }
  }
  const depths = Object.keys(lastClose).map(Number);
  for (let d = depths.length ? Math.max(...depths) : 0; d >= 1; d--) {
    if (lastClose[d] === undefined) continue;
    try { return JSON.parse(body.slice(0, lastClose[d] + 1) + "}".repeat(d - 1)); } catch { /* keep trying */ }
  }
  return null;
}

// ─── PLANNER PANEL (agent overlay) ───────────────────────────────────────────
function PlannerPanel({ tasks, projects, authToken, onClose, onApplyBriefs, focusProjectId }) {
  const [brief, setBrief] = useState(null);
  const [at, setAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useWeb, setUseWeb] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/brief", {}, authToken);
        const data = await res.json();
        if (data.brief) { setBrief(data.brief); setAt(data.at); }
        if (data.settings?.useWeb) setUseWeb(true);
        if (!data.brief) generate(false);
      } catch(e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line

  const generate = async (withWeb) => {
    setLoading(true); setError(null);
    const today = new Date().toISOString().split("T")[0];
    const projSummary = projects.map(p => {
      const mine = tasks.filter(t => (t.project_ids || []).includes(p.id));
      const cands = mine.filter(t => (t.kind || "action") === "candidate");
      const acts = mine.filter(t => (t.kind || "action") !== "candidate");
      const lastHist = mine.flatMap(t => (t.history || []).filter(h => h.label !== "Imported from tracker").map(h => ({ ...h, task: t.title }))).sort((a, b) => new Date(b.ts) - new Date(a.ts))[0] || null;
      return {
        id: p.id, name: p.name, status: p.status, target_date: p.target_date, phase: p.phase,
        open_tasks: acts.filter(t => t.status === "open").sort((a, b) => (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1).slice(0, 8).map(t => ({ title: t.title.slice(0, 90), due: t.due_date, priority: t.priority })),
        open_task_count: acts.filter(t => t.status === "open").length,
        done_count: acts.filter(t => t.status === "complete").length,
        candidates: cands.filter(t => t.status === "open").slice(0, 12).map(t => ({ name: t.title, stage: t.vendor_stage, due: t.due_date })),
        last_communication: lastHist ? { ts: lastHist.ts, from: lastHist.from, what: lastHist.label || lastHist.message, task: lastHist.task } : null,
      };
    });
    const wedding = projects.length ? "2027-09-04" : null;
    const system = `You are a wedding-planning agent inside Toodue. Today is ${today}.${wedding ? ` The wedding is Saturday ${wedding} in Kenmare, Co. Kerry, Ireland (~120+ guests, US couple).` : ""}
You see every project with its status, target date, open tasks, vendor candidates (with pipeline stage), and the last email communication logged.

Produce a concise operational brief. Be specific and name vendors/tasks. Prioritise by real deadline pressure and by what unblocks other things (venue → photographer → band → planner constrain everything else). Use general knowledge of typical lead times for Irish castle/estate weddings.${withWeb ? " You may use web search to check a vendor's current availability or a lead-time fact when it would change a recommendation; cite what you found in the text." : " Do not claim to have checked anything online."}

Be terse. Every string below has a hard length limit — respect it exactly, or the response will be truncated and discarded.

Return ONLY JSON:
{
  "headline": "<=25 words: the single most important thing right now",
  "urgent": ["3-5 imperatives, <=18 words each, most urgent first"],
  "this_week": ["2-4 items, <=12 words each"],
  "projects": {
    "<project_id>": {
      "next_action": "imperative, <=12 words",
      "status_read": "<=18 words",
      "risk": "<=12 words, or null"
    }
  }
}
Include an entry for EVERY project id given. Omit "risk" entirely when there is none. No prose outside the JSON.`;
    try {
      const body = {
        model: "claude-sonnet-4-6",
        max_tokens: 8192,
        system,
        messages: [{ role: "user", content: `PROJECTS (${projSummary.length}):\n${JSON.stringify(projSummary, null, 1)}` }],
      };
      if (withWeb) body.tools = [{ type: "web_search_20250305", name: "web_search", max_uses: 4 }];
      const res = await apiFetch("/api/claude", { method: "POST", body: JSON.stringify(body) }, authToken);
      const data = await res.json();
      const text = (data.content || []).filter(c => c.type === "text").map(c => c.text || "").join("");
      const parsed = parseLooseJson(text);
      if (!parsed) {
        console.error("Planner brief: unparseable response", text.slice(0, 400));
        throw new Error("The brief came back malformed. Hit Refresh to try again.");
      }
      setBrief(parsed); setAt(new Date().toISOString());
      await apiFetch("/api/brief", { method: "POST", body: JSON.stringify({ brief: parsed, settings: { useWeb: withWeb } }) }, authToken);
      onApplyBriefs && onApplyBriefs(parsed.projects || {});
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const H = ({ children }) => <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase", margin: "18px 0 8px" }}>{children}</div>;
  const focus = focusProjectId && brief?.projects?.[focusProjectId];
  const focusProj = focusProjectId && projects.find(p => p.id === focusProjectId);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(8,13,26,0.6)", zIndex: 1400 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 460, maxWidth: "95vw", background: "#0B1220", borderLeft: "1px solid #1E3A5F", boxShadow: "-20px 0 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <span style={{ fontSize: 18 }}>✨</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#E2E8F0", fontFamily: "'Lora', serif" }}>Planner brief</div>
            {at && <div style={{ fontSize: 11, color: "#5B6B80" }}>Updated {new Date(at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>}
          </div>
          <label style={{ fontSize: 11, color: "#94A3B8", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
            <input type="checkbox" checked={useWeb} onChange={e => setUseWeb(e.target.checked)} /> web research
          </label>
          <button onClick={() => generate(useWeb)} disabled={loading} style={{ fontSize: 12, color: "#fff", background: "#6366F1", border: "none", borderRadius: 6, padding: "6px 12px", cursor: loading ? "wait" : "pointer", fontWeight: 600, opacity: loading ? 0.6 : 1 }}>{loading ? "Thinking…" : "Refresh"}</button>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "6px 18px 24px", fontSize: 13, color: "#CBD5E1", lineHeight: 1.6 }}>
          {error && <div style={{ color: "#F87171", marginTop: 12 }}>Error: {error}</div>}
          {loading && !brief && <div style={{ color: "#64748B", marginTop: 12 }}>Reading every project and drafting the brief…</div>}
          {brief && (
            <>
              {focus && focusProj && (
                <div style={{ marginTop: 14, background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: "#A5B4FC", fontWeight: 700, marginBottom: 4 }}>{focusProj.name}</div>
                  <div style={{ color: "#E8EDF5", marginBottom: 6 }}>{focus.next_action}</div>
                  <div style={{ color: "#94A3B8" }}>{focus.status_read}</div>
                  {focus.risk && <div style={{ color: "#FBBF24", marginTop: 6 }}>⚠ {focus.risk}</div>}
                </div>
              )}
              <div style={{ marginTop: 16, fontSize: 15, color: "#E8EDF5", lineHeight: 1.5, fontFamily: "'Lora', serif" }}>{brief.headline}</div>
              <H>Urgent</H>
              <ol style={{ margin: 0, paddingLeft: 18 }}>{(brief.urgent || []).map((u, i) => <li key={i} style={{ marginBottom: 6 }}>{u}</li>)}</ol>
              <H>This week</H>
              <ul style={{ margin: 0, paddingLeft: 18 }}>{(brief.this_week || []).map((u, i) => <li key={i} style={{ marginBottom: 6 }}>{u}</li>)}</ul>
              <H>By project</H>
              {projects.map(p => {
                const b = brief.projects?.[p.id]; if (!b) return null;
                const c = PROJECT_STATUS_COLOR[p.status] || "#64748B";
                return (
                  <div key={p.id} style={{ padding: "10px 0", borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
                      <span style={{ fontWeight: 600, color: "#E2E8F0" }}>{p.name}</span>
                      {p.target_date && <span style={{ fontSize: 11, color: "#5B6B80" }}>· {new Date(p.target_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                    </div>
                    <div style={{ color: "#C7D2FE" }}>{b.next_action}</div>
                    <div style={{ color: "#94A3B8", fontSize: 12 }}>{b.status_read}</div>
                    {b.risk && <div style={{ color: "#FBBF24", fontSize: 12, marginTop: 3 }}>⚠ {b.risk}</div>}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CHECKLIST VIEW ──────────────────────────────────────────────────────────
// A ledger of every task, done or not, with what last happened and what comes
// next. Used for the overall Checklist folder (grouped by project) and inside a
// project (single group).
//
// Interaction model:
//   • click a title      → expands the full TaskCard under the row (notes, due date, filing…)
//   • ✎ on a row         → inline edit: title, next action, owner, due date
//   • "+ log" on a row   → add a dated activity entry by hand
//   • drag a row         → onto another row = reorder (switches that group to manual order);
//                          onto another group = move to that project;
//                          onto a sidebar project = file it there (draggedTask is shared)
const CHECKLIST_ORDER_KEY = "toodue_checklist_order"; // { [groupId]: "manual" | "urgency" }

const CL_TD = { padding: "10px 10px", verticalAlign: "top", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 13, lineHeight: 1.45 };
const CL_INPUT = { width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: 6, padding: "5px 8px", color: "#DDE4F0", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
const CL_BTN = { fontSize: 12, fontWeight: 600, color: "#A5B4FC", background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.35)", borderRadius: 6, padding: "4px 12px", cursor: "pointer" };
const CL_CANCEL = { fontSize: 12, color: "#64748B", background: "transparent", border: "none", cursor: "pointer" };

// ── Inline editor for one row ─────────────────────────────────────────────
function ChecklistRowEditor({ t, onDone, onEdit }) {
  const counterpart = (t.is_sent ? t.assignee_name : t.sender_name) || "";
  const curOwner = nextActionOwner(t) || "You";
  const [title, setTitle] = useState(t.title || "");
  const [next, setNext] = useState(t.next_action || "");
  const [owner, setOwner] = useState(curOwner);
  const [customOwner, setCustomOwner] = useState(curOwner !== "You" && curOwner !== counterpart ? curOwner : "");
  const [due, setDue] = useState(t.due_date ? String(t.due_date).slice(0, 10) : "");
  const save = () => {
    const finalOwner = owner === "__other" ? (customOwner.trim() || "You") : owner;
    const patch = { title: title.trim() || t.title, next_action: next.trim() || null, due_date: due || null };
    if (finalOwner === "You") { patch.action_owner = "you"; patch.awaiting_response = false; }
    else {
      patch.action_owner = "assignee"; patch.awaiting_response = true;
      if (t.is_sent) patch.assignee_name = finalOwner; else patch.sender_name = finalOwner;
    }
    onEdit && onEdit(t.task_id, patch, { at: new Date().toISOString(), label: "Edited", message: `You updated this task` });
    onDone();
  };
  const opts = ["You", ...(counterpart && counterpart !== "You" ? [counterpart] : []), "__other"];
  return (
    <tr style={{ background: "rgba(99,102,241,0.05)" }}>
      <td style={{ ...CL_TD, width: 28 }} />
      <td style={{ ...CL_TD, minWidth: 200 }}>
        <input autoFocus value={title} onChange={e => setTitle(e.target.value)} style={CL_INPUT} placeholder="Task title"
               onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") onDone(); }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11, color: "#64748B" }}>
          Due <input type="date" value={due} onChange={e => setDue(e.target.value)} style={{ ...CL_INPUT, width: "auto", padding: "3px 6px", fontSize: 12 }} />
        </div>
      </td>
      <td style={{ ...CL_TD, minWidth: 220, color: "#3A4A61", fontSize: 12 }}>Use “+ log” to add activity</td>
      <td style={{ ...CL_TD, minWidth: 200 }}>
        <input value={next} onChange={e => setNext(e.target.value)} style={CL_INPUT} placeholder="Next action (imperative)"
               onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") onDone(); }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#64748B" }}>Owner</span>
          <select value={opts.includes(owner) ? owner : "__other"} onChange={e => setOwner(e.target.value)}
                  style={{ ...CL_INPUT, width: "auto", padding: "3px 6px", fontSize: 12, background: "#0F1B2D" }}>
            {opts.map(o => <option key={o} value={o}>{o === "__other" ? "Someone else…" : o}</option>)}
          </select>
          {(owner === "__other" || !opts.includes(owner)) && (
            <input value={customOwner} onChange={e => setCustomOwner(e.target.value)} placeholder="Name" style={{ ...CL_INPUT, width: 110, padding: "3px 6px", fontSize: 12 }} />
          )}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button onClick={save} style={CL_BTN}>Save</button>
          <button onClick={onDone} style={CL_CANCEL}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}


// ── "+ log" activity entry ────────────────────────────────────────────────
function ChecklistLogEditor({ t, onDone, onEdit }) {
  const [text, setText] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [who, setWho] = useState("You");
  const counterpart = (t.is_sent ? t.assignee_name : t.sender_name) || "";
  const save = () => {
    if (!text.trim()) return;
    onEdit && onEdit(t.task_id, { reply_snippet: text.trim(), reply_from: who }, { at: new Date(date + "T12:00:00").toISOString(), from: who, type: "note", label: who === "You" ? "Note" : "Update", message: text.trim() });
    onDone();
  };
  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
      <input autoFocus value={text} onChange={e => setText(e.target.value)} placeholder="What happened? e.g. Venue confirmed the date by phone" style={CL_INPUT}
             onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") onDone(); }} />
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...CL_INPUT, width: "auto", padding: "3px 6px", fontSize: 12 }} />
        <select value={who} onChange={e => setWho(e.target.value)} style={{ ...CL_INPUT, width: "auto", padding: "3px 6px", fontSize: 12, background: "#0F1B2D" }}>
          <option value="You">You</option>
          {counterpart && counterpart !== "You" && <option value={counterpart}>{counterpart}</option>}
        </select>
        <button onClick={save} style={{ ...CL_BTN, padding: "3px 10px" }}>Add</button>
        <button onClick={onDone} style={CL_CANCEL}>Cancel</button>
      </div>
    </div>
  );
}


function ChecklistView({ tasks, projects = [], groupByProject = false, onComplete, onEdit, onReorder, onMoveToProject,
                         draggedTask, setDraggedTask, cardProps = {} }) {
  const [collapsedDone, setCollapsedDone] = useState({});
  const [showInfo, setShowInfo] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loggingId, setLoggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);     // row being hovered
  const [dragOverGroup, setDragOverGroup] = useState(null); // group being hovered
  const [orderMode, setOrderMode] = useState(() => loadStored(CHECKLIST_ORDER_KEY, {}));

  const setGroupOrder = (gid, mode) => {
    setOrderMode(prev => { const next = { ...prev, [gid]: mode }; saveStored(CHECKLIST_ORDER_KEY, next); return next; });
  };

  const groups = (() => {
    if (!groupByProject) return [{ id: "_single", pid: null, name: null, items: tasks }];
    const byId = {};
    const none = [];
    tasks.forEach(t => {
      const pids = Array.isArray(t.project_ids) && t.project_ids.length ? t.project_ids : (t.project ? [projects.find(p => p.name === t.project)?.id].filter(Boolean) : []);
      if (!pids.length) { none.push(t); return; }
      pids.forEach(pid => { (byId[pid] = byId[pid] || []).push(t); });
    });
    const ordered = [...projects].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .filter(p => byId[p.id]?.length).map(p => ({ id: p.id, pid: p.id, name: p.name, status: p.status, target_date: p.target_date, items: byId[p.id] }));
    if (none.length) ordered.push({ id: "_none", pid: null, name: "No project", items: none });
    return ordered;
  })();

  const th = { fontSize: 10, fontWeight: 700, color: "#3A4A61", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace", textAlign: "left", padding: "0 10px 8px", borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" };
  const td = CL_TD;
  const iconBtn = { background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 12, padding: "0 4px", lineHeight: 1 };
  const ownerChip = (owner) => ({ display: "inline-block", fontSize: 11, fontWeight: 600, borderRadius: 5, padding: "1px 7px",
    color: owner === "You" ? "#C084FC" : "#60A5FA", background: owner === "You" ? "rgba(192,132,252,0.12)" : "rgba(96,165,250,0.12)" });

  // Reorder `moving` to sit before `target` inside the group's open list
  const dropOnRow = (group, openList, moving, target) => {
    if (!onReorder || moving.task_id === target.task_id) return;
    const list = openList.filter(t => t.task_id !== moving.task_id);
    const idx = list.findIndex(t => t.task_id === target.task_id);
    if (idx === -1) return;
    list.splice(idx, 0, moving);
    const base = Date.now();
    onReorder(list.map((t, i) => ({ ...t, sort_order: base + i })));
    setGroupOrder(group.id, "manual");
  };

  // ── One task row ──────────────────────────────────────────────────────────
  // Plain render function (not a component) so rows don't remount on every state change mid-drag
  const renderRow = (t, group, openList) => {
    const done = t.status === "complete";
    const isInfo = (t.kind || "action") === "info";
    const last = lastActivity(t);
    const owner = nextActionOwner(t);
    const next = done ? null : (t.next_action || t.context || null);
    const overdue = !done && t.due_date && new Date(t.due_date) < new Date();
    const link = last?.msg_id ? gmailExactUrl(last.msg_id) : (t.source_url || null);
    const expanded = expandedId === t.task_id;
    const isDragging = draggedTask?.task_id === t.task_id;
    const isOver = dragOverId === t.task_id && draggedTask && draggedTask.task_id !== t.task_id;

    if (editingId === t.task_id) return <ChecklistRowEditor t={t} onEdit={onEdit} onDone={() => setEditingId(null)} />;

    return (
      <Fragment key={t.task_id}>
        <tr
          draggable={!isInfo}
          onDragStart={e => { e.stopPropagation(); setDraggedTask && setDraggedTask(t); }}
          onDragEnd={() => { setDraggedTask && setDraggedTask(null); setDragOverId(null); setDragOverGroup(null); }}
          onDragOver={e => { if (!draggedTask) return; e.preventDefault(); e.stopPropagation(); setDragOverId(t.task_id); }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={e => {
            if (!draggedTask) return;
            e.preventDefault(); e.stopPropagation();
            setDragOverId(null); setDragOverGroup(null);
            const sameGroup = openList.some(x => x.task_id === draggedTask.task_id);
            if (sameGroup && !done) dropOnRow(group, openList, draggedTask, t);
            else if (!sameGroup && group.id !== "_single" && onMoveToProject) onMoveToProject(draggedTask.task_id, group.pid);
            setDraggedTask && setDraggedTask(null);
          }}
          style={{ opacity: isDragging ? 0.35 : done ? 0.72 : 1, cursor: isInfo ? "default" : "grab",
                   boxShadow: isOver ? "inset 0 2px 0 #6366F1" : "none", transition: "opacity 0.15s" }}
        >
          <td style={{ ...td, width: 28, paddingRight: 0 }}>
            {isInfo ? (
              <span title="Information record — no action was needed" style={{ fontSize: 12, color: "#7DD3FC" }}>ℹ︎</span>
            ) : (
              <button onClick={() => onComplete && onComplete(t.task_id)} title={done ? "Reopen" : "Mark complete"}
                style={{ width: 16, height: 16, borderRadius: 4, cursor: "pointer", padding: 0,
                         border: `1.5px solid ${done ? "#34D399" : "rgba(255,255,255,0.18)"}`, background: done ? "rgba(52,211,153,0.2)" : "transparent",
                         color: "#34D399", fontSize: 11, lineHeight: "13px" }}>{done ? "✓" : ""}</button>
            )}
          </td>
          <td style={{ ...td, minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
              <div onClick={() => setExpandedId(expanded ? null : t.task_id)}
                   style={{ flex: 1, color: done ? "#8A9BB4" : "#DDE4F0", fontWeight: done ? 400 : 500, textDecoration: done && !isInfo ? "line-through" : "none", cursor: "pointer" }}>
                {t.title} <span style={{ fontSize: 10, color: "#3A4A61", marginLeft: 4 }}>{expanded ? "▴" : "▾"}</span>
              </div>
              {!isInfo && <button onClick={() => setEditingId(t.task_id)} title="Edit" style={iconBtn}>✎</button>}
            </div>
            <div style={{ fontSize: 11, color: "#3A4A61", marginTop: 2 }}>
              {done ? `Done ${fmtShortDate(t.completed_at)}` : t.due_date ? <span style={{ color: overdue ? "#F87171" : "#3A4A61" }}>Due {fmtShortDate(t.due_date)}</span> : "No due date"}
              {t.vendor_stage && <> · <span style={{ color: STAGE_COLOR[t.vendor_stage] || "#94A3B8", fontWeight: 600 }}>{t.vendor_stage}</span></>}
              {!done && t.priority === "high" && <span style={{ color: "#F87171" }}> · High</span>}
              {groupByProject && Array.isArray(t.project_ids) && t.project_ids.length > 1 && <span style={{ color: "#475569" }}> · in {t.project_ids.length} projects</span>}
            </div>
          </td>
          <td style={{ ...td, minWidth: 220, maxWidth: 360 }}>
            {last ? (
              <>
                <div style={{ color: "#A5B4C8" }}>
                  <span style={{ color: "#64748B", fontFamily: "monospace", fontSize: 11 }}>{fmtShortDate(last.at)}</span>
                  {" · "}<span style={{ color: last.from === "You" ? "#A5B4FC" : "#7DD3FC" }}>{last.from}</span>
                  {last.label && <> · <span style={{ color: "#8A9BB4" }}>{last.label}</span></>}
                  {link && <a href={link} target="_blank" rel="noreferrer" title="Open in Gmail" style={{ marginLeft: 6, color: "#475569", textDecoration: "none", fontSize: 11 }}>↗</a>}
                </div>
                {last.message && last.message !== last.label && <div style={{ color: "#64748B", fontSize: 12, marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{last.message}</div>}
              </>
            ) : <span style={{ color: "#3A4A61" }}>—</span>}
            {!isInfo && (loggingId === t.task_id
              ? <ChecklistLogEditor t={t} onEdit={onEdit} onDone={() => setLoggingId(null)} />
              : <button onClick={() => setLoggingId(t.task_id)} style={{ ...iconBtn, marginTop: 4, padding: 0, fontSize: 11 }}>+ log</button>)}
          </td>
          <td style={{ ...td, minWidth: 200, maxWidth: 320 }}>
            {done ? (
              <span style={{ color: "#34D399", fontSize: 12 }}>{isInfo ? "Recorded" : (t.next_action && t.action_owner === "done" ? t.next_action : "Complete")}</span>
            ) : next ? (
              <>
                <div onClick={() => setEditingId(t.task_id)} title="Click to edit" style={{ color: "#C8D2E0", cursor: "text", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{next}</div>
                {owner && <span style={{ ...ownerChip(owner), marginTop: 4 }}>→ {owner}</span>}
              </>
            ) : <button onClick={() => setEditingId(t.task_id)} style={{ ...iconBtn, padding: 0, fontSize: 12 }}>+ next action</button>}
          </td>
        </tr>
        {expanded && (
          <tr>
            <td colSpan={4} style={{ padding: "0 10px 12px 38px", borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(255,255,255,0.015)" }}>
              <div onClick={e => e.stopPropagation()}>
                <TaskCard task={t} onComplete={onComplete} isNew={false} projects={projects} {...cardProps} />
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  if (!groups.length) return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <p style={{ color: "#334155", fontSize: 14 }}>Nothing to show yet</p>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "#3A4A61" }}>Click a title to expand · ✎ to edit · drag rows to reorder or move between projects</span>
        <label style={{ fontSize: 11, color: "#475569", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={showInfo} onChange={e => setShowInfo(e.target.checked)} style={{ accentColor: "#6366F1" }} />
          Show info records
        </label>
      </div>
      {groups.map(g => {
        const allItems = g.items.filter(t => showInfo || (t.kind || "action") !== "info");
        // Vendor candidates get their own "Options" section, ordered by pipeline stage
        const vendors = allItems.filter(t => (t.kind || "action") === "candidate")
          .sort((a, b) => (a.status === "complete") - (b.status === "complete") || VENDOR_STAGES.indexOf(b.vendor_stage || "Identified") - VENDOR_STAGES.indexOf(a.vendor_stage || "Identified"));
        const items = allItems.filter(t => (t.kind || "action") !== "candidate");
        const mode = orderMode[g.id] === "manual" ? "manual" : "urgency";
        const open = items.filter(t => t.status !== "complete").sort((a, b) =>
          mode === "manual" ? (a.sort_order || 0) - (b.sort_order || 0) || urgencyScore(b) - urgencyScore(a)
                            : urgencyScore(b) - urgencyScore(a) || (a.sort_order || 0) - (b.sort_order || 0));
        const done = items.filter(t => t.status === "complete").sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
        const real = items.filter(t => (t.kind || "action") !== "info");
        const doneReal = real.filter(t => t.status === "complete").length;
        const pct = real.length ? Math.round(doneReal / real.length * 100) : 0;
        const collapsed = !!collapsedDone[g.id];
        const canReceive = groupByProject && draggedTask && !g.items.some(t => t.task_id === draggedTask.task_id);
        const isGroupOver = dragOverGroup === g.id && canReceive;
        return (
          <div key={g.id} style={{ marginBottom: 30 }}
               onDragOver={e => { if (!canReceive) return; e.preventDefault(); setDragOverGroup(g.id); }}
               onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverGroup(null); }}
               onDrop={e => {
                 if (!canReceive) return;
                 e.preventDefault();
                 setDragOverGroup(null); setDragOverId(null);
                 onMoveToProject && onMoveToProject(draggedTask.task_id, g.pid);
                 setDraggedTask && setDraggedTask(null);
               }}>
            {g.name && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 10px" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#DDE4F0", fontFamily: "'Lora', serif" }}>{g.name}</span>
                {g.status && <span style={{ fontSize: 10, color: PROJECT_STATUS_COLOR[g.status] || "#64748B", border: `1px solid ${(PROJECT_STATUS_COLOR[g.status] || "#64748B")}55`, borderRadius: 4, padding: "0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>{(PROJECT_STATUSES.find(s => s[0] === g.status) || [])[1] || g.status}</span>}
                <span style={{ fontSize: 11, color: "#475569" }}>{doneReal} of {real.length} done</span>
                <div style={{ flex: 1, maxWidth: 160, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.06)" }}>
                  <div style={{ width: `${pct}%`, height: "100%", borderRadius: 2, background: pct === 100 ? "#34D399" : "#6366F1", transition: "width 0.3s" }} />
                </div>
                <div style={{ flex: 1 }} />
                {isGroupOver && <span style={{ fontSize: 11, color: "#A5B4FC" }}>Drop to move here</span>}
              </div>
            )}
            <div style={{ overflowX: "auto", border: `1px solid ${isGroupOver ? "rgba(99,102,241,0.6)" : "rgba(255,255,255,0.05)"}`, borderRadius: 10,
                          background: isGroupOver ? "rgba(99,102,241,0.06)" : "rgba(255,255,255,0.015)", transition: "all 0.15s" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
                <thead><tr>
                  <th style={{ ...th, paddingTop: 10 }}></th>
                  <th style={{ ...th, paddingTop: 10 }}>Task</th>
                  <th style={{ ...th, paddingTop: 10 }}>Last activity</th>
                  <th style={{ ...th, paddingTop: 10 }}>Next action</th>
                </tr></thead>
                <tbody>
                  {open.length > 0 && (
                    <tr><td colSpan={4} style={{ padding: "8px 10px 2px", fontSize: 10, fontWeight: 700, color: "#FBBF24", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "monospace" }}>
                      Outstanding · {open.length}
                      <span onClick={() => setGroupOrder(g.id, mode === "manual" ? "urgency" : "manual")} title="Toggle ordering"
                            style={{ marginLeft: 12, color: "#475569", cursor: "pointer", fontWeight: 400, letterSpacing: "0.06em" }}>
                        {mode === "manual" ? "↕ manual order · reset" : "⚡ by urgency"}
                      </span>
                    </td></tr>
                  )}
                  {open.map(t => renderRow(t, g, open))}
                  {done.length > 0 && (
                    <tr><td colSpan={4} onClick={() => setCollapsedDone(c => ({ ...c, [g.id]: !collapsed }))}
                            style={{ padding: "10px 10px 2px", fontSize: 10, fontWeight: 700, color: "#34D399", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "monospace", cursor: "pointer", userSelect: "none" }}>
                      {collapsed ? "▸" : "▾"} Completed · {done.length}
                    </td></tr>
                  )}
                  {!collapsed && done.map(t => renderRow(t, g, open))}
                  {vendors.length > 0 && (
                    <tr><td colSpan={4} style={{ padding: "12px 10px 2px", fontSize: 10, fontWeight: 700, color: "#A78BFA", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "monospace" }}>
                      Vendor options · {vendors.length}
                      <span style={{ marginLeft: 10, fontWeight: 400, letterSpacing: "0.04em", color: "#475569", textTransform: "none" }}>
                        {(() => { const c = {}; vendors.forEach(v => { const st = v.vendor_stage || "Identified"; c[st] = (c[st] || 0) + 1; }); return VENDOR_STAGES.filter(st => c[st]).map(st => `${c[st]} ${st.toLowerCase()}`).join(" · "); })()}
                      </span>
                    </td></tr>
                  )}
                  {vendors.map(t => renderRow(t, g, open))}
                  {open.length === 0 && done.length === 0 && vendors.length === 0 && (
                    <tr><td colSpan={4} style={{ ...td, color: "#3A4A61", textAlign: "center" }}>{isGroupOver ? "Drop to move here" : "Nothing here"}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── ACTIVITY PANEL ──────────────────────────────────────────────────────────
// Two views of "what happened": every dated history entry across all tasks
// (newest first), and the per-email log of what the sync did with each message.
function ActivityPanel({ tasks, projects = [], msgLog = [], gmailEmail = null, onTaskClick }) {
  const [tab, setTab] = useState("activity");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(40);
  const projName = (t) => { const pid = (t.project_ids || [])[0]; return pid ? (projects.find(p => p.id === pid)?.name || t.project || null) : (t.project || null); };
  const needle = q.trim().toLowerCase();

  const events = tasks.flatMap(t => (Array.isArray(t.history) ? t.history : []).map(h => ({
    at: h.at || h.ts, from: h.from || "—", label: h.label || h.type || "", message: h.message || "", type: h.type || "", msg_id: h.msg_id || h.reply_msg_id || null, task: t,
  }))).filter(e => e.at).sort((a, b) => new Date(b.at) - new Date(a.at))
    .filter(e => !needle || `${e.from} ${e.label} ${e.message} ${e.task.title} ${projName(e.task) || ""}`.toLowerCase().includes(needle));

  const emails = (msgLog || []).filter(d => !needle || `${d.subject} ${d.from} ${d.to} ${(d.outcomes || []).map(o => o.text).join(" ")}`.toLowerCase().includes(needle));

  const fmtTime = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : `${fmtShortDate(iso)} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`; };
  const TYPE_COLOR = { created: "#818CF8", comms: "#7DD3FC", update: "#A5B4FC", completed: "#34D399", note: "#FBBF24", draft: "#94A3B8", waiting_on_user: "#C084FC", waiting_on_assignee: "#60A5FA" };
  const tabBtn = (id, label) => (
    <button key={id} onClick={() => { setTab(id); setLimit(40); }} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 6, cursor: "pointer", border: "none",
      background: tab === id ? "rgba(99,102,241,0.25)" : "transparent", color: tab === id ? "#C7D2FE" : "#64748B", fontWeight: tab === id ? 600 : 400 }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 7, padding: 2 }}>
          {tabBtn("activity", `Task activity · ${events.length}`)}
          {tabBtn("emails", `Email log · ${emails.length}`)}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter — e.g. Ballyseede, Erika, tour…"
          style={{ ...CL_INPUT, flex: 1, minWidth: 200, maxWidth: 360 }} />
      </div>

      {tab === "activity" && (
        events.length === 0 ? <div style={{ color: "#3A4A61", fontSize: 13, padding: "40px 0", textAlign: "center" }}>No activity yet</div> : (
          <div>
            {events.slice(0, limit).map((e, i) => {
              const proj = projName(e.task);
              const link = e.msg_id ? gmailExactUrl(e.msg_id, gmailEmail) : null;
              return (
                <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", alignItems: "flex-start" }}>
                  <div style={{ width: 118, flexShrink: 0, fontFamily: "monospace", fontSize: 11, color: "#64748B", paddingTop: 2 }}>{fmtTime(e.at)}</div>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: TYPE_COLOR[e.type] || "#64748B", flexShrink: 0, marginTop: 7 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "#DDE4F0" }}>
                      <span style={{ color: e.from === "You" ? "#A5B4FC" : "#7DD3FC", fontWeight: 600 }}>{e.from}</span>
                      {e.label && <> · <span style={{ color: "#8A9BB4" }}>{e.label}</span></>}
                      {link && <a href={link} target="_blank" rel="noreferrer" title="Open in Gmail" style={{ marginLeft: 8, color: "#475569", textDecoration: "none", fontSize: 11 }}>↗ Gmail</a>}
                    </div>
                    {e.message && e.message !== e.label && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{e.message}</div>}
                    <div onClick={() => onTaskClick && onTaskClick(e.task)} style={{ fontSize: 12, marginTop: 3, color: "#94A3B8", cursor: "pointer" }}>
                      → <span style={{ textDecoration: "underline dotted", textUnderlineOffset: 2 }}>{e.task.title}</span>
                      {proj && <span style={{ color: "#475569" }}> · {proj}</span>}
                      {(e.task.kind || "action") === "candidate" && <span style={{ color: STAGE_COLOR[e.task.vendor_stage] || "#64748B" }}> · {e.task.vendor_stage || "Identified"}</span>}
                      {e.task.status === "complete" && <span style={{ color: "#34D399" }}> · done</span>}
                      {(e.task.visibility === "fyi" || e.task.visibility === "review" || e.task.needs_filing) && <span style={{ color: "#FBBF24" }}> · in Review</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            {events.length > limit && <button onClick={() => setLimit(l => l + 40)} style={{ ...CL_CANCEL, marginTop: 10 }}>Show more</button>}
          </div>
        )
      )}

      {tab === "emails" && (
        emails.length === 0 ? (
          <div style={{ color: "#3A4A61", fontSize: 13, padding: "40px 0", textAlign: "center" }}>
            {msgLog.length === 0 ? "No emails logged yet — the log starts with the next sync." : "No matches"}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 11, color: "#3A4A61", marginBottom: 8 }}>What the sync did with each message it fetched. If an email you expect isn't listed, it was never fetched — check the account and hit Sync Now.</div>
            {emails.slice(0, limit).map((d, i) => {
              const link = d.gmail_id ? gmailMsgUrl(d.gmail_id, d.is_sent, gmailEmail) : null;
              const bad = (d.outcomes || []).some(o => /failed|Nothing recorded/i.test(o.text));
              return (
                <div key={d.key || i} style={{ padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
                    <div style={{ width: 118, flexShrink: 0, fontFamily: "monospace", fontSize: 11, color: "#64748B" }}>{fmtTime(d.at)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 11, color: d.is_sent ? "#A5B4FC" : "#7DD3FC", fontWeight: 600 }}>{d.is_sent ? "SENT" : "RECEIVED"}</span>
                      <span style={{ fontSize: 13, color: "#DDE4F0", marginLeft: 8 }}>{d.subject}</span>
                      {link && <a href={link} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: "#475569", textDecoration: "none", fontSize: 11 }}>↗ Gmail</a>}
                      <div style={{ fontSize: 11, color: "#475569", marginTop: 1 }}>{d.is_sent ? `to ${d.to}` : `from ${d.from}`}</div>
                    </div>
                  </div>
                  <div style={{ marginLeft: 130, marginTop: 4 }}>
                    {(d.outcomes || []).map((o, j) => {
                      const t = o.task_id ? tasks.find(x => x.task_id === o.task_id) : null;
                      return (
                        <div key={j} style={{ fontSize: 12, color: bad && /failed|Nothing recorded/i.test(o.text) ? "#F87171" : "#94A3B8", padding: "1px 0" }}>
                          • {o.text}
                          {t && <span onClick={() => onTaskClick && onTaskClick(t)} style={{ marginLeft: 6, color: "#A5B4FC", cursor: "pointer", textDecoration: "underline dotted" }}>open</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {emails.length > limit && <button onClick={() => setLimit(l => l + 40)} style={{ ...CL_CANCEL, marginTop: 10 }}>Show more</button>}
          </div>
        )
      )}
    </div>
  );
}

// ─── TRACKER PANEL ───────────────────────────────────────────────────────────
// Houses the wedding workbook. Drop an .xlsx to upload/replace it; Toodue
// writes task activity back into it after every sync (see /api/tracker/sync).
function TrackerPanel({ authToken, tracker, onTrackerChange, onSyncNow, syncing, lastResult }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(null);     // "upload" | "delete"
  const [err, setErr] = useState(null);
  const [log, setLog] = useState(null);       // null = not loaded
  const [preview, setPreview] = useState(null); // { sheet, header, rows }
  const [sheet, setSheet] = useState("Master Checklist");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);

  const loadLog = async () => {
    try { const r = await apiFetch("/api/tracker/log", {}, authToken); const d = await r.json(); setLog(d.log || []); } catch { setLog([]); }
  };
  const loadPreview = async (name) => {
    setPreview(null);
    try { const r = await apiFetch(`/api/tracker/preview?sheet=${encodeURIComponent(name)}`, {}, authToken); const d = await r.json(); if (r.ok) setPreview(d); } catch { /* ignore */ }
  };
  useEffect(() => { if (tracker) { loadLog(); loadPreview(sheet); } }, [tracker?.uploaded_at, tracker?.last_sync_at, sheet]); // eslint-disable-line

  const upload = async (file) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) { setErr("Please drop an .xlsx workbook (Excel format). .xls and .csv aren't supported."); return; }
    setErr(null); setBusy("upload");
    try {
      const data = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file); });
      const r = await apiFetch("/api/tracker/upload", { method: "POST", body: JSON.stringify({ filename: file.name, data }) }, authToken);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `Upload failed (${r.status})`);
      onTrackerChange && onTrackerChange(d.tracker);
    } catch(e) { setErr(e.message); }
    setBusy(null);
  };
  const remove = async () => {
    setBusy("delete");
    try { await apiFetch("/api/tracker", { method: "DELETE" }, authToken); onTrackerChange && onTrackerChange(null); setConfirmDelete(false); setLog(null); setPreview(null); }
    catch(e) { setErr(e.message); }
    setBusy(null);
  };
  const download = async () => {
    try {
      const r = await apiFetch("/api/tracker/download", {}, authToken);
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = tracker?.filename || "tracker.xlsx"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch(e) { setErr(e.message); }
  };

  const dropZone = (
    <div
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files && e.dataTransfer.files[0]); }}
      onClick={() => fileRef.current && fileRef.current.click()}
      style={{ border: `1.5px dashed ${drag ? "#A5B4FC" : "rgba(255,255,255,0.12)"}`, background: drag ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.02)", borderRadius: 12, padding: tracker ? "14px 18px" : "44px 18px", textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}>
      <input ref={fileRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={e => upload(e.target.files && e.target.files[0])} />
      <div style={{ fontSize: tracker ? 13 : 15, color: "#CBD5E1" }}>{busy === "upload" ? "Uploading…" : tracker ? "Drop a new version here to replace the workbook" : "Drop your Excel tracker here, or click to choose"}</div>
      <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>.xlsx · Toodue keeps every version and never edits cells it didn't update</div>
    </div>
  );

  const fmt = iso => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  const btn = (label, onClick, color = "#A5B4FC", bg = "rgba(99,102,241,0.15)", border = "rgba(99,102,241,0.35)", disabled = false) => (
    <button onClick={onClick} disabled={disabled} style={{ fontSize: 12, fontWeight: 600, color, background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: "6px 14px", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}>{label}</button>
  );

  return (
    <div>
      {err && <div style={{ color: "#F87171", fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {!tracker ? (
        <>
          {dropZone}
          <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, marginTop: 18, maxWidth: 640 }}>
            Once uploaded, Toodue updates the workbook after every sync: Master Checklist rows (matched by ID) get Status, Last Action, Date, Who We Contacted, Their Response and Next Step; Vendor Tracker rows (matched by supplier) get Stage, last contact and response; new vendors are appended. Every changed cell is tinted lavender with a note, and a <b style={{ color: "#94A3B8" }}>Toodue Log</b> sheet records each change. Cells with a newer hand-entered date are left alone.
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 280, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>📊</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#E2E8F0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tracker.filename}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{(tracker.sheets || []).length} sheets · {Math.round((tracker.size || 0) / 1024)} KB · uploaded {fmt(tracker.uploaded_at)}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 12, lineHeight: 1.7 }}>
                Last auto-update: <span style={{ color: tracker.last_sync_at ? "#34D399" : "#64748B" }}>{tracker.last_sync_at ? `${fmt(tracker.last_sync_at)} · ${tracker.last_sync_changes || 0} change${tracker.last_sync_changes === 1 ? "" : "s"}` : "not yet"}</span>
                {lastResult && <div style={{ color: lastResult.error ? "#F87171" : "#64748B" }}>{lastResult.error ? `Update failed: ${lastResult.error}` : `Just now: ${lastResult.changes} change${lastResult.changes === 1 ? "" : "s"}`}</div>}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {btn(syncing ? "Updating…" : "Update now", onSyncNow, "#0B1220", "#A5B4FC", "#A5B4FC", syncing)}
                {btn("⬇ Download", download)}
                {confirmDelete
                  ? <>{btn(busy === "delete" ? "Removing…" : "Confirm remove", remove, "#F87171", "rgba(248,113,113,0.12)", "rgba(248,113,113,0.4)", busy === "delete")}{btn("Cancel", () => setConfirmDelete(false), "#64748B", "transparent", "rgba(255,255,255,0.08)")}</>
                  : btn("Remove", () => setConfirmDelete(true), "#64748B", "transparent", "rgba(255,255,255,0.08)")}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>{dropZone}</div>
          </div>

          {/* Sheet preview */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#3A4A61", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "monospace" }}>Preview</span>
            {(tracker.sheets || []).filter(sh => sh.name !== "Lists").map(sh => (
              <button key={sh.name} onClick={() => setSheet(sh.name)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 5, cursor: "pointer", border: "1px solid rgba(255,255,255,0.07)", background: sheet === sh.name ? "rgba(99,102,241,0.2)" : "transparent", color: sheet === sh.name ? "#C7D2FE" : "#64748B" }}>{sh.name}</button>
            ))}
            <span style={{ fontSize: 11, color: "#3A4A61", marginLeft: "auto" }}>lavender = updated by Toodue · first 60 rows</span>
          </div>
          <div style={{ overflowX: "auto", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, background: "rgba(255,255,255,0.015)", maxHeight: 420, overflowY: "auto", marginBottom: 24 }}>
            {!preview ? <div style={{ padding: 20, color: "#3A4A61", fontSize: 12 }}>Loading…</div> : (
              <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
                <thead><tr>{preview.header.map((h, i) => <th key={i} style={{ position: "sticky", top: 0, background: "#0F1B2D", textAlign: "left", padding: "6px 10px", color: "#64748B", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
                <tbody>{preview.rows.map(row => (
                  <tr key={row.r}>{row.vals.map((v, i) => <td key={i} title={v} style={{ padding: "5px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", color: row.marks.includes(i) ? "#C4B5FD" : "#A5B4C8", background: row.marks.includes(i) ? "rgba(167,139,250,0.12)" : "transparent", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</td>)}</tr>
                ))}</tbody>
              </table>
            )}
          </div>

          {/* Change log */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#3A4A61", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "monospace", marginBottom: 8 }}>Toodue Log · what was auto-updated</div>
          {log === null ? <div style={{ color: "#3A4A61", fontSize: 12 }}>Loading…</div> : log.length === 0 ? <div style={{ color: "#3A4A61", fontSize: 12 }}>No changes written yet — click Update now.</div> : (
            <div>
              {log.slice(0, 60).map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 12, alignItems: "baseline" }}>
                  <span style={{ width: 118, flexShrink: 0, fontFamily: "monospace", fontSize: 11, color: "#64748B" }}>{c.at.slice(0, 16)}</span>
                  <span style={{ flex: 1, minWidth: 0, color: "#A5B4C8" }}>
                    <span style={{ color: "#8A9BB4" }}>{c.sheet} · row {c.row}</span> · <span style={{ color: "#DDE4F0" }}>{c.item}</span> · <span style={{ color: "#C4B5FD" }}>{c.field}</span>
                    {c.field !== "(new row)" && <span style={{ color: "#64748B" }}>: {c.from ? `"${c.from}" → ` : ""}"{c.to}"</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── SETTINGS MODAL ──────────────────────────────────────────────────────────
function SettingsModal({ session, authToken, onClose, onSignOut, gmailConnected, gmailEmail, onConnectGmail, onImportSeed, partner = null, onSavePartner }) {
  const [importing, setImporting] = useState(false);
  const [pName, setPName] = useState(partner?.name || "");
  const [pEmail, setPEmail] = useState(partner?.email || "");
  const [pSaved, setPSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [busy, setBusy] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handleDisconnect = async () => {
    setBusy("disconnect");
    try {
      const res = await apiFetch("/api/disconnect-gmail", { method: "POST" }, authToken);
      if (res.ok) {
        alert("Gmail disconnected. You'll need to re-authorize to sync again.");
        window.location.reload();
      } else {
        alert("Failed to disconnect Gmail. Please try again.");
      }
    } catch(e) { alert("Error: " + e.message); }
    setBusy(null);
  };

  const handleDelete = async () => {
    setBusy("delete");
    try {
      const res = await apiFetch("/api/delete-account", { method: "POST" }, authToken);
      if (res.ok) {
        alert("Your account and all data have been deleted.");
        onSignOut();
      } else {
        const err = await res.json();
        alert("Failed to delete account: " + (err.error || "unknown error"));
      }
    } catch(e) { alert("Error: " + e.message); }
    setBusy(null);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1500, padding:20 }} onClick={onClose}>
      <div style={{ background:"#0F1B2D", border:"1px solid #1E3A5F", borderRadius:14, padding:"28px 32px", width:480, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", alignItems:"center", marginBottom:22 }}>
          <h2 style={{ margin:0, color:"#E2E8F0", fontSize:18, fontFamily:"'Lora', serif", fontWeight:700 }}>Settings</h2>
          <div style={{ flex:1 }} />
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:18 }}>✕</button>
        </div>

        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#475569", fontWeight:600, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Account</div>
          <div style={{ background:"rgba(255,255,255,0.02)", borderRadius:8, padding:"12px 14px", fontSize:13, color:"#CBD5E1" }}>
            {session?.user?.email}
          </div>
        </div>

        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#475569", fontWeight:600, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Planning partner</div>
          <div style={{ background:"rgba(255,255,255,0.02)", borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:12, color:"#64748B", lineHeight:1.5, marginBottom:10 }}>
              Emails from this person count as "our side". When they write to a vendor and you're cc'd, Toodue records it as your enquiry — not a request to you.
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <input value={pName} onChange={e => { setPName(e.target.value); setPSaved(false); }} placeholder="Name (e.g. Erika)"
                     style={{ ...inputStyle, flex:1, minWidth:120 }} />
              <input value={pEmail} onChange={e => { setPEmail(e.target.value); setPSaved(false); }} placeholder="Email"
                     style={{ ...inputStyle, flex:2, minWidth:160 }} />
              <button onClick={() => { onSavePartner && onSavePartner({ name: pName, email: pEmail }); setPSaved(true); }}
                      style={{ fontSize:12, fontWeight:600, color:"#A5B4FC", background:"rgba(99,102,241,0.15)", border:"1px solid rgba(99,102,241,0.35)", borderRadius:6, padding:"6px 14px", cursor:"pointer" }}>
                {pSaved ? "Saved ✓" : "Save"}
              </button>
            </div>
          </div>
        </div>

        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#475569", fontWeight:600, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Connected Services</div>
          <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(255,255,255,0.02)", borderRadius:8, padding:"10px 14px", marginBottom:8, fontSize:13 }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background: gmailConnected === false ? "#F87171" : "#34D399" }} />
            <div style={{ flex:1, color:"#CBD5E1" }}>
              Gmail
              <div style={{ fontSize:11, color:"#64748B" }}>{gmailConnected === false ? "Not connected" : (gmailEmail || "Connected")}</div>
            </div>
            <button onClick={() => { onClose(); onConnectGmail(); }}
              style={{ fontSize:12, color:"#A5B4FC", background:"rgba(99,102,241,0.1)", border:"1px solid rgba(99,102,241,0.25)", borderRadius:6, padding:"5px 12px", cursor:"pointer", fontWeight:600 }}>
              {gmailConnected === false ? "Connect" : "Reconnect"}
            </button>
          </div>
          {gmailConnected !== false && (!confirmDisconnect ? (
            <button onClick={() => setConfirmDisconnect(true)}
              style={{ width:"100%", background:"rgba(234,67,53,0.08)", border:"1px solid rgba(234,67,53,0.2)", borderRadius:8, color:"#F87171", padding:"10px 14px", fontSize:13, cursor:"pointer", textAlign:"left" }}>
              Disconnect Gmail
            </button>
          ) : (
            <div style={{ background:"rgba(234,67,53,0.05)", border:"1px solid rgba(234,67,53,0.2)", borderRadius:8, padding:"14px" }}>
              <p style={{ margin:"0 0 10px", fontSize:13, color:"#CBD5E1" }}>Disconnect Gmail? Your tasks will remain, but syncing will stop until you reconnect.</p>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={handleDisconnect} disabled={busy==="disconnect"}
                  style={{ flex:1, background:"#EA4335", border:"none", borderRadius:6, color:"#fff", padding:"8px", fontSize:12, fontWeight:600, cursor:"pointer", opacity:busy?0.5:1 }}>
                  {busy==="disconnect" ? "Disconnecting..." : "Yes, disconnect"}
                </button>
                <button onClick={() => setConfirmDisconnect(false)}
                  style={{ padding:"8px 14px", background:"transparent", border:"1px solid rgba(255,255,255,0.08)", borderRadius:6, color:"#64748B", fontSize:12, cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#475569", fontWeight:600, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Import</div>
          <button disabled={importing} onClick={async () => { setImporting(true); await onImportSeed("ireland_wedding"); setImporting(false); }}
            style={{ width:"100%", background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.25)", borderRadius:8, color:"#A5B4FC", padding:"10px 14px", fontSize:13, cursor:importing?"wait":"pointer", textAlign:"left", opacity:importing?0.6:1 }}>
            {importing ? "Importing…" : "Import Ireland wedding tracker"}
            <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>18 projects · 153 checklist tasks · 81 vendor candidates. Safe to re-run; never overwrites your edits.</div>
          </button>
        </div>

        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#475569", fontWeight:600, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Privacy & Legal</div>
          <a href="/privacy" target="_blank" rel="noreferrer" style={{ display:"block", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#CBD5E1", textDecoration:"none", marginBottom:6 }}>
            Privacy Policy →
          </a>
          <a href="/terms" target="_blank" rel="noreferrer" style={{ display:"block", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#CBD5E1", textDecoration:"none", marginBottom:6 }}>
            Terms of Service →
          </a>
          <a href="/security" target="_blank" rel="noreferrer" style={{ display:"block", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#CBD5E1", textDecoration:"none" }}>
            Security Overview →
          </a>
        </div>

        <div style={{ marginBottom:24, paddingTop:20, borderTop:"1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize:11, color:"#F87171", fontWeight:600, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Danger Zone</div>
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)}
              style={{ width:"100%", background:"rgba(234,67,53,0.08)", border:"1px solid rgba(234,67,53,0.25)", borderRadius:8, color:"#F87171", padding:"10px 14px", fontSize:13, cursor:"pointer", textAlign:"left", fontWeight:600 }}>
              Delete Account
            </button>
          ) : (
            <div style={{ background:"rgba(234,67,53,0.05)", border:"1px solid rgba(234,67,53,0.3)", borderRadius:8, padding:"14px" }}>
              <p style={{ margin:"0 0 6px", fontSize:13, color:"#F87171", fontWeight:600 }}>Permanently delete your account?</p>
              <p style={{ margin:"0 0 12px", fontSize:12, color:"#94A3B8", lineHeight:1.5 }}>
                All your tasks, Gmail tokens, and profile data will be erased immediately. This cannot be undone.
              </p>
              <p style={{ margin:"0 0 6px", fontSize:11, color:"#64748B" }}>Type DELETE to confirm:</p>
              <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                style={{ width:"100%", background:"#080D1A", border:"1px solid #1E3A5F", borderRadius:6, color:"#CBD5E1", padding:"8px 10px", fontSize:13, marginBottom:10, boxSizing:"border-box" }} />
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={handleDelete} disabled={deleteConfirmText !== "DELETE" || busy==="delete"}
                  style={{ flex:1, background:"#EA4335", border:"none", borderRadius:6, color:"#fff", padding:"8px", fontSize:12, fontWeight:700, cursor:deleteConfirmText==="DELETE"?"pointer":"not-allowed", opacity:deleteConfirmText==="DELETE"&&!busy?1:0.4 }}>
                  {busy==="delete" ? "Deleting..." : "Delete my account"}
                </button>
                <button onClick={() => { setConfirmDelete(false); setDeleteConfirmText(""); }}
                  style={{ padding:"8px 14px", background:"transparent", border:"1px solid rgba(255,255,255,0.08)", borderRadius:6, color:"#64748B", fontSize:12, cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <button onClick={onSignOut}
          style={{ width:"100%", background:"transparent", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, color:"#94A3B8", padding:"10px", fontSize:13, cursor:"pointer" }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ─── RELOOK DATE MODAL ───────────────────────────────────────────────────────
function RelookModal({ onConfirm, onCancel }) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [date, setDate] = useState(tomorrow.toISOString().split("T")[0]);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={onCancel}>
      <div style={{ background:"#0F1B2D", border:"1px solid #1E3A5F", borderRadius:14, padding:"28px 32px", width:320, boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin:"0 0 6px", color:"#E2E8F0", fontSize:16, fontWeight:700 }}>Set Relook Date</h3>
        <p style={{ margin:"0 0 20px", color:"#64748B", fontSize:13 }}>This task will automatically return to Today on this date.</p>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ width:"100%", background:"#080D1A", border:"1px solid #1E3A5F", borderRadius:8, color:"#CBD5E1", padding:"10px 12px", fontSize:14, marginBottom:16, boxSizing:"border-box" }} />
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => onConfirm(date)}
            style={{ flex:1, background:"#6366F1", border:"none", borderRadius:8, color:"#fff", padding:"10px", fontSize:14, fontWeight:600, cursor:"pointer" }}>
            Move to Later
          </button>
          <button onClick={onCancel}
            style={{ padding:"10px 16px", background:"transparent", border:"1px solid #1E3A5F", borderRadius:8, color:"#64748B", fontSize:14, cursor:"pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── API helper: attaches auth token for web, uses localhost for desktop ────
// API_BASE logic:
//   - If REACT_APP_API_URL is set to a non-empty value, use it (for custom deployments)
//   - If running in production (window.location exists and is not localhost), use same-origin ("")
//   - Otherwise (local dev), use http://localhost:3001
const API_BASE = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== "undefined" && window.location && !window.location.hostname.includes("localhost")) return "";
  return "http://localhost:3001";
})();

// On a 401 (login token expired — common after the tab has been asleep and the
// auto-refresh timer hasn't fired yet) refresh the Supabase session once and
// retry with the new token. Prevents a stale token from failing a sync or save.
let refreshInFlight = null;
async function apiFetch(path, options = {}, token = null) {
  const doFetch = (tok) => {
    const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
    if (tok) headers["Authorization"] = `Bearer ${tok}`;
    return fetch(`${API_BASE}${path}`, { ...options, headers });
  };
  let res = await doFetch(token);
  if (res.status === 401 && token && supabase) {
    try {
      if (!refreshInFlight) refreshInFlight = supabase.auth.refreshSession().finally(() => { refreshInFlight = null; });
      const { data } = await refreshInFlight;
      const fresh = data?.session?.access_token;
      if (fresh && fresh !== token) {
        window.__toodue_token__ = fresh;
        res = await doFetch(fresh);
      }
    } catch(e) { console.warn("Session refresh failed:", e.message); }
  }
  return res;
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
          queryParams: { prompt: "select_account" },
        },
      });
      if (error) throw error;
    } catch(e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh", background:"#080D1A", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background:"#0F1B2D", border:"1px solid #1E3A5F", borderRadius:16, padding:"48px 40px", maxWidth:400, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:32, fontWeight:800, color:"#E2E8F0", marginBottom:8, letterSpacing:"-0.02em" }}>
          Too<span style={{ color:"#6366F1" }}>due</span>
        </div>
        <p style={{ color:"#64748B", fontSize:14, marginBottom:36 }}>AI-powered task management for your inbox</p>
        {error && (
          <div style={{ background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)", borderRadius:8, padding:"10px 14px", color:"#F87171", fontSize:13, marginBottom:20 }}>
            {error.includes("not_allowed") ? "You're on the waitlist — Sean will grant you access soon." : error}
          </div>
        )}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          style={{ width:"100%", padding:"12px 20px", background:"#fff", border:"none", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", gap:10, cursor:loading?"not-allowed":"pointer", opacity:loading?0.7:1, fontSize:15, fontWeight:600, color:"#1a1a1a" }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          {loading ? "Signing in..." : "Continue with Google"}
        </button>
        <p style={{ color:"#334155", fontSize:12, marginTop:24 }}>By signing in you agree to our terms. Your email data is never stored or shared.</p>
      </div>
    </div>
  );
}

// ─── Waitlist Screen ──────────────────────────────────────────────────────────
function WaitlistScreen({ email, onSignOut }) {
  return (
    <div style={{ minHeight:"100vh", background:"#080D1A", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background:"#0F1B2D", border:"1px solid #1E3A5F", borderRadius:16, padding:"48px 40px", maxWidth:400, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:32, marginBottom:16 }}>⏳</div>
        <h2 style={{ color:"#E2E8F0", fontWeight:700, marginBottom:8 }}>You're on the waitlist</h2>
        <p style={{ color:"#64748B", fontSize:14, marginBottom:8 }}>Signed in as <strong style={{color:"#94A3B8"}}>{email}</strong></p>
        <p style={{ color:"#64748B", fontSize:14, marginBottom:32 }}>Sean will grant you access shortly. Check back soon!</p>
        <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
          <button onClick={() => window.location.reload()} style={{ background:"#6366F1", border:"none", borderRadius:8, padding:"8px 20px", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:600 }}>Check again</button>
          <button onClick={onSignOut} style={{ background:"transparent", border:"1px solid #1E3A5F", borderRadius:8, padding:"8px 20px", color:"#64748B", cursor:"pointer", fontSize:13 }}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [tasks, setTasksRaw]            = useState(() => loadStored("toodue_tasks", []));
  const [projects, setProjects]         = useState(() => loadStored("toodue_projects", []));
  const [newIds, setNewIds]             = useState(new Set());
  const [syncStatus, setSyncStatus]     = useState("idle");
  const [lastSync, setLastSync]         = useState(() => loadStored("toodue_lastSync", null));
  const lastSyncTimestamp = useRef(loadStored("toodue_lastSyncTs", null)); // Unix seconds for incremental sync
  const [syncLog, setSyncLog]           = useState([]);
  const [msgLog, setMsgLog]             = useState(() => loadStored("toodue_msglog", [])); // per-email dispositions, newest first
  const [newCount, setNewCount]         = useState(0);
  const [activeFolder, setActiveFolder] = useState("all");
  const [projectTab, setProjectTab] = useState("tasks"); // "tasks" | "checklist" — inside a project view
  // Clear banner filter when changing folders
  const setActiveFolderAndClearFilter = (f) => { setActiveFolder(f); setBannerFilter(null); };
  const [filterPriority, setFilterPriority] = useState("all");
  const [bannerFilter, setBannerFilter] = useState(null); // "overdue" | "followup" | "replies" | null
  const [activeBucket, setActiveBucket] = useState("today"); // "today" | "tomorrow" | "later"
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverBucket, setDragOverBucket] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [relookModal, setRelookModal] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAskClaude, setShowAskClaude] = useState(false);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showBetaDisclaimer, setShowBetaDisclaimer] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const hasSynced = useRef(false);
  const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron;
  const isWeb = !isElectron && !!supabase;

  // ── Auth state (web only) ──────────────────────────────────────────────────
  const [session, setSession]       = useState(null);
  const [authLoading, setAuthLoading] = useState(isWeb);
  const [isAllowed, setIsAllowed]   = useState(false);
  const authToken = session?.access_token || null;
  // Cmd/Ctrl+K opens Ask Claude
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowAskClaude(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Expose token and Gmail email globally so TaskCard can access them
  useEffect(() => { window.__toodue_token__ = authToken; }, [authToken]);
  const [gmailUserEmail, setGmailUserEmail] = useState(null);
  const [gmailConnected, setGmailConnected] = useState(null); // null = unknown, false = needs connect
  const [gmailExpired, setGmailExpired] = useState(false);      // true when the server reports a dead token
  const [gmailCheck, setGmailCheck] = useState(null);           // result of the "Test Gmail" button
  const testGmail = async () => {
    setGmailCheck({ pending: true });
    try {
      const res = await apiFetch("/api/gmail/check", {}, authToken);
      const data = await res.json();
      setGmailCheck(data);
      if (data.connected === false || data.reconnect) { setGmailConnected(false); setGmailExpired(!!data.reconnect); }
      setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: data.ok ? `Gmail check OK — ${data.email}: ${data.inbox7d} inbox + ${data.sent7d} sent in 7d` : `Gmail check FAILED: ${data.error}`, error: !data.ok }]);
    } catch(e) { setGmailCheck({ ok: false, error: e.message }); }
  };
  useEffect(() => { if (gmailUserEmail) window.__toodue_gmail_email__ = gmailUserEmail; }, [gmailUserEmail]);

  useEffect(() => {
    if (!isWeb) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) upsertAccount(session);
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Every SIGNED_IN / TOKEN_REFRESHED carries fresh tokens — keep the store current
      // so switching back to this account later still works.
      if (session) upsertAccount(session);
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, [isWeb]);

  // Import a bundled seed (projects + tasks) into the active account, then reload data.
  const importSeed = async (seedName) => {
    try {
      const res = await apiFetch("/api/import-seed", { method: "POST", body: JSON.stringify({ seed: seedName }) }, authToken);
      const data = await res.json();
      if (!res.ok) { alert("Import failed: " + (data.error || res.status)); return; }
      alert(`Imported ${data.projects} projects and ${data.tasks_added} tasks${data.tasks_skipped ? ` (${data.tasks_skipped} already present)` : ""}.`);
      window.location.reload();
    } catch(e) { alert("Import failed: " + e.message); }
  };

  // Open the server-side Gmail OAuth in a popup; the callback page self-closes.
  const connectGmail = () => {
    if (!authToken) return;
    const w = window.open(`${API_BASE}/auth/gmail?token=${encodeURIComponent(authToken)}`, "toodue_gmail", "width=520,height=640");
    // When the popup closes, re-check connection and kick a sync.
    const poll = setInterval(() => {
      if (!w || w.closed) { clearInterval(poll); setGmailConnected(null); runSync(); }
    }, 800);
  };

  // ── Multi-account: switch / add / sign out ─────────────────────────────────
  const [, setAccountsVersion] = useState(0); // bump to re-render after the store changes
  const accounts = Object.values(loadAccounts()).sort((a, b) => (b.last_used || 0) - (a.last_used || 0));
  const activeUserId = session?.user?.id || null;

  const switchAccount = async (userId) => {
    if (userId === activeUserId) return;
    const acct = loadAccounts()[userId];
    if (!acct) return;
    const { error } = await supabase.auth.setSession({
      access_token: acct.access_token,
      refresh_token: acct.refresh_token,
    });
    if (error) {
      // Refresh token is dead (revoked or expired). Drop it and ask to re-add.
      removeAccount(userId);
      setAccountsVersion(v => v + 1);
      alert(`That session for ${acct.email} has expired. Use "Add account" to sign in again.`);
      return;
    }
    localStorage.setItem(ACTIVE_KEY, userId);
    // Full reload: resets every piece of in-memory state (tasks, projects,
    // gmail email, sync cursor) so nothing from the old account lingers.
    window.location.reload();
  };

  const addAccount = async () => {
    // Do NOT sign out first — signOut() defaults to global scope and would
    // revoke this account's refresh token, killing the saved session.
    // Supabase simply replaces the local session when the new login lands.
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        queryParams: { prompt: "select_account" }, // force the Google account chooser
      },
    });
  };

  const signOutCurrent = async () => {
    const leaving = activeUserId;
    // Local scope only: clears this browser's copy without revoking the token
    // server-side, so other tabs/devices (and our stored copy) stay valid.
    await supabase.auth.signOut({ scope: "local" });
    if (leaving) removeAccount(leaving);
    const remaining = Object.values(loadAccounts());
    if (remaining.length > 0) {
      // Hop to the most recently used remaining account
      const next = remaining.sort((a, b) => (b.last_used || 0) - (a.last_used || 0))[0];
      const { error } = await supabase.auth.setSession({ access_token: next.access_token, refresh_token: next.refresh_token });
      if (!error) localStorage.setItem(ACTIVE_KEY, next.id);
      else removeAccount(next.id);
    }
    window.location.reload();
  };

  // Check beta allowlist when session changes
  useEffect(() => {
    if (!isWeb || !session) { setIsAllowed(!isWeb); return; }
    supabase.from("profiles").select("is_allowed,beta_accepted_at").eq("id", session.user.id).single()
      .then(({ data }) => setIsAllowed(data?.is_allowed || false));
  }, [isWeb, session]);

  // Use a ref for processedMsgIds so changes don't re-create runSync or trigger re-renders
  const processedMsgIds = useRef(loadStored("toodue_processedIds", new Set()));

  // Planning partner (e.g. a fiancée who emails vendors with you cc'd). Their
  // emails count as "our side" in triage. Cached locally, source of truth in
  // profiles.agent_settings.partner. A ref so runSync never goes stale.
  // Tracker workbook metadata (filename, sheets, last auto-update). The file
  // itself lives in Supabase Storage; see TrackerPanel and /api/tracker/*.
  const [tracker, setTracker] = useState(null);
  const [trackerSyncing, setTrackerSyncing] = useState(false);
  const [trackerResult, setTrackerResult] = useState(null);
  const trackerRef = useRef(null);
  useEffect(() => { trackerRef.current = tracker; }, [tracker]);
  const trackerTimer = useRef(null);
  const runTrackerSync = useCallback(async () => {
    if (!isWeb || !authToken || !trackerRef.current) return;
    setTrackerSyncing(true);
    try {
      const res = await apiFetch("/api/tracker/sync", { method: "POST", body: JSON.stringify({}) }, authToken);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTracker(data.tracker || trackerRef.current);
      setTrackerResult({ changes: data.changes });
      if (data.changes > 0) setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `📊 Tracker updated — ${data.changes} cell${data.changes !== 1 ? "s" : ""} written to ${trackerRef.current?.filename || "the workbook"}` }]);
    } catch(e) {
      setTrackerResult({ error: e.message });
      setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `📊 Tracker update failed: ${e.message}`, error: true }]);
    }
    setTrackerSyncing(false);
  }, [isWeb, authToken]);
  // Debounced: called after task saves and after syncs, so the workbook follows the app
  const scheduleTrackerSync = useCallback((delayMs = 8000) => {
    if (!trackerRef.current) return;
    clearTimeout(trackerTimer.current);
    trackerTimer.current = setTimeout(() => runTrackerSync(), delayMs);
  }, [runTrackerSync]);

  const [partner, setPartner] = useState(() => loadStored("toodue_partner", null));
  const partnerRef = useRef(partner);
  useEffect(() => { partnerRef.current = partner; }, [partner]);
  const savePartner = async (p) => {
    const clean = p && (p.name || p.email) ? { name: (p.name || "").trim(), email: (p.email || "").trim().toLowerCase() } : null;
    setPartner(clean);
    saveStored("toodue_partner", clean);
    try { await apiFetch("/api/brief", { method: "POST", body: JSON.stringify({ settings: { partner: clean } }) }, authToken); }
    catch(e) { console.warn("Save partner failed:", e.message); }
  };
  // Save-tracking for flushTasksToServer (declared here so the login loader can seed it)
  const lastSaved = useRef(new Map()); // task_id → JSON of the row as last confirmed by the server
  const saveRetry = useRef(null);

  const setTasks = useCallback((updater) => {
    setTasksRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!isWeb) saveStored("toodue_tasks", next);
      return next;
    });
  }, [isWeb]);

  const setProcessedMsgIds = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(processedMsgIds.current) : updater;
    processedMsgIds.current = next;
    if (!isWeb) saveStored("toodue_processedIds", next);
    // Web: keep the most recent ~1500 keys locally so the fetch overlap window
    // doesn't re-triage messages we already looked at (task-less ones included).
    else saveStored("toodue_processedRecent", Array.from(next).slice(-1500));
  }, [isWeb]);

  // ── Web: load tasks from Supabase on login ───────────────────────────────
  useEffect(() => {
    if (!isWeb || !session || !isAllowed) return;
    (async () => {
      try {
        const tasksRes = await apiFetch("/api/tasks", {}, authToken);
        const tasksData = await tasksRes.json();
        if (tasksData.tasks && tasksData.tasks.length > 0) {
          setTasksRaw(tasksData.tasks);
          // Everything just loaded is by definition already saved
          lastSaved.current = new Map(tasksData.tasks.map(t => [t.task_id, JSON.stringify(t)]));
          const ids = new Set();
          tasksData.tasks.forEach(t => {
            if (t.gmail_id) {
              ids.add(t.is_sent ? `sent:${t.gmail_id}` : `recv:${t.gmail_id}`);
            }
            if (t.task_id) {
              const key = t.task_id.replace('task-', '');
              ids.add(key);
            }
          });
          (loadStored("toodue_processedRecent", []) || []).forEach(k => ids.add(k));
          processedMsgIds.current = ids;
          console.log(`Loaded ${tasksData.tasks.length} tasks, ${ids.size} processed IDs (tasks + recent)`);
        }

        // Check if cron has run since our last client sync — if so, auto-sync
        const lastClientSyncTs = lastSyncTimestamp.current;
        if (lastClientSyncTs) {
          const statusRes = await apiFetch("/api/status", {}, authToken);
          const statusData = await statusRes.json();
          if (statusData.last_cron_sync) {
            const cronTs = Math.floor(new Date(statusData.last_cron_sync).getTime() / 1000);
            if (cronTs > lastClientSyncTs) {
              console.log("Cron ran since last sync — auto-syncing on open");
              // Small delay to let tasks load first
              setTimeout(() => { if (!hasSynced.current) { hasSynced.current = true; runSync(); } }, 1000);
            }
          }
        }
      } catch(e) { console.error("Failed to load data from server:", e); }
    })();
  }, [isWeb, session, isAllowed, authToken]); // eslint-disable-line

  // ── Web: load planning-partner setting on login ─────────────────────────
  useEffect(() => {
    if (!isWeb || !session || !isAllowed) return;
    (async () => {
      try {
        const res = await apiFetch("/api/brief", {}, authToken);
        const data = await res.json();
        if (data?.settings && "partner" in data.settings) { setPartner(data.settings.partner || null); saveStored("toodue_partner", data.settings.partner || null); }
        if (data?.settings && "tracker" in data.settings) setTracker(data.settings.tracker || null);
      } catch(e) { /* keep local cache */ }
    })();
  }, [isWeb, session, isAllowed, authToken]); // eslint-disable-line

  // ── Web: load projects from Supabase on login ────────────────────────────
  useEffect(() => {
    if (!isWeb || !session || !isAllowed) return;
    (async () => {
      try {
        const res = await apiFetch("/api/projects", {}, authToken);
        const data = await res.json();
        let loaded = Array.isArray(data.projects) ? data.projects : [];

        // One-time migration: any legacy task.project string without a matching
        // project row becomes a real project, and its tasks get project_ids set.
        const existingNames = new Set(loaded.map(p => p.name));
        const legacyNames = [...new Set(
          tasks.filter(t => t.project && (!Array.isArray(t.project_ids) || t.project_ids.length === 0))
               .map(t => t.project)
        )].filter(n => !existingNames.has(n));

        if (legacyNames.length > 0) {
          const created = [];
          for (const name of legacyNames) {
            const id = `proj_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
            const proj = { id, name, sort_order: Date.now() + created.length, origin: "auto" };
            created.push(proj);
            try { await apiFetch("/api/projects", { method: "POST", body: JSON.stringify(proj) }, authToken); }
            catch(e) { console.warn("Migrate project failed:", name, e.message); }
          }
          loaded = [...loaded, ...created];
          const nameToId = {};
          loaded.forEach(p => { nameToId[p.name] = p.id; });
          // backfill project_ids on tasks and persist
          setTasks(prev => {
            const next = prev.map(t => {
              if (t.project && (!Array.isArray(t.project_ids) || t.project_ids.length === 0) && nameToId[t.project]) {
                return { ...t, project_ids: [nameToId[t.project]] };
              }
              return t;
            });
            flushTasksToServer();
            return next;
          });
          console.log(`Migrated ${legacyNames.length} legacy project(s)`);
        }

        setProjects(loaded);
        saveStored("toodue_projects", loaded);
      } catch(e) { console.error("Failed to load projects:", e); }
    })();
  }, [isWeb, session, isAllowed, authToken]); // eslint-disable-line

  // ── Web: save tasks to Supabase whenever they change ──────────────────────
  const savePending = useRef(null);
  // Debounced save. The passed list is IGNORED as the payload — when the timer
  // fires we read the live task state instead. Two callers racing (e.g. adding a
  // task while a sync finishes) used to clobber each other, because the second
  // call cancelled the first's timer and sent its own older snapshot, silently
  // dropping the newer task. Reading current state at fire time makes the write
  // order-independent and always complete.
  //
  // Only tasks that changed since the last SUCCESSFUL save are sent. Posting the
  // entire list every time blew past the server's request-size cap as the list
  // grew, and because the response was never checked the failure was invisible.
  // A failed save keeps its tasks dirty so the next flush retries them.
  const flushTasksToServer = useCallback(() => {
    if (!isWeb || !authToken) return;
    clearTimeout(savePending.current);
    savePending.current = setTimeout(async () => {
      const current = await new Promise(resolve => {
        setTasksRaw(prev => { resolve(prev); return prev; });
      });
      if (!current?.length) return;
      const changed = current.filter(t => JSON.stringify(t) !== lastSaved.current.get(t.task_id));
      if (!changed.length) return;
      try {
        const res = await apiFetch("/api/tasks", { method:"POST", body: JSON.stringify({ tasks: changed }) }, authToken);
        if (!res.ok) {
          let detail = `${res.status} ${res.statusText}`;
          try { const j = await res.json(); if (j?.error) detail += ` — ${j.error}`; } catch {}
          throw new Error(detail);
        }
        changed.forEach(t => lastSaved.current.set(t.task_id, JSON.stringify(t)));
        clearTimeout(saveRetry.current);
        scheduleTrackerSync();
      } catch(e) {
        console.error(`Failed to save ${changed.length} task(s):`, e.message);
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `⚠️ Save failed (${changed.length} task${changed.length !== 1 ? "s" : ""} unsaved): ${e.message}`, error: true }]);
        // Retry once after a short pause (covers transient network / rate-limit blips)
        clearTimeout(saveRetry.current);
        saveRetry.current = setTimeout(() => flushTasksToServer(), 8000);
      }
    }, 1500); // debounce so rapid changes batch together
  }, [isWeb, authToken]);

  const runSync = useCallback(async () => {
    setSyncStatus("syncing");
    setNewCount(0);
    setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: "Fetching messages from Gmail and Slack..." }]);
    try {
      // Incremental sync with a safety overlap. Gmail's search index can lag new
      // mail by a minute or more, and a long sync used to move the cursor past
      // anything that arrived while it ran — both silently lost messages. We now
      // fetch from 15 minutes BEFORE the last cursor and rely on the persisted
      // processed-key set to skip what we've already handled.
      const syncStartTs = Math.floor(Date.now() / 1000);
      const OVERLAP = 15 * 60;
      const sinceTs = lastSyncTimestamp.current ? Math.max(0, lastSyncTimestamp.current - OVERLAP) : null;
      const sinceParam = sinceTs ? `?since=${sinceTs}` : "";
      const res = await apiFetch(`/api/messages${sinceParam}`, {}, authToken);
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || data.message || `Fetch failed (${res.status})`);
      {
        const inbox = (data.gmail || []).filter(m => !m.is_sent).length, sent = (data.gmail || []).filter(m => m.is_sent).length;
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Fetched ${inbox} inbox + ${sent} sent from Gmail${sinceTs ? ` since ${new Date(sinceTs * 1000).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : " (last 30 days)"}${data.gmail_connected === false ? " — Gmail NOT connected" : ""}`, dim: true }]);
      }
      if (data.userEmail) setGmailUserEmail(data.userEmail);
      if (typeof data.gmail_connected === "boolean") setGmailConnected(data.gmail_connected);
      setGmailExpired(!!data.gmail_reconnect);
      if (data.gmail_error) {
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: data.gmail_reconnect
          ? `⚠️ Gmail access has expired or been revoked — click Reconnect Gmail in the sync bar to resume syncing. (${data.gmail_error})`
          : `⚠️ Gmail error: ${data.gmail_error}`, error: true }]);
      }
      const allMessages = [
        ...data.gmail.map(m => ({
          // proc_key namespaces sent vs received so the same message ID
          // from two folders is never incorrectly skipped
          proc_key:  m.is_sent ? `sent:${m.id}` : `recv:${m.id}`,
          msg_id:    m.id,
          source:    "Gmail",
          subject:   m.subject,
          thread_id: m.thread_id,
          from:      m.from,
          to:        m.to || "",
          cc:        m.cc || "",
          cc_only:   m.cc_only || false,
          is_forwarded: m.is_forwarded || false,
          body:      m.snippet,
          timestamp: m.receivedAt,
          gmail_id:  m.id,
          is_sent:   m.is_sent || false,
        })),
        ...data.slack.map(m => ({
          proc_key:  `slack:${m.id}`,
          msg_id:    m.id,
          source:    "Slack",
          channel:   m.channel,
          thread_id: m.thread_id,
          from:      m.author,
          to:        "",
          body:      m.message,
          timestamp: m.receivedAt,
          gmail_id:  null,
          is_sent:   false,
        })),
      ];
      const unprocessed = allMessages.filter(m => !processedMsgIds.current.has(m.proc_key));
      // Per-message disposition log — answers "what did Toodue do with that email?"
      const disp = {}; // proc_key → { ...message meta, outcomes: [] }
      const noteMsg = (m, text, taskId = null) => {
        if (!m) return;
        const k = m.proc_key || m.msg_id;
        if (!disp[k]) disp[k] = { key: k, msg_id: m.msg_id, gmail_id: m.gmail_id || null, subject: m.subject || m.channel || "(no subject)", from: m.from || "", to: m.to || "", is_sent: !!m.is_sent, source: m.source, at: m.timestamp || new Date().toISOString(), synced_at: new Date().toISOString(), outcomes: [] };
        disp[k].outcomes.push({ text, task_id: taskId });
      };
      if (unprocessed.length === 0) {
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `No new messages to process${allMessages.length ? ` (${allMessages.length} fetched, all already handled)` : ""}.`, dim: true }]);
        setSyncStatus("idle");
        setLastSync(t => { const v = new Date().toLocaleTimeString(); saveStored("toodue_lastSync", v); return v; });
      lastSyncTimestamp.current = syncStartTs;
      saveStored("toodue_lastSyncTs", lastSyncTimestamp.current);
      // Flush all tasks to server after sync (reads live state when it fires)
      if (isWeb) flushTasksToServer();
        return;
      }
      setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Found ${unprocessed.length} new messages — triaging...` }]);

      // ── PASS 1: Quick triage with snippet only — run batches in parallel ──
      const triageBatchSize = 20;
      const triageBatches = [];
      for (let i = 0; i < unprocessed.length; i += triageBatchSize) {
        triageBatches.push(unprocessed.slice(i, i + triageBatchSize).map(m => ({
          msg_id: m.msg_id,
          source: m.source,
          from: m.from || "Unknown",
          to: m.to || "",
          cc: m.cc || "",
          subject: m.subject || "",
          body: (m.body || "").slice(0, 300),
          is_sent: m.is_sent || false,
          cc_only: m.cc_only || false,
          is_forwarded: m.is_forwarded || false,
        })));
      }
      const triageBatchResults = await Promise.all(triageBatches.map(b => claudeTriageMessages(b, authToken)));
      const triageResults = triageBatchResults.flat();

      const actionableIds = new Set(triageResults.filter(r => r.actionable).map(r => r.msg_id));
      const noiseIds = new Set(triageResults.filter(r => !r.actionable && r.category === "noise").map(r => r.msg_id));
      const skippedByTriage = unprocessed.filter(m => !actionableIds.has(m.msg_id));
      const actionable = unprocessed.filter(m => actionableIds.has(m.msg_id));
      // Status-only correspondence: nothing to do, but worth recording against a task.
      // Extraction "skip" results get added to this set below.
      const statusCandidates = new Map(); // msg_id → message
      skippedByTriage.forEach(m => { if (!noiseIds.has(m.msg_id)) statusCandidates.set(m.msg_id, m); else noteMsg(m, "Ignored as noise (marketing / automated)"); });

      setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Triage: ${actionable.length} actionable, ${statusCandidates.size} status updates, ${noiseIds.size} noise — extracting tasks...` }]);

      // ── PASS 2: Full extraction on actionable messages only (full body) ──
      const batchSize = 10;
      const results = [];

      // Detect emails with numbered action lists — send these solo for best extraction
      const hasNumberedList = (m) => {
        const body = m.body || '';
        return /\d+\.\s+\w/.test(body) && (body.match(/\d+\./g) || []).length >= 3;
      };

      const soloMessages = actionable.filter(m => hasNumberedList(m));
      const batchMessages = actionable.filter(m => !hasNumberedList(m));
      // Messages whose extraction call failed (rate limit, overload). They are NOT
      // marked processed and the sync cursor is held back so the next sync retries them.
      const failedMsgs = [];

      // Process list emails one at a time with a focused prompt
      for (const m of soloMessages) {
        const msg = [{
          proc_key: m.proc_key,
          msg_id: m.msg_id,
          source: m.source,
          from: m.from || "Unknown",
          to: m.to || "",
          cc: m.cc || "",
          subject: m.subject || "",
          body: (m.body || "").slice(0, 2500),
          timestamp: m.timestamp,
          channel: m.channel || "",
          is_sent: m.is_sent || false,
          cc_only: m.cc_only || false,
          is_forwarded: m.is_forwarded || false,
          current_user_email: data.userEmail || "",
        }];
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Extracting multiple tasks from: "${m.subject || '(no subject)'}"...` }]);
        try {
          const soloResults = await claudeExtractTasks(msg, authToken, projects, partnerRef.current);
          results.push(...soloResults);
        } catch(e) {
          failedMsgs.push(m); noteMsg(m, `Extraction failed — will retry (${e.message})`);
          setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `⚠️ Extraction failed for "${m.subject || '(no subject)'}" — will retry next sync (${e.message})`, error: true }]);
        }
      }

      // Process remaining emails in batches
      for (let i = 0; i < batchMessages.length; i += batchSize) {
        const batch = batchMessages.slice(i, i + batchSize).map(m => ({
          proc_key: m.proc_key,
          msg_id: m.msg_id,
          source: m.source,
          from: m.from || "Unknown",
          to: m.to || "",
          cc: m.cc || "",
          subject: m.subject || "",
          body: (m.body || "").slice(0, 2500),
          timestamp: m.timestamp,
          channel: m.channel || "",
          is_sent: m.is_sent || false,
          cc_only: m.cc_only || false,
          is_forwarded: m.is_forwarded || false,
          current_user_email: data.userEmail || "",
        }));
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Extracting tasks from messages ${i + 1}-${Math.min(i + batchSize, batchMessages.length)} of ${batchMessages.length}...` }]);
        try {
          const batchResults = await claudeExtractTasks(batch, authToken, projects, partnerRef.current);
          results.push(...batchResults);
        } catch(e) {
          batchMessages.slice(i, i + batchSize).forEach(m => { failedMsgs.push(m); noteMsg(m, `Extraction failed — will retry (${e.message})`); });
          setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `⚠️ Extraction failed for messages ${i + 1}-${Math.min(i + batchSize, batchMessages.length)} — will retry next sync (${e.message})`, error: true }]);
        }
      }
      const created = [];
      const log = [];
      // Vendor mentions gathered from BOTH passes, applied together after the status pass
      const vendorMentions = []; // { vendor_name, project_id, stage, note, from, at, msg_id, fallback_project_id }
      const collectVendorMentions = (arr, src, fallbackProjectId = null) => {
        if (!Array.isArray(arr) || !src) return;
        const from = src.is_sent ? "You" : (extractSenderName(src.from) || "Unknown");
        arr.forEach(v => {
          if (!v || !v.vendor_name) return;
          if (vendorMentions.some(x => x.msg_id === src.msg_id && x.vendor_name.toLowerCase() === String(v.vendor_name).toLowerCase())) return;
          vendorMentions.push({ vendor_name: String(v.vendor_name).trim(), project_id: v.project_id || null, stage: v.stage || "Discussed",
                                note: v.note || "", from, at: src.timestamp || new Date().toISOString(), msg_id: src.gmail_id || null, fallback_project_id: fallbackProjectId });
        });
      };
      results.forEach(r => {
        // Match using Claude's returned is_sent to build the correct proc_key,
        // so a sent message is never confused with a received one sharing the same msg_id.
        // Claude echoes is_sent from what we sent it, so this is reliable.
        const claudeIsSent = r.is_sent || false;
        const expectedProcKey = claudeIsSent ? `sent:${r.msg_id}` : `recv:${r.msg_id}`;
        let src = unprocessed.find(m => m.proc_key === expectedProcKey);
        // Fallback: if no exact match (e.g. Slack), match by msg_id only
        if (!src) src = unprocessed.find(m => m.msg_id === r.msg_id);
        collectVendorMentions(r.vendor_mentions, src, r.project_id || null);

        if (r.action === "skip") {
          if (src?.is_forwarded) {
            // Forwards are never dropped — turn the skip into a review item.
            r.action = "create";
            r.title = (src.subject || "Forwarded email").replace(/^\s*(fwd?|fw)\s*:\s*/i, "").trim() || "Forwarded email";
            r.visibility = "review";
            r.next_action = "Review and decide whether anything is needed";
            r.context = r.context || (src.body || "").slice(0, 240);
            r.priority = r.priority || "low";
            r.assignee_name = "You";
            log.push({ time: new Date().toLocaleTimeString(), text: `Forwarded email kept for review: "${r.title}"` });
          } else {
            // Not a task — but it may still tell us something about one. Hand it to the status pass.
            if (src && !noiseIds.has(src.msg_id)) statusCandidates.set(src.msg_id, src);
            log.push({ time: new Date().toLocaleTimeString(), text: `No task in "${src?.subject || (src?.body||"").slice(0,45)}..." — ${r.skip_reason}`, dim: true });
            noteMsg(src, `No task to extract (${r.skip_reason || "nothing actionable"}) — checked for status/vendor updates`);
            return;
          }
        }
        // Use server-provided is_sent as the definitive ground truth
        // But if Claude assigned to "you"/"sean"/the sender email, override to received
        const claudeAssigneeIsMe = ["you", "sean", "me", "i"].includes((r.assignee_name || "").toLowerCase().trim())
          || (data.userEmail && (r.assignee_name || "").toLowerCase().includes(data.userEmail.split("@")[0].toLowerCase()));
        const isSent = claudeAssigneeIsMe ? false : (src?.is_sent !== undefined ? src.is_sent : claudeIsSent);
        const senderName = isSent
          ? "You"
          : (r.sender_name || extractSenderName(src?.from) || "Unknown");
        // If Claude marked is_sent=false, this task is assigned to the current user
        // Force assignee_name to "You" regardless of what Claude returned
        const assigneeName = isSent
          ? (r.assignee_name && r.assignee_name !== "You" && !r.assignee_name.includes("@")
              ? r.assignee_name
              : extractSenderName(src?.to) || "Unknown")
          : "You";

        // Apply default due date only if Claude returned null
        const dueDate = r.due_date || null;

        // Follow-up deadline: 24h after due date, or 24h after send time if no due date
        const sendTime = src?.timestamp ? new Date(src.timestamp) : new Date();
        const followUpAfter = dueDate
          ? new Date(new Date(dueDate).getTime() + 24 * 60 * 60 * 1000).toISOString()
          : new Date(sendTime.getTime() + 48 * 60 * 60 * 1000).toISOString(); // 48h if no due date

        // Visibility: trust Claude if it said "fyi", or force "fyi" if server detected cc_only (safety net)
        const srcCcOnly = !!src?.cc_only;
        const claudeVisibility = r.visibility === "fyi" ? "fyi" : r.visibility === "review" ? "review" : "normal";
        const visibility = srcCcOnly && claudeVisibility === "normal" ? "fyi" : claudeVisibility;

        // Project routing: accept Claude's pick when confident and the id is real; otherwise flag for filing.
        const routedProject = r.project_id && projects.find(p => p.id === r.project_id) ? projects.find(p => p.id === r.project_id) : null;
        const routeConf = typeof r.project_confidence === "number" ? r.project_confidence : 0;
        const routed = routedProject && routeConf >= 0.65;
        const needsFiling = projects.length > 0 && !routed;

        created.push({
          task_id: `task-${src?.proc_key || r.msg_id}-${(r.assignee_name || '').replace(/[^a-zA-Z0-9]/g,'-').toLowerCase().slice(0,12)}-${(r.title || '').replace(/[^a-zA-Z0-9]/g,'-').toLowerCase().slice(0,20)}`,
          priority_bucket: "today",
          relook_date: null,
          sort_order: Date.now(),
          visibility,
          cc_only: srcCcOnly,
          is_forwarded: !!src?.is_forwarded,
          kind: "action",
          next_action: r.next_action || null,
          project_ids: routed ? [routedProject.id] : [],
          project_confidence: routeConf,
          needs_filing: needsFiling,
          title: r.title,
          owner_id: "u1",
          sender_id: "external",
          sender_name: senderName,
          assignee_name: assigneeName,
          source: src?.source || "Gmail",
          source_label: src?.subject || src?.channel || "(no subject)",
          priority: r.priority || "medium",
          status: "open",
          due_date: dueDate || defaultDueDate(),
          project: routed ? routedProject.name : (r.project || null),
          context: r.context,
          notes: "",
          is_sent: isSent,
          // Seed ownership at creation so it's never null. Reply analysis will
          // update it later; single-message threads are never analyzed, so
          // without this the field stays null forever on un-replied tasks.
          action_owner: isSent ? "assignee" : "you",
          awaiting_response: isSent,
          follow_up_after: isSent ? followUpAfter : null,
          created_at: src?.timestamp || new Date().toISOString(),
          thread_id: src?.thread_id,
          source_url: src?.gmail_id ? gmailMsgUrl(src.gmail_id, isSent) : null,
          gmail_id: src?.gmail_id || null,
        });
        log.push({ time: new Date().toLocaleTimeString(), text: `Task extracted: "${r.title}" · ${r.priority} priority${routed ? ` → ${routedProject.name}` : needsFiling ? " · needs filing" : ""}` });
        noteMsg(src, `Task extracted: "${r.title}"${routed ? ` → ${routedProject.name}` : needsFiling ? " → Review (needs filing)" : ""}${visibility !== "normal" ? ` · ${visibility}` : ""}`, created[created.length - 1].task_id);
      });
      if (created.length > 0) {
        // Before merging: ask Claude to decide which new tasks are continuations of existing thread tasks
        // vs genuinely new topics that deserve their own task
        const existingByThread = {};
        const currentTasks = await new Promise(resolve => {
          setTasks(prev => { resolve(prev); return prev; });
        });
        currentTasks.forEach(t => {
          if (t.thread_id && t.status === "open") {
            if (!existingByThread[t.thread_id]) existingByThread[t.thread_id] = [];
            existingByThread[t.thread_id].push(t);
          }
        });

        // Build candidates for Claude to evaluate — only new tasks that have an existing task in the same thread
        const matchCandidates = [];
        created.forEach(newTask => {
          const existingInThread = newTask.thread_id ? (existingByThread[newTask.thread_id] || []) : [];
          if (existingInThread.length > 0) {
            matchCandidates.push({
              new_task_id: newTask.task_id,
              new_title: newTask.title,
              new_context: (newTask.context || "").slice(0, 200),
              existing_tasks: existingInThread.map(t => ({ task_id: t.task_id, title: t.title, context: (t.context || "").slice(0, 200) })),
            });
          }
        });

        let matches = {};
        if (matchCandidates.length > 0) {
          try { matches = await claudeMatchThreadTasks(matchCandidates, authToken); }
          catch(e) { console.warn("Thread match failed, falling back to new-task behavior:", e.message); }
        }

        const newTaskIds = new Set();
        setTasks(prev => {
          const existingByTaskId = new Set(prev.map(t => t.task_id));
          const toAdd = [];
          const updatedPrev = [...prev]; // vendor stage changes are applied in the unified vendor pass below

          created.forEach(newTask => {
            if (existingByTaskId.has(newTask.task_id)) return;

            const matchedId = matches[newTask.task_id];
            if (matchedId) {
              // Continuation: append a history entry to the matched task
              const idx = updatedPrev.findIndex(t => t.task_id === matchedId);
              if (idx !== -1) {
                const existing = updatedPrev[idx];
                const history = Array.isArray(existing.history) ? [...existing.history] : [];
                history.push({
                  ts: new Date().toISOString(),
                  at: newTask.created_at,
                  type: "update",
                  from: newTask.sender_name || (newTask.is_sent ? "You" : newTask.assignee_name),
                  label: newTask.is_sent ? "You followed up" : "New message in thread",
                  message: newTask.context || newTask.title,
                  msg_id: newTask.gmail_id || null,
                });
                // Who sent the continuation decides who has the ball now: a message
                // YOU sent hands it to them; a message they sent hands it to you.
                const youSent = !!newTask.is_sent;
                updatedPrev[idx] = {
                  ...existing,
                  history,
                  has_reply: !youSent,
                  awaiting_response: youSent,
                  action_owner: youSent ? "assignee" : "you",
                  follow_up_after: youSent ? newTask.follow_up_after || existing.follow_up_after : existing.follow_up_after,
                  next_action: newTask.next_action || existing.next_action,
                  reply_snippet: (newTask.context || "").slice(0, 200),
                  reply_from: youSent ? "You" : (newTask.sender_name || existing.reply_from),
                  reply_msg_id: newTask.gmail_id || existing.reply_msg_id,
                };
                log.push({ time: new Date().toLocaleTimeString(), text: `Thread update on "${existing.title}" — ${history.length} events` });
                const srcMsg = unprocessed.find(m => m.gmail_id && m.gmail_id === newTask.gmail_id && !!m.is_sent === !!newTask.is_sent) || unprocessed.find(m => m.gmail_id === newTask.gmail_id);
                noteMsg(srcMsg, `Attached to existing task in this thread: "${existing.title}" (not a new task)`, existing.task_id);
              }
              return;
            }

            // Genuinely new task — seed with initial history entry
            toAdd.push({
              ...newTask,
              history: [{
                ts: new Date().toISOString(),
                at: newTask.created_at,
                type: "created",
                from: newTask.sender_name || (newTask.is_sent ? "You" : "Unknown"),
                label: newTask.is_sent ? "Task assigned" : "Task received",
                message: newTask.context || newTask.title,
                msg_id: newTask.gmail_id || null,
              }],
            });
            newTaskIds.add(newTask.task_id);
          });

          return [...toAdd, ...updatedPrev];
        });
        setNewIds(newTaskIds);
        setNewCount(newTaskIds.size);
        setTimeout(() => setNewIds(new Set()), 4000);
      }
      // ── PASS 1.5: Status updates from non-actionable correspondence ──────────
      // Confirmations, acknowledgements and general information don't create
      // tasks, but they are exactly what the Checklist's "Last activity" column
      // needs. Match each to an existing task (thread first) and log it.
      const statusMsgs = [...statusCandidates.values()].filter(m => m.source === "Gmail");
      let statusApplied = 0, infoRecords = 0;
      if (statusMsgs.length > 0) {
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Recording status from ${statusMsgs.length} non-actionable message${statusMsgs.length !== 1 ? "s" : ""}...` }]);
        try {
          const tasksNow = await new Promise(resolve => setTasks(p => { resolve(p); return p; }));
          const byThread = {};
          tasksNow.forEach(t => { if (t.thread_id) (byThread[t.thread_id] = byThread[t.thread_id] || []).push(t.task_id); });
          const projName = id => projects.find(p => p.id === id)?.name || "";
          // Catalog: open tasks first, then the most recently completed — capped so the prompt stays small
          const catalogTasks = [
            ...tasksNow.filter(t => t.status === "open" && (t.visibility || "normal") !== "dismissed"),
            ...tasksNow.filter(t => t.status === "complete").sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0)).slice(0, 60),
          ].filter(t => (t.kind || "action") !== "info");
          const catalog = catalogTasks.map(t =>
            `${t.task_id} | ${(t.title || "").slice(0, 70)} | ${projName((t.project_ids || [])[0]) || t.project || "-"} | ${t.status}${t.vendor_stage ? " (" + t.vendor_stage + ")" : ""} | ${t.is_sent ? t.assignee_name : t.sender_name}`
          ).join("\n");

          const payload = statusMsgs.map(m => ({
            msg_id: m.msg_id, thread_id: m.thread_id || null,
            from: m.from || "Unknown", to: m.to || "", subject: m.subject || "",
            body: (m.body || "").slice(0, 1500), timestamp: m.timestamp, is_sent: !!m.is_sent,
            likely_task_ids: byThread[m.thread_id] || [],
          }));
          const statusResults = [];
          for (let i = 0; i < payload.length; i += 12) {
            try {
              statusResults.push(...await claudeExtractStatusUpdates(payload.slice(i, i + 12), catalog, projects, authToken, partnerRef.current));
            } catch(e) {
              statusMsgs.slice(i, i + 12).forEach(m => { failedMsgs.push(m); noteMsg(m, `Status pass failed — will retry (${e.message})`); });
              setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `⚠️ Status pass failed for ${Math.min(12, payload.length - i)} messages — will retry next sync (${e.message})`, error: true }]);
            }
          }

          const validIds = new Set(tasksNow.map(t => t.task_id));
          const updates = []; // { task_id, entry, marks_complete, next_action, owner }
          const records = []; // new "info" tasks
          statusResults.forEach(r => {
            if (!r || r.is_noise) return;
            const src = statusMsgs.find(m => m.msg_id === r.msg_id);
            if (!src) return;
            collectVendorMentions(r.vendor_mentions, src, r.project_id || null);
            if (!r.summary) return;
            const from = src.is_sent ? "You" : (extractSenderName(src.from) || "Unknown");
            const entry = {
              ts: new Date().toISOString(), at: src.timestamp || new Date().toISOString(),
              type: "comms", from, label: r.label || (r.kind ? r.kind[0].toUpperCase() + r.kind.slice(1) : "Update"),
              message: r.summary, msg_id: src.gmail_id || null, kind: r.kind || "other",
            };
            const conf = typeof r.confidence === "number" ? r.confidence : 0;
            if (r.task_id && validIds.has(r.task_id) && conf >= 0.5) {
              updates.push({ task_id: r.task_id, entry, marks_complete: !!r.marks_complete, next_action: r.next_action || null, owner: r.next_action_owner || null, src });
              log.push({ time: new Date().toLocaleTimeString(), text: `📎 ${entry.label}: ${r.summary}` });
              noteMsg(src, `Status logged on "${(tasksNow.find(t => t.task_id === r.task_id) || {}).title || "task"}": ${r.summary}`, r.task_id);
            } else if (r.project_id && projects.find(p => p.id === r.project_id) && conf >= 0.7 && ["confirmation", "information", "payment", "scheduling", "cancellation"].includes(r.kind)) {
              // No task to attach to, but it belongs to a project: keep it as a dated record
              records.push({
                task_id: `info-${src.proc_key || src.msg_id}`,
                title: r.summary, kind: "info", status: "complete", completed_at: (src.timestamp || new Date().toISOString()).split("T")[0],
                owner_id: "u1", sender_name: from, assignee_name: "You", source: "Gmail", source_label: src.subject || "(no subject)",
                priority: "low", priority_bucket: "today", visibility: "normal", cc_only: !!src.cc_only,
                project_ids: [r.project_id], project: projName(r.project_id), context: r.summary, notes: "",
                is_sent: !!src.is_sent, action_owner: "done", awaiting_response: false, has_reply: false,
                next_action: null, sort_order: Date.now(), history: [entry],
                thread_id: src.thread_id || null, gmail_id: src.gmail_id || null,
                source_url: src.gmail_id ? gmailMsgUrl(src.gmail_id, !!src.is_sent) : null,
                created_at: src.timestamp || new Date().toISOString(),
              });
              log.push({ time: new Date().toLocaleTimeString(), text: `ℹ️ Recorded in ${projName(r.project_id)}: ${r.summary}`, dim: true });
              noteMsg(src, `Info record created in ${projName(r.project_id)}: ${r.summary}`, records[records.length - 1].task_id);
            }
          });

          if (updates.length || records.length) {
            setTasks(prev => {
              const existing = new Set(prev.map(t => t.task_id));
              const next = prev.map(t => {
                const us = updates.filter(u => u.task_id === t.task_id);
                if (!us.length) return t;
                let out = { ...t, history: Array.isArray(t.history) ? [...t.history] : [] };
                us.sort((a, b) => new Date(a.entry.at) - new Date(b.entry.at)).forEach(u => {
                  if (out.history.some(h => h.msg_id && h.msg_id === u.entry.msg_id)) return; // already logged
                  out.history.push(u.entry);
                  const external = u.entry.from !== "You";
                  out.reply_snippet = u.entry.message; out.reply_from = u.entry.from; out.reply_msg_id = u.entry.msg_id || out.reply_msg_id;
                  if (u.marks_complete && out.status === "open") {
                    out.status = "complete"; out.completed_at = u.entry.at.split("T")[0]; out.action_owner = "done";
                    out.awaiting_response = false; out.has_reply = false; out.next_action = u.entry.label;
                  } else if (out.status === "open") {
                    if (u.next_action) out.next_action = u.next_action;
                    if (u.owner === "you")       { out.action_owner = "you"; out.has_reply = external; out.awaiting_response = false; }
                    else if (u.owner === "them") { out.action_owner = "assignee"; out.has_reply = false; out.awaiting_response = true; }
                    else if (external)           { out.has_reply = true; }
                  }
                });
                return out;
              });
              return [...records.filter(r => !existing.has(r.task_id)), ...next];
            });
            statusApplied = updates.length; infoRecords = records.length;
          }
        } catch(e) {
          console.warn("Status pass failed:", e.message);
          setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Status pass error: ${e.message}`, error: true }]);
        }
      }

      // ── VENDOR PASS: turn vendor mentions into candidate records ─────────────
      // Every vendor an email is to/from/about becomes (or updates) a candidate in
      // its project, with a pipeline stage and a dated history entry. This is how
      // "we discussed options with Aoife and Emily" shows up without anyone
      // creating a task by hand.
      let vendorsCreated = 0, vendorsUpdated = 0;
      if (vendorMentions.length > 0) {
        const bookedProjectIds = new Set();
        const norm = s => String(s || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
        const rank = st => VENDOR_STAGES.indexOf(st);
        setTasks(prev => {
          let next = [...prev];
          vendorMentions.forEach(vm => {
            const pid = (vm.project_id && projects.find(p => p.id === vm.project_id)) ? vm.project_id
                      : (vm.fallback_project_id && projects.find(p => p.id === vm.fallback_project_id)) ? vm.fallback_project_id : null;
            if (!pid) return; // no project to file the vendor under — skip rather than guess
            const stage = VENDOR_STAGES.includes(vm.stage) ? vm.stage : "Discussed";
            const needle = norm(vm.vendor_name);
            if (needle.length < 2) return;
            const first = needle.split(" ")[0];
            const inProject = next.filter(t => (t.kind || "action") === "candidate" && (t.project_ids || []).includes(pid));
            const match = inProject.find(t => {
              const title = norm(t.title);
              return title === needle || title.includes(needle) || needle.includes(title) || (first.length > 3 && title.split(" ")[0] === first);
            });
            const entry = { ts: new Date().toISOString(), at: vm.at, type: "comms", from: vm.from, msg_id: vm.msg_id,
                            label: match ? (rank(stage) > rank(match.vendor_stage || "Identified") || TERMINAL_STAGES.includes(stage) ? `Stage → ${stage}` : "Mentioned") : `Added · ${stage}`,
                            message: vm.note || `${vm.from} mentioned ${vm.vendor_name}` };
            const waiting = stage === "Enquiry Sent" || stage === "Awaiting Reply";
            const closed = stage === "Declined by Us" || stage === "Unavailable";
            if (stage === "Booked") bookedProjectIds.add(pid);
            if (match) {
              if ((match.history || []).some(h => h.msg_id && h.msg_id === vm.msg_id)) return; // already logged
              const advance = TERMINAL_STAGES.includes(stage) || rank(stage) > rank(match.vendor_stage || "Identified");
              const newStage = advance ? stage : (match.vendor_stage || "Identified");
              const idx = next.findIndex(t => t.task_id === match.task_id);
              next[idx] = { ...match, history: [...(match.history || []), entry], vendor_stage: newStage,
                            reply_snippet: vm.note || match.reply_snippet, reply_from: vm.from, reply_msg_id: vm.msg_id || match.reply_msg_id,
                            status: closed ? "complete" : match.status, completed_at: closed ? vm.at.split("T")[0] : match.completed_at,
                            awaiting_response: advance ? waiting : match.awaiting_response,
                            action_owner: closed ? "done" : advance ? (waiting ? "assignee" : "you") : match.action_owner,
                            next_action: advance ? (waiting ? `Wait for ${vm.vendor_name} to reply` : stage === "Quote Received" ? `Review ${vm.vendor_name}'s pricing and decide` : match.next_action) : match.next_action };
              vendorsUpdated++;
              log.push({ time: new Date().toLocaleTimeString(), text: `🏷 ${vm.vendor_name}${advance ? ` → ${stage}` : ""}: ${vm.note || "mentioned"}` });
              noteMsg(unprocessed.find(m => m.gmail_id === vm.msg_id), `Vendor "${match.title}"${advance ? ` → ${stage}` : " noted"}: ${vm.note || ""}`, match.task_id);
            } else {
              const projName = projects.find(p => p.id === pid)?.name || null;
              next.unshift({
                task_id: `cand-${pid}-${needle.replace(/ /g, "-").slice(0, 24)}`,
                title: vm.vendor_name, kind: "candidate", vendor_stage: stage,
                status: closed ? "complete" : "open", completed_at: closed ? vm.at.split("T")[0] : null,
                owner_id: "u1", sender_name: vm.from, assignee_name: vm.vendor_name, source: "Gmail", source_label: "Vendor option",
                priority: "medium", priority_bucket: "today", visibility: "normal", cc_only: false,
                project_ids: [pid], project: projName, context: vm.note || `${vm.vendor_name} — option for ${projName}`, notes: "",
                is_sent: waiting, awaiting_response: waiting, has_reply: false,
                action_owner: closed ? "done" : waiting ? "assignee" : "you",
                next_action: waiting ? `Wait for ${vm.vendor_name} to reply` : stage === "Discussed" ? `Decide whether to contact ${vm.vendor_name}` : stage === "Quote Received" ? `Review ${vm.vendor_name}'s pricing and decide` : null,
                reply_snippet: vm.note || null, reply_from: vm.from, reply_msg_id: vm.msg_id || null,
                sort_order: Date.now(), history: [entry], thread_id: null, gmail_id: vm.msg_id || null,
                source_url: vm.msg_id ? gmailMsgUrl(vm.msg_id, false) : null, created_at: vm.at, due_date: null,
              });
              vendorsCreated++;
              log.push({ time: new Date().toLocaleTimeString(), text: `🏷 New option in ${projName}: ${vm.vendor_name} · ${stage}` });
              noteMsg(unprocessed.find(m => m.gmail_id === vm.msg_id), `New vendor option "${vm.vendor_name}" · ${stage} in ${projName}`, next[0].task_id);
            }
          });
          return next;
        });
        if (bookedProjectIds.size) {
          bookedProjectIds.forEach(pid => {
            apiFetch(`/api/projects/${pid}`, { method: "PATCH", body: JSON.stringify({ status: "booked" }) }, authToken).catch(() => {});
          });
          persistProjects(projects.map(p => bookedProjectIds.has(p.id) ? { ...p, status: "booked" } : p));
        }
      }

      // Anything fetched but never noted got through every pass without a match
      unprocessed.forEach(m => { if (!disp[m.proc_key || m.msg_id]) noteMsg(m, "Nothing recorded — no task, status match, or vendor mention found"); });
      setMsgLog(prev => {
        const fresh = Object.values(disp);
        const keys = new Set(fresh.map(d => d.key));
        const next = [...fresh, ...(prev || []).filter(d => !keys.has(d.key))].slice(0, 300);
        saveStored("toodue_msglog", next);
        return next;
      });
      const failedKeys = new Set(failedMsgs.map(m => m.proc_key));
      setProcessedMsgIds(prev => new Set([...prev, ...unprocessed.filter(m => !failedKeys.has(m.proc_key)).map(m => m.proc_key)]));
      // Note: on web, processed IDs are derived from tasks on load, not stored separately
      // If anything failed, hold the incremental cursor back to just before the
      // oldest failed message so the next sync fetches it again.
      let cursorOverride = null;
      if (failedMsgs.length) {
        const oldest = Math.min(...failedMsgs.map(m => new Date(m.timestamp || Date.now()).getTime()));
        cursorOverride = Math.floor(oldest / 1000) - 60;
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `${failedMsgs.length} message${failedMsgs.length !== 1 ? "s" : ""} will be retried on the next sync.`, error: true }]);
      }
      setSyncLog(l => [...l, ...log, { time: new Date().toLocaleTimeString(), text: `Sync complete — ${created.length} tasks extracted, ${statusApplied} status update${statusApplied !== 1 ? "s" : ""} logged${infoRecords ? `, ${infoRecords} info record${infoRecords !== 1 ? "s" : ""}` : ""}${vendorsCreated || vendorsUpdated ? `, vendors: ${vendorsCreated} new / ${vendorsUpdated} updated` : ""}` }]);

      // ── PHASE 2: Reply detection for open tasks (both sent and received) ───
      const allCurrentTasks = await new Promise(resolve => {
        setTasks(prev => { resolve(prev); return prev; });
      });
      // Sent tasks: check if assignee replied
      const openSentTasks = allCurrentTasks.filter(t => t.is_sent && t.status === "open" && t.thread_id);
      // Received tasks: check if YOU replied
      const openReceivedTasks = allCurrentTasks.filter(t => !t.is_sent && t.status === "open" && t.thread_id);
      const allTasksToCheck = [...openSentTasks, ...openReceivedTasks];

      if (allTasksToCheck.length > 0) {
        setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Checking ${allTasksToCheck.length} task thread${allTasksToCheck.length !== 1 ? 's' : ''} for updates...` }]);
        try {
          const threadRes = await apiFetch("/api/threads", {
            method: "POST",
            body: JSON.stringify({ thread_ids: allTasksToCheck.map(t => t.thread_id) }),
          }, authToken);
          const threadData = await threadRes.json();
          const threadMap = {};
          (threadData.threads || []).forEach(th => { threadMap[th.thread_id] = th.messages; });

          // Build analysis payload — only threads with >1 message
          const toAnalyze = allTasksToCheck
            .filter(t => (threadMap[t.thread_id] || []).length > 1)
            .map(t => ({
              task_id: t.task_id,
              task_title: t.title,
              task_type: t.is_sent ? "sent" : "received",
              assignee_name: t.is_sent ? t.assignee_name : "You",
              messages: (threadMap[t.thread_id] || []).map(m => ({
                msg_id: m.msg_id,
                from: m.from,
                snippet: m.snippet,
                is_sent_by_user: m.is_sent_by_user,
                timestamp: m.timestamp,
              })),
            }));

          if (toAnalyze.length > 0) {
            setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Analyzing ${toAnalyze.length} thread${toAnalyze.length !== 1 ? 's' : ''} via Claude...` }]);
            const replyResults = await claudeAnalyzeReplies(toAnalyze, authToken);

            // DETERMINISTIC CORRECTION: walk the actual thread to verify who sent the last substantive message
            // Correct Claude's attribution if it disagrees with is_sent_by_user
            const isNonSubstantive = (snippet) => {
              const s = (snippet || "").toLowerCase().trim();
              if (!s) return true;
              if (s.includes("reacted via gmail")) return true;
              if (/^(thanks|thank you|ok|okay|got it|sounds good|great|cool|noted|\u{1F44D}|\u{2764}\u{FE0F}?)[.!\s]*$/u.test(s)) return true;
              return false;
            };
            replyResults.forEach(r => {
              const task = toAnalyze.find(t => t.task_id === r.task_id);
              if (!task || !task.messages?.length) return;
              let last = null;
              for (let i = task.messages.length - 1; i >= 0; i--) {
                if (!isNonSubstantive(task.messages[i].snippet)) { last = task.messages[i]; break; }
              }
              if (!last) return;
              const actualFromIsUser = last.is_sent_by_user === true;
              if (r.reply_from_is_user !== actualFromIsUser) {
                console.log(`Reply attribution corrected for ${r.task_id}: was is_user=${r.reply_from_is_user}, actual=${actualFromIsUser}`);
                r.reply_from_is_user = actualFromIsUser;
                r.reply_from = actualFromIsUser ? "You" : (last.from || r.reply_from);
                r.reply_snippet = (last.snippet || "").slice(0, 200);
                if (actualFromIsUser && r.outcome === "waiting_on_user") { r.outcome = "waiting_on_assignee"; r.action_owner = "assignee"; }
                if (!actualFromIsUser && r.outcome === "waiting_on_assignee") { r.outcome = "waiting_on_user"; r.action_owner = "you"; }
              }
            });

            const replyLog = [];

            // TOPIC DRIFT: spawn new tasks for drifted topics — with dedup
            const driftTasks = [];
            // Collect existing split tasks so we don't duplicate
            const existingSplitsByOrigin = new Set();
            const currentTasksNow = await new Promise(resolve => setTasks(p => { resolve(p); return p; }));
            currentTasksNow.forEach(t => {
              if (t.drifted_from_task_id) existingSplitsByOrigin.add(t.drifted_from_task_id);
            });

            replyResults.forEach(r => {
              if (!r.topic_drift || !r.drift_title) return;
              const origTask = toAnalyze.find(t => t.task_id === r.task_id);
              // Skip if we already created a split-off from this original task
              if (existingSplitsByOrigin.has(r.task_id)) {
                console.log(`Skipping drift spawn for ${r.task_id} — split-off already exists`);
                r.skipOriginalUpdate = true; // still don't pollute the original
                return;
              }
              // Deterministic task_id based on original task + reply_msg_id so re-syncs don't duplicate
              const newTaskId = `task-split-${r.task_id.replace(/^task-/, "").slice(0, 20)}-${(r.reply_msg_id || "").slice(0, 12)}`;
              // Skip if this exact ID already exists
              if (currentTasksNow.some(t => t.task_id === newTaskId)) {
                r.skipOriginalUpdate = true;
                return;
              }
              const isUserTurn = !r.reply_from_is_user;
              driftTasks.push({
                task_id: newTaskId,
                title: (r.drift_title || r.drift_summary || "New action from thread").slice(0, 80),
                context: r.drift_summary || r.drift_title,
                next_action: r.drift_title || null,
                priority: "medium",
                priority_bucket: "today",
                sort_order: Date.now(),
                status: "open",
                is_sent: false,
                awaiting_response: false,
                has_reply: isUserTurn,
                action_owner: isUserTurn ? "you" : "assignee",
                sender_name: r.reply_from_is_user ? "You" : (r.reply_from || "Unknown"),
                assignee_name: r.reply_from_is_user ? (r.reply_from || "Unknown") : "You",
                thread_id: null,
                drifted_from_task_id: r.task_id, // track origin for dedup
                gmail_id: r.reply_msg_id,
                source: "Gmail",
                source_label: "Split from: " + (origTask?.task_title || "thread"),
                source_url: null,
                project: origTask ? currentTasksNow.find(t => t.task_id === r.task_id)?.project : null,
                notes: `Auto-split from "${origTask?.task_title || "another task"}" when thread drifted.`,
                created_at: new Date().toISOString(),
                reply_snippet: r.reply_snippet,
                reply_from: r.reply_from,
                reply_msg_id: r.reply_msg_id,
                history: [{
                  ts: new Date().toISOString(),
                  type: "created",
                  from: r.reply_from_is_user ? "You" : (r.reply_from || "Unknown"),
                  label: "Split from another thread",
                  message: r.drift_summary || r.drift_title,
                }],
              });
              replyLog.push({ time: new Date().toLocaleTimeString(), text: `🧵 Split: "${r.drift_title.slice(0,50)}"` });
              r.skipOriginalUpdate = true;
            });

            setTasks(prev => {
              const updated = prev.map(task => {
              const result = replyResults.find(r => r.task_id === task.task_id);
              if (!result || result.outcome === "pending") return task;

              // Build structured history entry for this update
              const replyFrom = result.reply_from_is_user ? "You" : (result.reply_from || task.assignee_name || "Unknown");
              const addHistory = (type) => {
                const history = Array.isArray(task.history) ? [...task.history] : [];
                // Skip if this exact reply is already logged (re-syncs re-analyze the same thread)
                if (result.reply_msg_id && history.some(h => h.reply_msg_id === result.reply_msg_id && h.type === type)) return history;
                const srcMsg = (toAnalyze.find(t => t.task_id === task.task_id)?.messages || []).find(m => m.msg_id === result.reply_msg_id);
                history.push({
                  ts: new Date().toISOString(),
                  at: srcMsg?.timestamp || new Date().toISOString(),
                  type,
                  from: replyFrom,
                  label: result.event_label || null,
                  message: result.status_update || result.reply_snippet || "",
                  reply_msg_id: result.reply_msg_id || null,
                });
                return history;
              };

              if (result.outcome === "completed") {
                replyLog.push({ time: new Date().toLocaleTimeString(), text: `✓ Auto-completed: "${task.title}" — ${result.status_update || "task done"}` });
                return {
                  ...task,
                  status: "complete",
                  awaiting_response: false,
                  has_reply: false,
                  action_owner: "done",
                  completed_at: new Date().toISOString().split("T")[0],
                  next_action: result.next_action || null,
                  thread_summary: result.thread_summary || task.thread_summary || null,
                  reply_snippet: result.reply_snippet,
                  reply_from: replyFrom,
                  reply_msg_id: result.reply_msg_id,
                  history: addHistory("completed"),
                };
              }

              if (result.outcome === "waiting_on_assignee") {
                replyLog.push({ time: new Date().toLocaleTimeString(), text: `⏳ Waiting on ${task.assignee_name}: "${task.title}" — ${result.status_update || "no response yet"}` });
                return {
                  ...task,
                  awaiting_response: true,
                  has_reply: false,
                  action_owner: "assignee",
                  next_action: result.next_action || null,
                  thread_summary: result.thread_summary || task.thread_summary || null,
                  reply_snippet: result.reply_snippet,
                  reply_from: replyFrom,
                  reply_msg_id: result.reply_msg_id,
                  history: addHistory("waiting_on_assignee"),
                };
              }

              if (result.outcome === "waiting_on_user") {
                replyLog.push({ time: new Date().toLocaleTimeString(), text: `🎯 Your turn: "${task.title}" — ${result.status_update || "action needed"}` });
                return {
                  ...task,
                  awaiting_response: false,
                  has_reply: true,
                  action_owner: "you",
                  next_action: result.next_action || null,
                  thread_summary: result.thread_summary || task.thread_summary || null,
                  reply_snippet: result.reply_snippet,
                  reply_from: replyFrom,
                  reply_msg_id: result.reply_msg_id,
                  history: addHistory("waiting_on_user"),
                };
              }

              return task;
              });
              // Prepend drift-split tasks
              return [...driftTasks, ...updated];
            });

            if (replyLog.length > 0) {
              setSyncLog(l => [...l, ...replyLog]);
              const needsAction = replyResults.filter(r => r.outcome === "waiting_on_user" || r.topic_drift).length;
              if (needsAction > 0) setNewCount(prev => prev + needsAction);
            }
          }
        } catch(e) {
          setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Reply check error: ${e.message}`, error: true }]);
        }
      }

      setLastSync(t => { const v = new Date().toLocaleTimeString(); saveStored("toodue_lastSync", v); return v; });
      lastSyncTimestamp.current = cursorOverride !== null ? Math.min(cursorOverride, syncStartTs) : syncStartTs;
      saveStored("toodue_lastSyncTs", lastSyncTimestamp.current);
      // Flush all tasks to server after sync (reads live state when it fires)
      if (isWeb) flushTasksToServer();
      setSyncStatus("idle");

      // Report to Electron tray: how many new tasks + total open
      if (isElectron) {
        const allTasks = await new Promise(resolve => { setTasksRaw(prev => { resolve(prev); return prev; }); });
        const totalOpen = allTasks.filter(t => t.status === "open").length;
        window.electronAPI.reportSyncComplete(created.length, totalOpen);
      }
    } catch(e) {
      setSyncStatus("error");
      setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Error: ${e.message}`, error: true }]);
    }
  }, [setTasks, setProcessedMsgIds, authToken, isWeb, flushTasksToServer, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial sync on mount — for web, wait until session is loaded and user is allowed
  useEffect(() => {
    if (isWeb && (authLoading || !isAllowed)) return;
    if (!hasSynced.current) {
      hasSynced.current = true;
      runSync();
    }
  }, [runSync, isWeb, authLoading, isAllowed]);

  // Web: keep syncing while the tab is open. Every 5 minutes, and whenever the
  // tab comes back into focus after a while. Incremental, so cheap when idle —
  // and it means a vendor email that lands while Toodue is open shows up
  // without anyone pressing Sync Now.
  const lastAutoSync = useRef(Date.now()); // page load counts as a sync (initial sync runs on mount)
  useEffect(() => {
    if (!isWeb || authLoading || !isAllowed) return;
    const maybeSync = (minGapMs) => {
      if (syncStatus === "syncing") return;
      if (Date.now() - lastAutoSync.current < minGapMs) return;
      lastAutoSync.current = Date.now();
      runSync();
    };
    const timer = setInterval(() => maybeSync(4.5 * 60 * 1000), 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") maybeSync(2 * 60 * 1000); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", onVisible); };
  }, [isWeb, authLoading, isAllowed, runSync, syncStatus]);

  // Electron: listen for auto-sync triggers from tray menu / background timer
  useEffect(() => {
    if (!isElectron) return;
    const cleanup = window.electronAPI.onTriggerSync(() => {
      runSync();
    });
    return cleanup;
  }, [isElectron, runSync]);

  const completeTask = id => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== id) return t;
        const completing = t.status !== "complete";
        // Log it so the project's "last action taken" reflects completions too.
        const history = Array.isArray(t.history) ? [...t.history] : [];
        history.push({
          ts: new Date().toISOString(), type: completing ? "completed" : "update", from: "You",
          label: completing ? "Marked complete" : "Reopened",
          message: completing ? `Completed: ${t.title}` : `Reopened: ${t.title}`,
        });
        return {
          ...t,
          status: completing ? "complete" : "open",
          completed_at: completing ? new Date().toISOString().split("T")[0] : null,
          action_owner: completing ? "done" : (t.is_sent ? "assignee" : "you"),
          history,
        };
      });
      flushTasksToServer();
      return next;
    });
  };

  const updateDueDate = (id, newDate) => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== id) return t;
        const followUpAfter = newDate
          ? new Date(new Date(newDate).getTime() + 24 * 60 * 60 * 1000).toISOString()
          : t.follow_up_after;
        return { ...t, due_date: newDate || null, follow_up_after: followUpAfter };
      });
      flushTasksToServer();
      return next;
    });
  };

  const updateBucket = (id, bucket, relookDate = null, newSortOrder = null) => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== id) return t;
        return {
          ...t,
          priority_bucket: bucket,
          relook_date: bucket === "later" ? relookDate : null,
          sort_order: newSortOrder !== null ? newSortOrder : (t.sort_order || Date.now()),
        };
      });
      flushTasksToServer();
      return next;
    });
  };

  const updateVisibility = (id, newVisibility) => {
    // "normal" = keep as real task. "dismissed" = archive. Called from FYI Keep/Dismiss.
    setTasks(prev => {
      if (newVisibility === "dismissed") {
        // Remove from active list entirely — mark as complete so it stays out of view
        const next = prev.map(t =>
          t.task_id === id
            ? { ...t, visibility: "dismissed", status: "complete", completed_at: new Date().toISOString().split("T")[0] }
            : t
        );
        flushTasksToServer();
        return next;
      }
      const next = prev.map(t => t.task_id === id ? { ...t, visibility: newVisibility } : t);
      flushTasksToServer();
      return next;
    });
  };

  const reorderTasks = (reorderedList) => {
    setTasks(prev => {
      const idxMap = {};
      const orderMap = {};
      reorderedList.forEach((t, i) => { idxMap[t.task_id] = i; if (t.sort_order !== undefined) orderMap[t.task_id] = t.sort_order; });
      // Apply the new sort_order values so the order survives a reload (previously
      // only the in-memory array was reordered and the DB never learned about it).
      const withOrder = prev.map(t => orderMap[t.task_id] !== undefined && orderMap[t.task_id] !== t.sort_order ? { ...t, sort_order: orderMap[t.task_id] } : t);
      const next = [...withOrder].sort((a, b) => {
        const ai = idxMap[a.task_id];
        const bi = idxMap[b.task_id];
        if (ai !== undefined && bi !== undefined) return ai - bi;
        if (ai !== undefined) return -1;
        if (bi !== undefined) return 1;
        return 0;
      });
      flushTasksToServer();
      return next;
    });
  };

  // Auto-migrate: tomorrow → today at midnight, later → today on relook_date
  useEffect(() => {
    const check = () => {
      const today = new Date().toISOString().split("T")[0];
      setTasks(prev => {
        let changed = false;
        const next = prev.map(t => {
          if (t.priority_bucket === "tomorrow") {
            // Check if it's a new day since this was set
            const lastMidnight = new Date();
            lastMidnight.setHours(0,0,0,0);
            const setDate = t.bucket_set_date;
            if (!setDate || new Date(setDate) < lastMidnight) {
              changed = true;
              return { ...t, priority_bucket: "today", bucket_set_date: null };
            }
          }
          if (t.priority_bucket === "later" && t.relook_date && t.relook_date <= today) {
            changed = true;
            return { ...t, priority_bucket: "today", relook_date: null };
          }
          return t;
        });
        if (changed) flushTasksToServer();
        return changed ? next : prev;
      });
    };
    check();
    // Check every hour
    const interval = setInterval(check, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [flushTasksToServer]);

  const addManualTask = (task) => {
    setTasks(prev => {
      const next = [task, ...prev];
      flushTasksToServer();
      return next;
    });
    setNewIds(new Set([task.task_id]));
    setTimeout(() => setNewIds(new Set()), 4000);
  };

  // ── Project mutators ──────────────────────────────────────────────────────
  const persistProjects = (list) => {
    setProjects(list);
    saveStored("toodue_projects", list);
  };

  const createProject = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return null;
    const id = `proj_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    const sort_order = projects.length ? Math.max(...projects.map(p => p.sort_order || 0)) + 1 : Date.now();
    const proj = { id, name: clean, sort_order, origin: "manual" };
    persistProjects([...projects, proj]);
    try { await apiFetch("/api/projects", { method: "POST", body: JSON.stringify(proj) }, authToken); }
    catch(e) { console.error("Create project failed:", e); }
    return proj;
  };

  const renameProject = async (id, name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    // Renaming is a deliberate act — an auto-created project becomes a kept one.
    persistProjects(projects.map(p => p.id === id ? { ...p, name: clean, origin: "manual" } : p));
    try { await apiFetch(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify({ name: clean, origin: "manual" }) }, authToken); }
    catch(e) { console.error("Rename project failed:", e); }
  };

  const deleteProject = async (id) => {
    persistProjects(projects.filter(p => p.id !== id));
    setTasks(prev => {
      const next = prev.map(t => Array.isArray(t.project_ids) && t.project_ids.includes(id)
        ? { ...t, project_ids: t.project_ids.filter(x => x !== id) } : t);
      flushTasksToServer();
      return next;
    });
    if (activeFolder === `proj:${id}`) setActiveFolder("all");
    try { await apiFetch(`/api/projects/${id}`, { method: "DELETE" }, authToken); }
    catch(e) { console.error("Delete project failed:", e); }
  };

  // Resolve a "needs filing" task: file it into a project (or none) and clear the flag.
  // A reply draft was created in Gmail for this task. Attach the thread (new for
  // manual tasks) so the sent message and every later reply route back here.
  const markReplyDrafted = (taskId, info = {}) => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== taskId) return t;
        const history = Array.isArray(t.history) ? [...t.history] : [];
        history.push({ ts: new Date().toISOString(), at: new Date().toISOString(), type: "draft", from: "You",
                       label: t.thread_id ? "Reply drafted in Gmail" : "Email drafted in Gmail", message: info.snippet || "" });
        return {
          ...t,
          thread_id: t.thread_id || info.thread_id || null,
          source: t.thread_id ? t.source : (info.thread_id ? "Gmail" : t.source),
          source_label: t.thread_id ? t.source_label : (info.subject || t.source_label),
          history,
        };
      });
      flushTasksToServer();
      return next;
    });
  };

  // Apply a batch of changes proposed by Ask Claude (after the user confirms).
  // Each action maps onto the same mutators the UI buttons use, so behaviour
  // (history entries, project booking on "Booked", debounced save) is identical.
  const applyAgentActions = (actions = []) => {
    const stamp = () => new Date().toISOString();
    const agentLog = (label, message) => ({ at: stamp(), type: "note", from: "You", label, message });
    actions.forEach(a => {
      const t = tasks.find(x => x.task_id === a.task_id);
      switch (a.type) {
        case "set_stage":
          if (t && VENDOR_STAGES.includes(a.stage)) setCandidateStage(a.task_id, a.stage);
          break;
        case "complete":
          if (t && t.status !== "complete") completeTask(a.task_id);
          break;
        case "reopen":
          if (t && t.status === "complete") completeTask(a.task_id);
          break;
        case "set_due":
          if (t && /^\d{4}-\d{2}-\d{2}$/.test(a.due_date || "")) updateDueDate(a.task_id, a.due_date);
          break;
        case "set_priority":
          if (t && ["high", "medium", "low"].includes(a.priority)) editTask(a.task_id, { priority: a.priority }, agentLog("Priority changed", `Set to ${a.priority} via Ask Claude`));
          break;
        case "set_bucket":
          if (t && ["today", "tomorrow", "later"].includes(a.bucket)) updateBucket(a.task_id, a.bucket, a.bucket === "later" ? (a.relook_date || null) : null, Date.now());
          break;
        case "set_next_action":
          if (t) editTask(a.task_id, {
            next_action: a.next_action || t.next_action,
            ...(a.owner === "them" ? { action_owner: "assignee", awaiting_response: true } : a.owner === "you" ? { action_owner: "you", awaiting_response: false } : {}),
          }, agentLog("Next action set", a.next_action || ""));
          break;
        case "rename":
          if (t && a.title) editTask(a.task_id, { title: a.title }, agentLog("Renamed", `Was: ${t.title}`));
          break;
        case "move_to_project":
          if (t && projects.some(p => p.id === a.project_id)) fileTask(a.task_id, a.project_id);
          break;
        case "dismiss":
          if (t) updateVisibility(a.task_id, "dismissed");
          break;
        case "add_note":
          if (t && a.note) editTask(a.task_id, { reply_snippet: a.note, reply_from: "You" }, agentLog("Note", a.note));
          break;
        case "create_task": {
          if (!a.title) break;
          const proj = a.project_id ? projects.find(p => p.id === a.project_id) : null;
          const isCand = a.kind === "candidate";
          const stage = isCand ? (VENDOR_STAGES.includes(a.stage) ? a.stage : "Identified") : null;
          addManualTask({
            task_id: `${isCand ? "cand" : "manual"}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            title: a.title, owner_id: "u1", sender_id: "u1", sender_name: "You", assignee_name: isCand ? a.title : "You",
            source: "Manual", source_label: isCand ? "Vendor option" : "Added via Ask Claude",
            priority: ["high", "medium", "low"].includes(a.priority) ? a.priority : "medium", status: "open",
            due_date: /^\d{4}-\d{2}-\d{2}$/.test(a.due_date || "") ? a.due_date : null,
            project_ids: proj ? [proj.id] : [], project: proj ? proj.name : null,
            kind: isCand ? "candidate" : "action", vendor_stage: stage,
            context: a.next_action || a.title, next_action: a.next_action || null, notes: "",
            is_sent: false, awaiting_response: false, action_owner: "you", follow_up_after: null,
            priority_bucket: "today", sort_order: Date.now(), created_at: stamp(),
            thread_id: null, source_url: null, gmail_id: null,
            history: [{ ts: stamp(), at: stamp(), type: "created", from: "You", label: "Created via Ask Claude", message: a.title }],
          });
          break;
        }
        default: break;
      }
    });
    setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `✨ Applied ${actions.length} change${actions.length !== 1 ? "s" : ""} from Ask Claude` }]);
  };

  // Plain-text snapshot of sync state, for pasting into a support/debug chat.
  const buildDiagnostics = () => {
    const cur = lastSyncTimestamp.current;
    const lines = [
      `Toodue diagnostics · ${new Date().toString()}`,
      `Account: ${session?.user?.email || "?"} · Gmail: ${gmailUserEmail || "?"} (connected: ${gmailConnected})`,
      `Partner: ${partner ? `${partner.name} <${partner.email}>` : "not set"}`,
      `Tasks loaded: ${tasks.length} (open ${tasks.filter(t => t.status === "open").length}) · projects: ${projects.length}`,
      `Sync cursor: ${cur ? new Date(cur * 1000).toString() : "none (full 30d)"} · processed keys: ${processedMsgIds.current.size}`,
      `Sync status: ${syncStatus} · last: ${lastSync || "never"}`,
      `Gmail check: ${gmailCheck ? JSON.stringify(gmailCheck) : "not run (click Test Gmail)"}`,
      "",
      "— Sync log (newest last) —",
      ...syncLog.slice(-40).map(l => `${l.time}  ${l.text}`),
      "",
      "— Email log (newest first, last 20) —",
      ...(msgLog || []).slice(0, 20).map(d => `${d.at}  ${d.is_sent ? "SENT" : "RECV"}  "${d.subject}"  ${d.is_sent ? "to " + d.to : "from " + d.from}\n    ${(d.outcomes || []).map(o => o.text).join("\n    ")}`),
    ];
    return lines.join("\n");
  };

  // Re-read the last N days of mail. Messages that already produced a task are
  // still skipped (no duplicates); ones that were seen but produced nothing get
  // a fresh look with the current pipeline.
  const resyncDays = (days) => {
    lastSyncTimestamp.current = Math.floor(Date.now() / 1000) - days * 86400;
    saveStored("toodue_lastSyncTs", lastSyncTimestamp.current);
    saveStored("toodue_processedRecent", []);
    const ids = new Set();
    tasks.forEach(t => { if (t.gmail_id) ids.add(t.is_sent ? `sent:${t.gmail_id}` : `recv:${t.gmail_id}`); });
    processedMsgIds.current = ids;
    setSyncLog(l => [...l, { time: new Date().toLocaleTimeString(), text: `Re-syncing the last ${days} day${days !== 1 ? "s" : ""}…` }]);
    runSync();
  };

  // Generic manual edit from the Checklist: merge fields, optionally log an entry.
  const editTask = (taskId, patch = {}, logEntry = null) => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== taskId) return t;
        const history = Array.isArray(t.history) ? [...t.history] : [];
        if (logEntry) history.push({ ts: new Date().toISOString(), from: "You", type: "note", ...logEntry });
        return { ...t, ...patch, history };
      });
      flushTasksToServer();
      return next;
    });
  };

  const fileTask = (taskId, projectId) => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== taskId) return t;
        const proj = projectId ? projects.find(p => p.id === projectId) : null;
        return { ...t, needs_filing: false, project_ids: proj ? [proj.id] : [], project: proj ? proj.name : null, project_confidence: proj ? 1 : t.project_confidence };
      });
      flushTasksToServer();
      return next;
    });
  };

  // Move a vendor candidate to a new pipeline stage. Booking a candidate books its project.
  const setCandidateStage = (taskId, stage) => {
    let bookedInto = [];
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== taskId) return t;
        const history = Array.isArray(t.history) ? [...t.history] : [];
        history.push({ ts: new Date().toISOString(), type: "update", from: "You", label: `Stage → ${stage}`, message: `Moved to ${stage}` });
        const closed = stage === "Declined by Us" || stage === "Unavailable";
        const waiting = stage === "Enquiry Sent" || stage === "Awaiting Reply";
        if (stage === "Booked") bookedInto = t.project_ids || [];
        return { ...t, vendor_stage: stage, history,
          status: closed ? "complete" : "open",
          completed_at: closed ? new Date().toISOString().split("T")[0] : null,
          is_sent: waiting, awaiting_response: waiting, action_owner: waiting ? "assignee" : "you",
          follow_up_after: waiting ? new Date(Date.now() + 5 * 24 * 3600 * 1000).toISOString() : null };
      });
      flushTasksToServer();
      return next;
    });
    if (bookedInto.length) bookedInto.forEach(pid => setProjectStatus(pid, "booked"));
  };

  const setProjectStatus = (projectId, status) => {
    persistProjects(projects.map(p => p.id === projectId ? { ...p, status } : p));
    apiFetch(`/api/projects/${projectId}`, { method: "PATCH", body: JSON.stringify({ status }) }, authToken)
      .catch(e => console.error("Project status failed:", e));
  };

  // Add or remove a project membership on a task. `on` true = add, false = remove.
  const setTaskProject = (taskId, projectId, on = true) => {
    setTasks(prev => {
      const next = prev.map(t => {
        if (t.task_id !== taskId) return t;
        const cur = Array.isArray(t.project_ids) ? t.project_ids : [];
        const ids = on ? [...new Set([...cur, projectId])] : cur.filter(x => x !== projectId);
        const firstName = ids.length ? (projects.find(p => p.id === ids[0])?.name || t.project) : null;
        return { ...t, project_ids: ids, project: firstName };
      });
      flushTasksToServer();
      return next;
    });
    // Dragging a task into an auto-created project makes it a kept one.
    if (on) {
      const proj = projects.find(p => p.id === projectId);
      if (proj && proj.origin === "auto") {
        persistProjects(projects.map(p => p.id === projectId ? { ...p, origin: "manual" } : p));
        apiFetch(`/api/projects/${projectId}`, { method: "PATCH", body: JSON.stringify({ origin: "manual" }) }, authToken)
          .catch(e => console.error("Promote project failed:", e));
      }
    }
  };

  const getVisible = () => {
    let f = tasks;
    // "normal" folders exclude: FYI/review/dismissed items, unfiled items, and vendor
    // candidates (which live only inside their project).
    const normalOnly = (t) => (t.visibility || "normal") === "normal" && !t.needs_filing && !["candidate", "info"].includes(t.kind || "action");
    const needsReview = (t) => t.status === "open" && (t.visibility === "fyi" || t.visibility === "review" || t.needs_filing);

    if (activeFolder === "activity" || activeFolder === "tracker") {
      f = [];
    } else if (activeFolder === "checklist") {
      // Every real task, open or done, plus info records — the full ledger
      f = f.filter(t => (t.visibility || "normal") === "normal"); // tasks, info records AND vendor candidates
    } else if (activeFolder === "fyi") {
      f = f.filter(needsReview);
    } else if (activeFolder === "sent") {
      f = f.filter(t => t.is_sent && t.status === "open" && normalOnly(t));
    } else if (activeFolder === "complete") {
      f = f.filter(t => t.status === "complete");
    } else if (activeFolder === "all") {
      const now = new Date();
      if (bannerFilter === "overdue") {
        f = f.filter(t => !t.is_sent && t.status === "open" && normalOnly(t) && t.due_date && new Date(t.due_date) < now);
      } else if (bannerFilter === "followup") {
        f = f.filter(t => t.is_sent && t.awaiting_response && t.status === "open" && normalOnly(t) && t.follow_up_after && new Date(t.follow_up_after) <= now);
      } else if (bannerFilter === "replies") {
        f = f.filter(t => t.is_sent && t.has_reply && t.status === "open" && normalOnly(t));
      } else {
        f = f.filter(t =>
          t.status === "open" && normalOnly(t) && (
            !t.is_sent ||
            (t.is_sent && t.has_reply) ||
            (t.is_sent && t.follow_up_after && new Date(t.follow_up_after) <= now)
          )
        );
      }
    } else if (activeFolder.startsWith("proj:")) {
      const pid = activeFolder.slice(5);
      const proj = projects.find(p => p.id === pid);
      f = f.filter(t => {
        // Completed tasks STAY in their project — a project is the record of the
        // whole effort, not just what's outstanding. Only dismissed items leave.
        if (t.visibility === "dismissed") return false;
        if (Array.isArray(t.project_ids) && t.project_ids.includes(pid)) return true;
        // legacy fallback: task still carries the old string and matches this project's name
        if (proj && t.project && t.project === proj.name) return true;
        return false;
      });
    } else if (activeFolder.startsWith("sender:")) {
      const person = activeFolder.slice(7);
      f = f.filter(t => t.status === "open" && (
        (t.is_sent && t.assignee_name === person) ||
        (!t.is_sent && t.sender_name === person)
      ));
    } else {
      f = f.filter(t => t.status === "open");
    }
    if (filterPriority !== "all") f = f.filter(t => t.priority === filterPriority);
    return f;
  };

  const visible = getVisible();
  const isProjectView = activeFolder.startsWith("proj:");
  const activeProject = isProjectView ? projects.find(p => p.id === activeFolder.slice(5)) : null;
  const candidates = isProjectView ? visible.filter(t => (t.kind || "action") === "candidate" && t.status === "open") : [];
  const projDone   = isProjectView ? visible.filter(t => t.status === "complete" && (t.kind || "action") !== "info") : [];
  const actionable = isProjectView
    ? visible.filter(t => (t.kind || "action") !== "candidate" && t.status === "open")
    : visible;
  // Top 3: the most urgent actionable tasks, shown above everything else in the
  // All Tasks view and in each project view. Everything else lists below.
  const showTopThree = (activeFolder === "all" && !bannerFilter) || isProjectView;
  const topThree = showTopThree ? pickTopTasks(actionable, 3) : [];
  const topIds = new Set(topThree.map(t => t.task_id));
  const rest = showTopThree ? actionable.filter(t => !topIds.has(t.task_id)) : actionable;

  const hi  = rest.filter(t => t.priority === "high");
  const med = rest.filter(t => t.priority === "medium");
  const lo  = rest.filter(t => t.priority === "low");

  const title =
    activeFolder === "all" && bannerFilter === "overdue" ? "Overdue Tasks" :
    activeFolder === "all" && bannerFilter === "followup" ? "Follow-up Needed" :
    activeFolder === "all" && bannerFilter === "replies"  ? "Replies to Review" :
    activeFolder === "all"      ? "All Open Tasks" :
    activeFolder === "checklist" ? "Checklist" :
    activeFolder === "activity" ? "Activity" :
    activeFolder === "tracker" ? "Tracker" :
    activeFolder === "fyi"      ? "Review" :
    activeFolder === "sent"     ? "Sent & Tracking" :
    activeFolder === "complete" ? "Completed" :
    activeFolder.startsWith("proj:")   ? (projects.find(p => p.id === activeFolder.slice(5))?.name || "Project") :
    activeFolder.startsWith("sender:") ? `From ${activeFolder.slice(7)}` : "Tasks";

  const Section = ({ label, items, priority }) => {
    if (!items.length) return null;
    const pm = PRIORITY_META[priority];
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "22px 0 10px" }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: pm.color, boxShadow: `0 0 8px ${pm.glow}`, flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: pm.color, textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "monospace" }}>{label}</span>
          <span style={{ fontSize: 11, color: "#1E2D40", background: pm.bg, borderRadius: 8, padding: "1px 7px" }}>{items.length}</span>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${pm.color}28, transparent)` }} />
        </div>
        {items.map(t => (
          <div key={t.task_id} draggable onDragStart={() => setDraggedTask(t)} onDragEnd={() => setDraggedTask(null)}>
            <TaskCard task={t} onComplete={completeTask} isNew={newIds.has(t.task_id)} onUpdateDueDate={updateDueDate} onUpdateVisibility={updateVisibility} projects={projects} onSetProject={fileTask} onSetStage={setCandidateStage} onReplyDrafted={markReplyDrafted} />
          </div>
        ))}
      </div>
    );
  };

  // Top 3 block — numbered, gold-accented, sits above the rest of the list
  const TopThree = () => {
    if (!topThree.length) return null;
    const gold = "#FCD34D";
    return (
      <div style={{ marginBottom: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 10px" }}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>⭐</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: gold, textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "monospace" }}>Top 3</span>
          <span style={{ fontSize: 11, color: "#64748B" }}>most urgent by due date &amp; priority</span>
          <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${gold}40, transparent)` }} />
        </div>
        <div style={{ border: `1px solid ${gold}30`, background: `${gold}08`, borderRadius: 12, padding: "10px 12px 2px" }}>
          {topThree.map((t, i) => (
            <div key={t.task_id} draggable onDragStart={() => setDraggedTask(t)} onDragEnd={() => setDraggedTask(null)}
                 style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, marginTop: 14,
                            background: `${gold}20`, border: `1px solid ${gold}60`, color: gold,
                            fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TaskCard task={t} onComplete={completeTask} isNew={newIds.has(t.task_id)} onUpdateDueDate={updateDueDate} onUpdateVisibility={updateVisibility} projects={projects} onSetProject={fileTask} onSetStage={setCandidateStage} onReplyDrafted={markReplyDrafted} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Auth gates (web only) ─────────────────────────────────────────────────
  if (isWeb && authLoading) {
    return <div style={{ minHeight:"100vh", background:"#080D1A", display:"flex", alignItems:"center", justifyContent:"center", color:"#475569", fontFamily:"'DM Sans',sans-serif", fontSize:14 }}>Loading...</div>;
  }
  if (isWeb && !session) return <LoginScreen />;
  if (isWeb && session && !isAllowed) return <WaitlistScreen email={session.user.email} onSignOut={signOutCurrent} />;

  return (
    <div style={{ minHeight: "100vh", background: "#080D1A", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: "#CBD5E1", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.06); border-radius: 10px; }
        @keyframes syncpulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes slidein { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
        @keyframes fadein { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        input:focus, textarea:focus, select:focus { border-color: rgba(99,102,241,0.5) !important; outline: none; }
      `}</style>

      {/* NAV */}
      <div style={{ display: "flex", alignItems: "center", padding: "0 22px", height: 56, borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(8,13,26,0.97)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 100, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 30, height: 30, background: "linear-gradient(135deg, #6366F1, #8B5CF6)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>⚡</div>
          <span style={{ fontFamily: "'Lora', serif", fontSize: 18, fontWeight: 700, color: "#E2E8F0", letterSpacing: "-0.03em" }}>Toodue</span>
          <span style={{ fontSize: 9, color: "#1E2D40", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: "2px 6px", fontWeight: 700, letterSpacing: "0.1em" }}>AI-POWERED</span>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {syncStatus === "syncing" && (
            <span style={{ fontSize: 12, color: "#6366F1", display: "flex", alignItems: "center", gap: 5, background: "rgba(99,102,241,0.08)", borderRadius: 6, padding: "4px 10px" }}>
              <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>↻</span>
              Claude reading messages
            </span>
          )}
          {/* ASK CLAUDE SEARCH BAR */}
          <button
            onClick={() => setShowAskClaude(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#94A3B8", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "6px 12px", cursor: "pointer", minWidth: 240, transition: "background 0.15s, border-color 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.06)"; e.currentTarget.style.borderColor = "rgba(99,102,241,0.25)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}
          >
            <span style={{ fontSize: 14 }}>✨</span>
            <span style={{ flex: 1, textAlign: "left" }}>Ask about your tasks…</span>
            <span style={{ fontSize: 10, color: "#475569", background: "rgba(255,255,255,0.04)", borderRadius: 4, padding: "2px 6px", fontFamily: "monospace" }}>⌘K</span>
          </button>
          {/* PLANNER BRIEF */}
          {projects.length > 0 && (
            <button onClick={() => setShowPlanner(true)} title="Planner brief"
              style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.25)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, fontSize: 15 }}>✨</button>
          )}
          {/* ALERT ICONS */}
          <AlertIcons tasks={tasks} setActiveFolder={setActiveFolder} setBannerFilter={setBannerFilter} />
          {/* ADD TASK BUTTON */}
          <button
            onClick={() => setShowAddModal(true)}
            style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "linear-gradient(135deg, #6366F1, #8B5CF6)", border: "none", borderRadius: 7, padding: "6px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
          >
            + Add Task
          </button>
          <AccountSwitcher
            accounts={accounts}
            activeUserId={activeUserId}
            onSwitch={switchAccount}
            onAdd={addAccount}
            onSignOut={signOutCurrent}
            onOpenSettings={() => setShowSettings(true)}
          />
        </div>
      </div>

      {/* MODALS */}
      {showAddModal && (
        <AddTaskModal
          onAdd={addManualTask}
          onClose={() => setShowAddModal(false)}
          projects={projects}
          defaultProjectId={activeFolder.startsWith("proj:") ? activeFolder.slice(5) : null}
        />
      )}
      {showPlanner && (
        <PlannerPanel
          tasks={tasks}
          projects={projects}
          authToken={authToken}
          focusProjectId={activeFolder.startsWith("proj:") ? activeFolder.slice(5) : null}
          onClose={() => setShowPlanner(false)}
          onApplyBriefs={(perProject) => {
            // cache each project's slice of the brief on the project row so the project page can show it
            persistProjects(projects.map(p => perProject[p.id] ? { ...p, agent_brief: perProject[p.id], agent_brief_at: new Date().toISOString() } : p));
            Object.entries(perProject).forEach(([pid, b]) => {
              if (projects.some(p => p.id === pid))
                apiFetch(`/api/projects/${pid}`, { method: "PATCH", body: JSON.stringify({ agent_brief: b }) }, authToken).catch(() => {});
            });
          }}
        />
      )}
      {showAskClaude && (
        <AskClaude
          tasks={tasks}
          projects={projects}
          authToken={authToken}
          userEmail={session?.user?.email}
          gmailEmail={gmailUserEmail}
          onClose={() => setShowAskClaude(false)}
          onApplyActions={applyAgentActions}
          onTaskClick={(t) => {
            // Navigate to All Tasks; user can then find the task. Could be enhanced to scroll-to.
            setActiveFolder("all");
            setBannerFilter(null);
          }}
        />
      )}
      {showSettings && session && (
        <SettingsModal
          session={session}
          authToken={authToken}
          onClose={() => setShowSettings(false)}
          onSignOut={signOutCurrent}
          gmailConnected={gmailConnected}
          gmailEmail={gmailUserEmail}
          onConnectGmail={connectGmail}
          onImportSeed={importSeed}
          partner={partner}
          onSavePartner={savePartner}
        />
      )}
      {showBetaDisclaimer && session && (
        <BetaDisclaimer onAccept={async () => {
          await supabase.from("profiles").update({ beta_accepted_at: new Date().toISOString() }).eq("id", session.user.id);
          setShowBetaDisclaimer(false);
        }} />
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Sidebar
          tasks={tasks} active={activeFolder} setActive={setActiveFolderAndClearFilter}
          projects={projects}
          onCreateProject={createProject}
          onRenameProject={renameProject}
          onDeleteProject={deleteProject}
          draggedTask={draggedTask}
          onDropTaskInProject={(task, projectId) => { setTaskProject(task.task_id, projectId, true); setDraggedTask(null); }}
        />

        <div style={{ flex: 1, padding: "22px 26px", overflowY: "auto", maxHeight: "calc(100vh - 56px)" }}>
          <SyncBar
            status={syncStatus} lastSync={lastSync} newCount={newCount}
            onManualSync={() => { runSync(); }}
            onResync={resyncDays}
            gmailConnected={gmailConnected}
            gmailExpired={gmailExpired}
            onTestGmail={testGmail}
            gmailCheck={gmailCheck}
            onConnectGmail={connectGmail}
          />

          {/* Page header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontFamily: "'Lora', serif", fontWeight: 700, color: "#DDE4F0", letterSpacing: "-0.02em" }}>{title}</h1>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#1E2D40" }}>{visible.length} item{visible.length !== 1 ? "s" : ""}</p>
            </div>
            <div style={{ flex: 1 }} />
            {isProjectView && (
              <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 7, padding: 2 }}>
                {[["tasks", "Tasks"], ["checklist", "☑ Checklist"]].map(([id, label]) => (
                  <button key={id} onClick={() => setProjectTab(id)} style={{
                    fontSize: 12, padding: "4px 12px", borderRadius: 5, cursor: "pointer", border: "none",
                    background: projectTab === id ? "rgba(99,102,241,0.25)" : "transparent",
                    color: projectTab === id ? "#C7D2FE" : "#64748B", fontWeight: projectTab === id ? 600 : 400,
                  }}>{label}</button>
                ))}
              </div>
            )}
            {activeFolder.startsWith("proj:") && (
              <button onClick={() => setShowAddModal(true)}
                style={{ fontSize: 12, fontWeight: 600, color: "#A5B4FC", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 6, padding: "5px 12px", cursor: "pointer" }}>
                + Add task to project
              </button>
            )}
            <div style={{ display: "flex", gap: 5 }}>
              {["all","high","medium","low"].map(p => {
                const pm = p !== "all" ? PRIORITY_META[p] : null;
                return (
                  <button key={p} onClick={() => setFilterPriority(p)} style={{
                    fontSize: 11, padding: "4px 11px", borderRadius: 6, cursor: "pointer",
                    border: filterPriority===p ? `1px solid ${p==="all"?"rgba(99,102,241,0.4)":pm.border}` : "1px solid rgba(255,255,255,0.06)",
                    background: filterPriority===p ? (p==="all"?"rgba(99,102,241,0.12)":pm.bg) : "transparent",
                    color: filterPriority===p ? (p==="all"?"#818CF8":pm.color) : "#334155",
                    fontWeight: filterPriority===p ? 700 : 400, textTransform: "capitalize", transition: "all 0.15s",
                  }}>{p}</button>
                );
              })}
            </div>
          </div>

          {/* Loading */}
          {syncStatus === "syncing" && tasks.length === 0 && (
            <div style={{ textAlign: "center", padding: "70px 0" }}>
              <div style={{ fontSize: 28, display: "inline-block", animation: "spin 1.5s linear infinite", marginBottom: 16, color: "#6366F1" }}>↻</div>
              <p style={{ color: "#334155", fontSize: 14, margin: "0 0 6px" }}>Claude is reading your connected apps</p>
              <p style={{ color: "#1E2D40", fontSize: 12, margin: 0 }}>Gmail · Slack · QuickBooks — extracting tasks automatically</p>
            </div>
          )}

          {/* Checklist: overall (grouped by project) or inside a project */}
          {activeFolder === "tracker" ? (
            <TrackerPanel authToken={authToken} tracker={tracker} onTrackerChange={(t) => { setTracker(t); if (t) scheduleTrackerSync(500); }} onSyncNow={() => runTrackerSync()} syncing={trackerSyncing} lastResult={trackerResult} />
          ) : activeFolder === "activity" ? (
            <ActivityPanel tasks={tasks} projects={projects} msgLog={msgLog} gmailEmail={gmailUserEmail}
              onTaskClick={(t) => { const pid = (t.project_ids || [])[0]; if (pid) { setActiveFolder(`proj:${pid}`); setProjectTab("checklist"); } else if (t.visibility === "fyi" || t.visibility === "review" || t.needs_filing) setActiveFolder("fyi"); else if (t.is_sent && t.status === "open") setActiveFolder("sent"); else if (t.status === "complete") setActiveFolder("complete"); else setActiveFolder("all"); }} />
          ) : activeFolder === "checklist" || (isProjectView && projectTab === "checklist") ? (
            <ChecklistView
              tasks={visible}
              projects={projects}
              groupByProject={activeFolder === "checklist"}
              onComplete={completeTask}
              onEdit={editTask}
              onReorder={reorderTasks}
              onMoveToProject={fileTask}
              draggedTask={draggedTask}
              setDraggedTask={setDraggedTask}
              cardProps={{ onUpdateDueDate: updateDueDate, onUpdateVisibility: updateVisibility, onSetProject: fileTask, onSetStage: setCandidateStage, onReplyDrafted: markReplyDrafted }}
            />
          ) : activeFolder !== "complete" ? (
            <>
              {/* Priority Bucket Tabs — only show in All Tasks view */}
              {activeFolder === "all" && !bannerFilter && (
                <div style={{ marginBottom: 20 }}>
                  <TopThree />
                  {rest.length > 0 && topThree.length > 0 && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "monospace", margin: "0 0 10px" }}>Everything else</div>
                  )}
                  {/* Bucket tab bar */}
                  <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                    {[
                      { id:"today", label:"Today", icon:"🎯", color:"#6366F1" },
                      { id:"tomorrow", label:"Tomorrow", icon:"📅", color:"#60A5FA" },
                      { id:"later", label:"Later", icon:"🕐", color:"#94A3B8" },
                    ].map(bucket => {
                      const count = rest.filter(t => (t.priority_bucket || "today") === bucket.id).length;
                      const isActive = activeBucket === bucket.id;
                      const isOver = dragOverBucket === bucket.id;
                      return (
                        <div
                          key={bucket.id}
                          onClick={() => setActiveBucket(bucket.id)}
                          onDragOver={e => { e.preventDefault(); setDragOverBucket(bucket.id); }}
                          onDragLeave={() => setDragOverBucket(null)}
                          onDrop={e => {
                            e.preventDefault();
                            setDragOverBucket(null);
                            if (!draggedTask || draggedTask.priority_bucket === bucket.id) return;
                            if (bucket.id === "later") {
                              setRelookModal(draggedTask.task_id);
                            } else {
                              updateBucket(draggedTask.task_id, bucket.id, null, Date.now());
                              if (bucket.id === "tomorrow") {
                                updateBucket(draggedTask.task_id, "tomorrow", null, Date.now());
                                setTasks(prev => prev.map(t => t.task_id === draggedTask.task_id ? { ...t, bucket_set_date: new Date().toISOString().split("T")[0] } : t));
                              }
                            }
                            setDraggedTask(null);
                          }}
                          style={{
                            flex:1, padding:"10px 16px", borderRadius:10, cursor:"pointer", transition:"all 0.15s",
                            background: isOver ? `${bucket.color}22` : isActive ? `${bucket.color}18` : "rgba(255,255,255,0.03)",
                            border: `1px solid ${isOver ? bucket.color : isActive ? `${bucket.color}60` : "rgba(255,255,255,0.06)"}`,
                            display:"flex", alignItems:"center", gap:8,
                            transform: isOver ? "scale(1.02)" : "none",
                          }}
                        >
                          <span style={{ fontSize:16 }}>{bucket.icon}</span>
                          <span style={{ fontSize:13, fontWeight: isActive ? 700 : 500, color: isActive ? bucket.color : "#64748B" }}>{bucket.label}</span>
                          <span style={{ marginLeft:"auto", fontSize:11, background: isActive ? `${bucket.color}25` : "rgba(255,255,255,0.05)", color: isActive ? bucket.color : "#475569", borderRadius:6, padding:"1px 7px", fontWeight:600 }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Tasks in active bucket — draggable */}
                  {(() => {
                    const bucketTasks = rest
                      .filter(t => (t.priority_bucket || "today") === activeBucket)
                      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                    if (bucketTasks.length === 0) return (
                      <div style={{ textAlign:"center", padding:"40px 0", border:"1px dashed rgba(255,255,255,0.06)", borderRadius:10 }}>
                        <p style={{ color:"#334155", fontSize:13, margin:0 }}>
                          {activeBucket === "today" ? (topThree.length ? "Nothing else for today — your Top 3 is the list" : "Nothing for today 🎉") : activeBucket === "tomorrow" ? "Drag tasks here to defer to tomorrow" : "Drag tasks here to schedule for later"}
                        </p>
                      </div>
                    );
                    return bucketTasks.map((t, idx) => (
                      <div
                        key={t.task_id}
                        draggable
                        onDragStart={() => setDraggedTask(t)}
                        onDragEnd={() => { setDraggedTask(null); setDragOverTaskId(null); }}
                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverTaskId(t.task_id); }}
                        onDragLeave={() => setDragOverTaskId(null)}
                        onDrop={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverTaskId(null);
                          if (!draggedTask || draggedTask.task_id === t.task_id) return;
                          // Reorder within bucket
                          const reordered = [...bucketTasks];
                          const fromIdx = reordered.findIndex(x => x.task_id === draggedTask.task_id);
                          const toIdx = reordered.findIndex(x => x.task_id === t.task_id);
                          if (fromIdx === -1) return;
                          reordered.splice(fromIdx, 1);
                          reordered.splice(toIdx, 0, draggedTask);
                          // Assign new sort_orders
                          const now = Date.now();
                          const updates = reordered.map((task, i) => ({ ...task, sort_order: now + i }));
                          reorderTasks(updates);
                          setDraggedTask(null);
                        }}
                        style={{
                          opacity: draggedTask?.task_id === t.task_id ? 0.4 : 1,
                          borderTop: dragOverTaskId === t.task_id && draggedTask?.task_id !== t.task_id ? "2px solid #6366F1" : "2px solid transparent",
                          transition: "opacity 0.15s, border-top 0.1s",
                          cursor: "grab",
                        }}
                      >
                        <TaskCard task={t} onComplete={completeTask} isNew={newIds.has(t.task_id)} onUpdateDueDate={updateDueDate} onUpdateVisibility={updateVisibility} projects={projects} onSetProject={fileTask} onSetStage={setCandidateStage} onReplyDrafted={markReplyDrafted} />
                      </div>
                    ));
                  })()}
                </div>
              )}

              {/* Project view: status, brief, candidate pipeline */}
              {isProjectView && activeProject && (
                <ProjectHeader
                  project={activeProject}
                  tasks={tasks}
                  candidates={candidates}
                  actionable={actionable}
                  done={projDone}
                  onStatus={(st) => setProjectStatus(activeProject.id, st)}
                  onTargetDate={(d) => { persistProjects(projects.map(p => p.id === activeProject.id ? { ...p, target_date: d } : p)); apiFetch(`/api/projects/${activeProject.id}`, { method: "PATCH", body: JSON.stringify({ target_date: d }) }, authToken).catch(() => {}); }}
                  onOpenPlanner={() => setShowPlanner(true)}
                  renderCandidate={(t) => (
                    <div key={t.task_id} draggable onDragStart={() => setDraggedTask(t)} onDragEnd={() => setDraggedTask(null)}>
                      <TaskCard task={t} onComplete={completeTask} isNew={newIds.has(t.task_id)} onUpdateDueDate={updateDueDate} onUpdateVisibility={updateVisibility} projects={projects} onSetProject={fileTask} onSetStage={setCandidateStage} onReplyDrafted={markReplyDrafted} />
                    </div>
                  )}
                />
              )}

              {/* Standard priority sections for non-bucket views */}
              {(activeFolder !== "all" || bannerFilter) && (
                actionable.length === 0 && candidates.length === 0 && projDone.length === 0 && syncStatus !== "syncing" ? (
                  <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <div style={{ fontSize: 32, opacity: 0.2, marginBottom: 12 }}>✓</div>
                    <p style={{ color: "#334155", fontSize: 14 }}>Nothing here</p>
                  </div>
                ) : (
                  <>
                    {isProjectView && <TopThree />}
                    <Section label="High Priority" items={hi} priority="high" />
                    <Section label="Medium Priority" items={med} priority="medium" />
                    <Section label="Low Priority" items={lo} priority="low" />
                    {isProjectView && projDone.length > 0 && <CompletedInProject items={projDone} onComplete={completeTask} projects={projects} onSetStage={setCandidateStage} onReplyDrafted={markReplyDrafted} />}
                  </>
                )
              )}
            </>
          ) : (
            visible.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <p style={{ color: "#334155", fontSize: 14 }}>No completed tasks yet</p>
              </div>
            ) : visible.map(t => <TaskCard key={t.task_id} task={t} onComplete={completeTask} isNew={false} onUpdateVisibility={updateVisibility} projects={projects} onSetProject={fileTask} onSetStage={setCandidateStage} onReplyDrafted={markReplyDrafted} />)
          )}

          {/* Relook date modal */}
          {relookModal && (
            <RelookModal
              onConfirm={(date) => {
                updateBucket(relookModal, "later", date, Date.now());
                setRelookModal(null);
              }}
              onCancel={() => setRelookModal(null)}
            />
          )}

          <ActivityLog log={syncLog} diagnostics={buildDiagnostics} />
        </div>
      </div>
    </div>
  );
}
